// src/components/EventMemoryBook.js
/**
 * Event Memory Book: post-event page with final scores, winners, photos, quotes, sponsors.
 *
 * Data structures:
 * - Event doc (events/{eventId}): optional memoryBookPublished: boolean. When true, public route shows content.
 * - Firestore eventMemoryMeta/{eventId}: intro (string), heroImageUrl (string), highlights (array of strings or {text}),
 *   statsOverrides (optional): manual corrections for “By the numbers”, hero carousel stat slides, and headline hero — see applyMemoryBookStatsOverrides.
 *   testimonials (array of {quote|text, name, role}), quiplashQuotes (array of {prompt, winningQuote|quote, teamNumber}),
 *   galleryCredits (map of Storage fullPath → credit string for gallery images).
 *   Legacy entries may use { author } instead of teamNumber.
 * - Storage: memoryBooks/{eventId}/gallery/ — gallery; memoryBooks/{eventId}/teams/ — team winner photos.
 * - Public URL query: eventId (required), teamId (optional) opens team detail for sharing.
 *   On the Gallery page: galleryCredit (optional) filters by exact credit string; use __no_credit__ for uncredited photos.
 */
import React, { useEffect, useLayoutEffect, useState, useMemo, useRef, useCallback } from 'react';
import { NavLink, useSearchParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, getDoc, doc, query, where, setDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { ref, listAll, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';
import { firestore, storage } from '../firebase';
import { calculateRank } from '../utils/rankUtils';
import { getEventDateRangeWithYear } from '../utils/eventUtils';
import { getAgeFromYear, getFirstName } from '../utils/flyerDisplay';
import { buildPublicMemoryBookUrl, buildPublicLeaderboardUrl } from '../utils/memoryBookShare';
import TeamSharePoster from './TeamSharePoster';
import { downloadBlob, renderNodeToPngBlob, shareOrDownloadPng } from '../utils/shareImage';
import SponsorLogos from './SponsorLogos';
import UsHeatMap from './UsHeatMap';
import InterestListModal from './InterestListModal';
import './EventMemoryBook.css';
import { getEventEditionYear } from '../utils/eventRouting';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import { getTeamLabelCore, getTeamNumberDisplay } from '../utils/teamDisplayLabel';

const MEMORY_BOOK_STORAGE_PREFIX = 'memoryBooks';

/** Chrome/Edge do not decode TIFF in img elements; transcode to JPEG on upload.
 *  Use a lower max dimension so featured/gallery images stay lightweight on web.
 */
const MEMORY_BOOK_TRANSCODE_MAX_DIM = 2048;

const CANOPY_THUR = 'Canopy Thursday';
const CANOPY_FRI = 'Canopy Friday';
const WATER_THUR = 'Water Training (Thur)';
const WATER_FRI = 'Water Training (Fri)';
const TICKET_HIGH_ALTITUDE = 'High Altitude';
const TICKET_JET = 'Jet';

function hashStringToSeed(s) {
  const str = String(s || '');
  // FNV-1a-ish 32-bit hash; stable across sessions.
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function rand() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function sampleUnique(items, count, seedKey) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!list.length || count <= 0) return [];
  if (list.length <= count) return list.slice();
  const rand = mulberry32(hashStringToSeed(seedKey));
  const pickedIdx = new Set();
  const out = [];
  while (out.length < count && pickedIdx.size < list.length) {
    const idx = Math.floor(rand() * list.length);
    if (pickedIdx.has(idx)) continue;
    pickedIdx.add(idx);
    out.push(list[idx]);
  }
  return out;
}

function baseNameNoExt(path) {
  const base = String(path || '').split('/').pop() || '';
  const idx = base.lastIndexOf('.');
  return idx > 0 ? base.slice(0, idx) : base;
}

function thumbKeyForFullPath(fullPath) {
  const h = hashStringToSeed(fullPath).toString(16).padStart(8, '0');
  const bn = baseNameNoExt(fullPath).replace(/[^\w.-]+/g, '-').slice(0, 64) || 'photo';
  return `t_${h}_${bn}.jpg`;
}

function usageOrManifestCount(flyer, label) {
  const u = flyer?.usage || {};
  const mt = flyer?.manifestTickets || {};
  const used = u[label]?.used;
  const manifest = mt[label];
  // If "used" is explicitly 0 but manifest is >0, we want manifest for Memory Book totals.
  // (Usage often tracks consumption; manifest tracks what was booked.)
  if (Number.isFinite(used) && used > 0) return used;
  if (Number.isFinite(manifest) && manifest > 0) return manifest;
  if (Number.isFinite(used) && used > 0) return used;
  return 0;
}

function sumUsageOrManifest(flyers, label) {
  return flyers.reduce((sum, f) => sum + usageOrManifestCount(f, label), 0);
}

function isActiveForActivityStats(flyer) {
  if (!flyer) return false;
  if (flyer.test) return false;
  if (flyer.cancelled) return false;
  if (isWithdrawnOrDuplicate(flyer)) return false;
  if (flyer.type === 'mentor') {
    const statusText = (flyer.status || '').toString().toLowerCase();
    if (!statusText.includes('accept')) return false;
  }
  return true;
}

function isWithdrawnOrDuplicate(flyer) {
  if (!flyer) return false;
  const statusText = (flyer.status || '').toString().toLowerCase();
  const notesText = (flyer.notes || '').toString().toLowerCase();
  return (
    statusText.includes('withdrawn') ||
    statusText.includes('duplicate') ||
    statusText.includes('cancelled') ||
    statusText.includes('canceled') ||
    notesText.includes('withdrawn') ||
    notesText.includes('duplicate') ||
    notesText.includes('cancelled') ||
    notesText.includes('canceled')
  );
}

/** Order matches buildNumbersDataBase teaser slides. */
const MEMORY_BOOK_TEASER_SLIDE_ORDER = [
  'freshlings',
  'teams',
  'mentors',
  'avgJumps',
  'ages',
  'ha',
  'jet',
  'canopy',
  'water',
  'states',
  'countries',
];

const MEMORY_BOOK_TEASER_LABELS = {
  freshlings: 'Freshlings',
  teams: 'Teams',
  mentors: 'Mentors',
  avgJumps: 'Avg. jumps at start',
  ages: 'Freshling ages',
  ha: 'High altitude jumps',
  jet: 'Jet jumps',
  canopy: 'Canopy courses',
  water: 'Water training',
  states: 'States',
  countries: 'Countries',
};

const MEMORY_BOOK_CHIP_TONE_BY_ID = {
  teams: 'slate',
  mentors: 'amber',
  avgJumps: 'violet',
  states: 'teal',
  countries: 'indigo',
};

function memoryBookSlideOrderIndex(id) {
  const i = MEMORY_BOOK_TEASER_SLIDE_ORDER.indexOf(id);
  return i === -1 ? 999 : i;
}

/**
 * Firestore statsOverrides shape: { hero?: { value, label }, [slideId]?: { value, label } | ages: { display, label },
 * activities?: { highAltitude, jet, canopy, water } }. Empty fields in admin form are not stored.
 */
function applyMemoryBookStatsOverrides(data, rawOverrides) {
  if (!data || !rawOverrides || typeof rawOverrides !== 'object' || Object.keys(rawOverrides).length === 0) {
    return data;
  }
  const o = rawOverrides;

  const patchTeaserSlide = (slide) => {
    const patch = o[slide.id];
    if (!patch || typeof patch !== 'object') return slide;
    const next = { ...slide };
    if (slide.kind === 'text') {
      if (patch.display != null && String(patch.display).trim()) next.display = String(patch.display).trim();
      if (patch.label != null && String(patch.label).trim()) next.label = String(patch.label).trim();
    } else {
      if (patch.value != null && Number.isFinite(Number(patch.value))) next.value = Number(patch.value);
      if (patch.label != null && String(patch.label).trim()) next.label = String(patch.label).trim();
    }
    return next;
  };

  let teaserSlides = (data.teaserSlides || []).map(patchTeaserSlide);
  const seenIds = new Set(teaserSlides.map((s) => s.id));

  for (const id of MEMORY_BOOK_TEASER_SLIDE_ORDER) {
    if (seenIds.has(id)) continue;
    const patch = o[id];
    if (!patch || typeof patch !== 'object') continue;
    if (id === 'ages') {
      const d = patch.display != null && String(patch.display).trim();
      if (d) {
        teaserSlides.push({
          id: 'ages',
          kind: 'text',
          display: d,
          label: (patch.label && String(patch.label).trim()) || MEMORY_BOOK_TEASER_LABELS.ages,
        });
        seenIds.add(id);
      }
    } else {
      const v = patch.value != null ? Number(patch.value) : NaN;
      if (Number.isFinite(v)) {
        teaserSlides.push({
          id,
          kind: 'number',
          value: v,
          label: (patch.label && String(patch.label).trim()) || MEMORY_BOOK_TEASER_LABELS[id] || id,
        });
        seenIds.add(id);
      }
    }
  }

  teaserSlides = [...teaserSlides].sort(
    (a, b) => memoryBookSlideOrderIndex(a.id) - memoryBookSlideOrderIndex(b.id)
  );

  let hero = { ...data.hero };
  if (o.hero && typeof o.hero === 'object') {
    if (o.hero.value != null && Number.isFinite(Number(o.hero.value))) hero.value = Number(o.hero.value);
    if (o.hero.label != null && String(o.hero.label).trim()) hero.label = String(o.hero.label).trim();
  }

  const chipIds = ['teams', 'mentors', 'avgJumps', 'states', 'countries'];
  let chips = (data.chips || []).map((c) => {
    const patch = o[c.id];
    if (!patch || typeof patch !== 'object') return c;
    const next = { ...c };
    if (patch.value != null && Number.isFinite(Number(patch.value))) next.value = Number(patch.value);
    if (patch.label != null && String(patch.label).trim()) next.label = String(patch.label).trim();
    return next;
  });
  const chipSeen = new Set(chips.map((c) => c.id));
  for (const id of chipIds) {
    if (chipSeen.has(id)) continue;
    const patch = o[id];
    if (!patch || typeof patch !== 'object') continue;
    const v = patch.value != null ? Number(patch.value) : NaN;
    if (!Number.isFinite(v)) continue;
    chips.push({
      id,
      label: (patch.label && String(patch.label).trim()) || MEMORY_BOOK_TEASER_LABELS[id] || id,
      value: v,
      tone: MEMORY_BOOK_CHIP_TONE_BY_ID[id] || 'slate',
    });
    chipSeen.add(id);
  }
  chips = [...chips].sort((a, b) => memoryBookSlideOrderIndex(a.id) - memoryBookSlideOrderIndex(b.id));

  let ageRange = data.ageRange ? { ...data.ageRange } : null;
  if (o.ages && typeof o.ages === 'object') {
    const d = o.ages.display != null && String(o.ages.display).trim();
    const lbl = o.ages.label != null && String(o.ages.label).trim();
    if (d || lbl) {
      ageRange = {
        text: d || ageRange?.text || '',
        label: lbl || ageRange?.label || MEMORY_BOOK_TEASER_LABELS.ages,
      };
    }
  }

  let activities = { ...(data.activities || {}) };
  if (o.activities && typeof o.activities === 'object') {
    for (const k of ['highAltitude', 'jet', 'canopy', 'water']) {
      const v = o.activities[k];
      if (v != null && Number.isFinite(Number(v))) activities[k] = Number(v);
    }
  }
  const hasAnyActivity =
    (activities.highAltitude || 0) > 0 ||
    (activities.jet || 0) > 0 ||
    (activities.canopy || 0) > 0 ||
    (activities.water || 0) > 0;

  return {
    ...data,
    teaserSlides,
    hero,
    chips,
    ageRange,
    activities,
    hasAnyActivity,
  };
}

function emptyMemoryBookStatsOverrideForm() {
  return {
    hero: { value: '', label: '' },
    slides: Object.fromEntries(
      MEMORY_BOOK_TEASER_SLIDE_ORDER.map((id) => [id, { value: '', label: '', display: '' }])
    ),
    activities: { highAltitude: '', jet: '', canopy: '', water: '' },
  };
}

function memoryBookStatsOverridesMetaToForm(meta) {
  const form = emptyMemoryBookStatsOverrideForm();
  if (!meta || typeof meta !== 'object') return form;
  if (meta.hero && typeof meta.hero === 'object') {
    form.hero = {
      value: meta.hero.value != null ? String(meta.hero.value) : '',
      label: meta.hero.label != null ? String(meta.hero.label) : '',
    };
  }
  for (const id of MEMORY_BOOK_TEASER_SLIDE_ORDER) {
    const p = meta[id];
    if (!p || typeof p !== 'object') continue;
    form.slides[id] = {
      value: p.value != null ? String(p.value) : '',
      label: p.label != null ? String(p.label) : '',
      display: p.display != null ? String(p.display) : '',
    };
  }
  if (meta.activities && typeof meta.activities === 'object') {
    for (const k of ['highAltitude', 'jet', 'canopy', 'water']) {
      if (meta.activities[k] != null) form.activities[k] = String(meta.activities[k]);
    }
  }
  return form;
}

function memoryBookStatsOverridesFormToFirestore(form) {
  if (!form || typeof form !== 'object') return {};
  const out = {};
  const hh = form.hero || {};
  const hv = hh.value != null && String(hh.value).trim();
  const hl = hh.label != null && String(hh.label).trim();
  if (hv || hl) {
    const hero = {};
    if (hv) {
      const n = Number(hh.value);
      if (Number.isFinite(n)) hero.value = n;
    }
    if (hl) hero.label = hl;
    if (Object.keys(hero).length) out.hero = hero;
  }
  for (const id of MEMORY_BOOK_TEASER_SLIDE_ORDER) {
    const row = form.slides?.[id];
    if (!row) continue;
    const entry = {};
    if (id === 'ages') {
      const d = row.display != null && String(row.display).trim();
      const l = row.label != null && String(row.label).trim();
      if (d) entry.display = d;
      if (l) entry.label = l;
    } else {
      const v = row.value != null && String(row.value).trim();
      const l = row.label != null && String(row.label).trim();
      if (v) {
        const n = Number(v);
        if (Number.isFinite(n)) entry.value = n;
      }
      if (l) entry.label = l;
    }
    if (Object.keys(entry).length) out[id] = entry;
  }
  const act = {};
  for (const k of ['highAltitude', 'jet', 'canopy', 'water']) {
    const raw = form.activities?.[k];
    if (raw != null && String(raw).trim() !== '') {
      const n = Number(raw);
      if (Number.isFinite(n)) act[k] = n;
    }
  }
  if (Object.keys(act).length) out.activities = act;
  return out;
}

const US_STATE_NAMES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};

const TABS = {
  PREVIEW: 'preview',
  TEAM_PHOTOS: 'teamPhotos',
  GALLERY: 'gallery',
  TESTIMONIALS: 'testimonials',
  INTRO_HERO: 'introHero',
  QUIPLASH: 'quiplash',
  VIDEOS: 'videos',
  NEXT_YEAR_INTEREST: 'nextYearInterest',
};

const PUBLIC_PAGES = {
  MAIN: 'main',
  LEADERBOARD: 'leaderboard',
  QUOTES: 'quotes',
  GALLERY: 'gallery',
  VIDEOS: 'videos',
};

/** URL / filter sentinel for “photos with no credit” (not a real credit string). */
const GALLERY_CREDIT_FILTER_NONE = '__no_credit__';

function uniqueTrimmedGalleryCredits(galleryItems) {
  const set = new Set();
  (galleryItems || []).forEach((i) => {
    const c = (i?.credit || '').trim();
    if (c) set.add(c);
  });
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function filterGalleryItemsByCreditParam(galleryItems, creditParam) {
  const list = (galleryItems || []).filter((i) => i && i.url && i.fullPath);
  const p = (creditParam || '').trim();
  if (!p) return list;
  if (p === GALLERY_CREDIT_FILTER_NONE) return list.filter((i) => !(i.credit || '').trim());
  return list.filter((i) => (i.credit || '').trim() === p);
}

function normalizeVideoUrl(url) {
  const trimmed = (url || '').toString().trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function dedupeVideoUrls(urls) {
  const out = [];
  const seen = new Set();
  (urls || []).forEach((url) => {
    const normalized = normalizeVideoUrl(url);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(normalized);
  });
  return out;
}

function parseVideoLines(text) {
  return dedupeVideoUrls(String(text || '').split('\n').map((line) => line.trim()));
}

const DEFAULT_TEAM_PHOTO_FOCUS_Y = 18; // percent, higher = more “faces-up” (legacy fallback)

function clampNumber(n, min, max) {
  const x = typeof n === 'number' ? n : Number.parseFloat(n);
  if (!Number.isFinite(x)) return min;
  return Math.min(max, Math.max(min, x));
}

function clamp01(n) {
  const x = typeof n === 'number' ? n : Number.parseFloat(n);
  if (!Number.isFinite(x)) return 0;
  return Math.min(1, Math.max(0, x));
}

function normalizeFocalPoint(fp) {
  if (!fp || typeof fp !== 'object') return null;
  const x = clamp01(fp.x);
  const y = clamp01(fp.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

function teamDocFocalPoint(team) {
  return normalizeFocalPoint(team?.photoFocalPoint || team?.focalPoint || team?.photoFocus);
}

function legacyTeamPhotoFocusY(teamId, memoryMeta) {
  const raw = memoryMeta?.teamPhotoFocus?.[teamId];
  return clampNumber(raw, 0, 100);
}

function teamPhotoObjectPosition(teamId, memoryMeta, teamFocalMap) {
  const fp = teamId ? normalizeFocalPoint(teamFocalMap?.[teamId]) : null;
  if (fp) return `${(fp.x * 100).toFixed(2)}% ${(fp.y * 100).toFixed(2)}%`;

  const metaFp = teamId ? normalizeFocalPoint(memoryMeta?.teamPhotoFocalPoints?.[teamId]) : null;
  if (metaFp) return `${(metaFp.x * 100).toFixed(2)}% ${(metaFp.y * 100).toFixed(2)}%`;

  const y = teamId ? legacyTeamPhotoFocusY(teamId, memoryMeta) : DEFAULT_TEAM_PHOTO_FOCUS_Y;
  const yy = Number.isFinite(y) ? y : DEFAULT_TEAM_PHOTO_FOCUS_Y;
  return `50% ${yy}%`;
}

function teamPhotoHeroObjectPosition(teamId, memoryMeta, teamFocalMap) {
  const fp = teamId ? normalizeFocalPoint(teamFocalMap?.[teamId]) : null;
  if (fp) return `${(fp.x * 100).toFixed(2)}% ${(fp.y * 100).toFixed(2)}%`;

  const metaFp = teamId ? normalizeFocalPoint(memoryMeta?.teamPhotoFocalPoints?.[teamId]) : null;
  if (metaFp) return `${(metaFp.x * 100).toFixed(2)}% ${(metaFp.y * 100).toFixed(2)}%`;

  const y = teamId ? legacyTeamPhotoFocusY(teamId, memoryMeta) : DEFAULT_TEAM_PHOTO_FOCUS_Y;
  const yy = Number.isFinite(y) ? y : DEFAULT_TEAM_PHOTO_FOCUS_Y;
  return `50% ${yy}%`;
}

function galleryPhotoObjectPosition(fullPath, memoryMeta) {
  const fp = normalizeFocalPoint(memoryMeta?.galleryFocalPoints?.[fullPath]);
  if (fp) return `${Math.round(fp.x * 100)}% ${Math.round(fp.y * 100)}%`;
  return '50% 50%';
}

function AnimatedNumber({ value, className = '' }) {
  const [display, setDisplay] = useState(0);
  const target = typeof value === 'number' ? value : parseInt(value, 10) || 0;
  useEffect(() => {
    if (target === 0) { setDisplay(0); return; }
    const duration = 900;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target]);
  return <div className={`memory-book-stat-value ${className}`.trim()}>{display}</div>;
}

function StarIcon({ filled = false }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        d="M12 2l3.09 6.26 6.91 1-5 4.87 1.18 6.87L12 17.77 5.82 21l1.18-6.87-5-4.87 6.91-1L12 2z"
      />
    </svg>
  );
}

function FocusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        d="M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3M12 8v8M8 12h8"
      />
      <circle cx="12" cy="12" r="3.5" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        d="M4 7h16M10 11v6M14 11v6M6 7l1 14h10l1-14M9 7V4h6v3"
      />
    </svg>
  );
}

/** Rotating headline for collapsed “By the numbers” teaser. */
/** Sort division headings: Beginner, Intermediate, others A–Z, Unassigned last. */
function divisionSortRank(label) {
  const L = (label || '').trim().toLowerCase();
  if (L === 'beginner') return { tier: 0, key: '' };
  if (L === 'intermediate') return { tier: 1, key: '' };
  if (!L || L === 'unassigned') return { tier: 99, key: (label || '').trim() };
  return { tier: 2, key: (label || '').trim() };
}

function compareDivisionLabel(a, b) {
  const ra = divisionSortRank(a);
  const rb = divisionSortRank(b);
  if (ra.tier !== rb.tier) return ra.tier - rb.tier;
  return ra.key.localeCompare(rb.key, undefined, { sensitivity: 'base' });
}

function divisionDisplayLabel(division) {
  const d = (division || '').trim();
  if (!d || d.toLowerCase() === 'unassigned') return 'Podium';
  return d;
}

/** 1 → 1st, 2 → 2nd, … for podium lines */
function ordinalPlace(n) {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return `${n}st`;
  if (j === 2 && k !== 12) return `${n}nd`;
  if (j === 3 && k !== 13) return `${n}rd`;
  return `${n}th`;
}

function podiumPlaceLabel(rank) {
  if (rank === 1) return '1ST';
  if (rank === 2) return '2ND';
  if (rank === 3) return '3RD';
  return '';
}

/** Chunk array into fixed-size slices for gallery rows in the mixed stream. */
function chunkArray(arr, size) {
  if (!arr || !arr.length) return [];
  const out = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

const GALLERY_IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|heic|avif|tiff?)$/i;

/** Formats we store as JPEG so all browsers can render memory-book image URLs. */
function imageNeedsJpegTranscodeForWebImg(file) {
  if (!file) return false;
  const mime = (file.type || '').toLowerCase();
  if (mime === 'image/tiff' || mime === 'image/x-tiff' || mime === 'image/tif') return true;
  return /\.(tif|tiff)$/i.test(file.name || '');
}

function isGalleryImageFile(file) {
  if (!file || !file.name) return false;
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('image/')) return true;
  return GALLERY_IMAGE_EXT.test(file.name);
}

function uniqueGalleryImageFiles(fileList) {
  const seen = new Set();
  const out = [];
  const list = fileList?.length != null ? Array.from(fileList) : [];
  list.forEach((f) => {
    if (!isGalleryImageFile(f)) return;
    const key = `${f.name}_${f.size}_${f.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(f);
  });
  return out;
}

function readDirectoryEntriesAll(reader) {
  return new Promise((resolve, reject) => {
    const acc = [];
    const read = () => {
      reader.readEntries((batch) => {
        if (!batch.length) {
          resolve(acc);
          return;
        }
        acc.push(...batch);
        read();
      }, reject);
    };
    read();
  });
}

async function traverseFileTreeEntry(entry, fileList) {
  if (entry.isFile) {
    await new Promise((resolve) => {
      entry.file((file) => {
        if (isGalleryImageFile(file)) fileList.push(file);
        resolve();
      });
    });
    return;
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    const entries = await readDirectoryEntriesAll(reader);
    await Promise.all(entries.map((e) => traverseFileTreeEntry(e, fileList)));
  }
}

/** Collect image files from a drag-drop DataTransfer (files and folders). */
async function collectGalleryFilesFromDataTransfer(dataTransfer) {
  const files = [];
  const items = dataTransfer?.items;
  if (!items?.length) {
    const fl = dataTransfer?.files;
    return uniqueGalleryImageFiles(fl);
  }
  const tasks = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.kind !== 'file') continue;
    const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
    if (entry) {
      tasks.push(traverseFileTreeEntry(entry, files));
    } else {
      const f = item.getAsFile();
      if (f && isGalleryImageFile(f)) files.push(f);
    }
  }
  await Promise.all(tasks);
  return uniqueGalleryImageFiles(files);
}

async function fetchGalleryItemsFromStorage(eventId, creditsMap) {
  const base = `${MEMORY_BOOK_STORAGE_PREFIX}/${eventId}/gallery/`;
  const galleryRef = ref(storage, base);
  const thumbsBase = `${MEMORY_BOOK_STORAGE_PREFIX}/${eventId}/galleryThumbs/`;
  const thumbsFolderRef = ref(storage, thumbsBase);
  const [listResult, thumbsListResult] = await Promise.all([
    listAll(galleryRef),
    listAll(thumbsFolderRef).catch(() => ({ items: [] })),
  ]);
  const thumbNames = new Set((thumbsListResult.items || []).map((r) => r.name));
  const cm = creditsMap && typeof creditsMap === 'object' ? creditsMap : {};
  const items = await Promise.all(
    listResult.items.map(async (itemRef) => {
      const url = await getDownloadURL(itemRef);
      const fullPath = itemRef.fullPath;
      const thumbKey = thumbKeyForFullPath(fullPath);
      let thumbUrl = '';
      if (thumbNames.has(thumbKey)) {
        try {
          thumbUrl = await getDownloadURL(ref(storage, `${thumbsBase}${thumbKey}`));
        } catch {
          thumbUrl = '';
        }
      }
      return {
        url,
        thumbUrl,
        fullPath,
        credit: cm[fullPath] || '',
      };
    })
  );
  items.sort((a, b) => a.fullPath.localeCompare(b.fullPath));
  return items;
}

async function imageFileToJpegBlob(file, { maxDim = 420, quality = 0.78 } = {}) {
  if (!file) throw new Error('Missing file');
  if (typeof createImageBitmap !== 'function') throw new Error('No bitmap support');
  const bmp = await createImageBitmap(file);
  const w = bmp.width || 1;
  const h = bmp.height || 1;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const tw = Math.max(1, Math.round(w * scale));
  const th = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement('canvas');
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('No canvas context');
  ctx.drawImage(bmp, 0, 0, tw, th);
  bmp.close?.();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) throw new Error('Could not encode JPEG');
  return blob;
}

async function imageFileToThumbnailJpegBlob(file, opts) {
  return imageFileToJpegBlob(file, { maxDim: 420, quality: 0.78, ...opts });
}

/** Tab-separated rows; double-quoted fields may contain tabs and newlines. */
function parseTabDelimitedRows(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const s = String(text || '').replace(/^\uFEFF/, '');

  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    const next = s[i + 1];

    if (inQuotes) {
      if (c === '"' && next === '"') {
        field += '"';
        i += 1;
        continue;
      }
      if (c === '"') {
        inQuotes = false;
        continue;
      }
      field += c;
      continue;
    }

    if (c === '"') {
      inQuotes = true;
      continue;
    }
    if (c === '\t') {
      row.push(field);
      field = '';
      continue;
    }
    if (c === '\n') {
      row.push(field);
      if (row.some((cell) => String(cell).trim() !== '')) rows.push(row);
      row = [];
      field = '';
      continue;
    }
    if (c === '\r') {
      if (next === '\n') i += 1;
      row.push(field);
      if (row.some((cell) => String(cell).trim() !== '')) rows.push(row);
      row = [];
      field = '';
      continue;
    }
    field += c;
  }

  row.push(field);
  if (row.some((cell) => String(cell).trim() !== '')) rows.push(row);

  return rows;
}

function capitalizeFirstToken(s) {
  const t = String(s || '').trim();
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

/** Best-effort first name from email local part; user should fix odd usernames in preview. */
function guessFirstNameFromEmail(email) {
  const local = String(email || '').split('@')[0].trim().toLowerCase();
  if (!local) return '';

  const byDot = local.split('.');
  if (byDot.length >= 2 && byDot[0].length <= 2 && byDot[1]) {
    return capitalizeFirstToken(byDot[1].replace(/[^a-z'-]/gi, ''));
  }
  if (byDot.length >= 2) {
    return capitalizeFirstToken(byDot[0].replace(/[^a-z'-]/gi, ''));
  }

  const byUnderscore = local.split('_');
  if (byUnderscore.length >= 2) {
    return capitalizeFirstToken(byUnderscore[0].replace(/[^a-z'-]/gi, ''));
  }

  const noTrailDigits = local.replace(/\d+$/g, '');
  return capitalizeFirstToken(noTrailDigits.replace(/[^a-z'-]/gi, '')) || capitalizeFirstToken(local);
}

/** Map lowercased email → display first name from event flyer roster (mentors + freshlings). */
function buildFirstNameByEmailMap(flyers) {
  const m = new Map();
  (flyers || []).forEach((f) => {
    const e = (f.email || '').toLowerCase().trim();
    if (!e || m.has(e)) return;
    const fn = getFirstName(f);
    if (fn) m.set(e, fn);
  });
  return m;
}

function testimonialDisplayNameFromRosterOrEmail(firstNameByEmail, email) {
  const k = String(email || '').toLowerCase().trim();
  if (k && firstNameByEmail?.has(k)) return firstNameByEmail.get(k);
  return guessFirstNameFromEmail(email);
}

function stripSpreadsheetQuotes(s) {
  return String(s || '').trim().replace(/^"|"$/g, '').replace(/""/g, '"');
}

function normalizeWrapSpaces(s) {
  // Some pasted content contains non-breaking spaces which prevent wrapping.
  return String(s || '').replace(/\u00A0/g, ' ').trim();
}

function buildSurveyTestimonialQuote(favorite, why, describe, mode) {
  const f = stripSpreadsheetQuotes(favorite);
  const w = stripSpreadsheetQuotes(why);
  const d = stripSpreadsheetQuotes(describe);
  switch (mode) {
    case 'favorite':
      return f;
    case 'why':
      return w;
    case 'all': {
      const parts = [];
      if (f) parts.push(`Favorite part: ${f}`);
      if (w) parts.push(`Why attend next year: ${w}`);
      if (d) parts.push(`How I describe it: ${d}`);
      return parts.join('\n\n');
    }
    case 'describe':
    default:
      return d;
  }
}

/**
 * One survey row may arrive as 4 TSV cells, or as one cell when tabs were lost (e.g. paste from chat).
 * Splits "email + rest" and then rest by tabs, or by 2+ spaces, or keeps rest as a single describe blob.
 */
function parseTestimonialSurveyRow(cells) {
  const parts = (cells || []).map((s) => stripSpreadsheetQuotes(String(s || '').trim()));

  if (parts.length >= 4 && parts[0].includes('@')) {
    return {
      email: parts[0],
      favorite: parts[1] || '',
      why: parts[2] || '',
      describe: parts[3] || '',
    };
  }

  if (parts.length === 3 && parts[0].includes('@')) {
    return { email: parts[0], favorite: parts[1] || '', why: parts[2] || '', describe: '' };
  }

  if (parts.length === 2 && parts[0].includes('@')) {
    const sub = parts[1].split(/\t+/).map((s) => s.trim()).filter(Boolean);
    if (sub.length >= 3) {
      return {
        email: parts[0],
        favorite: sub[0] || '',
        why: sub[1] || '',
        describe: sub.slice(2).join('\t') || '',
      };
    }
    if (sub.length === 2) {
      return { email: parts[0], favorite: sub[0] || '', why: sub[1] || '', describe: '' };
    }
    const bySpace = parts[1].split(/\s{2,}/).map((s) => s.trim()).filter(Boolean);
    if (bySpace.length >= 3) {
      return {
        email: parts[0],
        favorite: bySpace[0] || '',
        why: bySpace[1] || '',
        describe: bySpace.slice(2).join('  ') || '',
      };
    }
    return { email: parts[0], favorite: '', why: '', describe: parts[1] || '' };
  }

  if (parts.length === 1 && parts[0].includes('@')) {
    const whole = parts[0];
    const m = whole.match(/^([a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?:\s+([\s\S]*))?$/);
    if (!m) return null;
    const email = m[1];
    const rest = (m[2] || '').trim();
    if (!rest) {
      return { email, favorite: '', why: '', describe: '' };
    }
    const byTab = rest.split(/\t+/).map((s) => s.trim()).filter(Boolean);
    if (byTab.length >= 3) {
      return {
        email,
        favorite: byTab[0] || '',
        why: byTab[1] || '',
        describe: byTab.slice(2).join('\t') || '',
      };
    }
    if (byTab.length === 2) {
      return { email, favorite: byTab[0] || '', why: byTab[1] || '', describe: '' };
    }
    const bySpace = rest.split(/\s{2,}/).map((s) => s.trim()).filter(Boolean);
    if (bySpace.length >= 3) {
      return {
        email,
        favorite: bySpace[0] || '',
        why: bySpace[1] || '',
        describe: bySpace.slice(2).join('  ') || '',
      };
    }
    return { email, favorite: '', why: '', describe: rest };
  }

  return null;
}

/**
 * Round-robin stream: photos, quotes, highlights.
 * Winners and Quiplash are spliced in as single collapsible blocks on the page.
 */
function buildMemoryBookStream({
  galleryPhotos,
  testimonials,
  highlights,
}) {
  const galleryChunks = chunkArray(galleryPhotos || [], 3);

  let gi = 0;
  let ti = 0;
  let hi = 0;
  const items = [];

  const anyLeft = () =>
    gi < galleryChunks.length ||
    ti < (testimonials || []).length ||
    hi < (highlights || []).length;

  while (anyLeft()) {
    if (gi < galleryChunks.length) {
      items.push({ type: 'gallery', key: `g-${gi}`, photos: galleryChunks[gi] });
      gi += 1;
    }
    if (ti < (testimonials || []).length) {
      items.push({ type: 'testimonial', key: `t-${ti}`, item: testimonials[ti], index: ti });
      ti += 1;
    }
    if (hi < (highlights || []).length) {
      items.push({ type: 'highlight', key: `h-${hi}`, item: highlights[hi], index: hi });
      hi += 1;
    }
  }

  return items;
}

/** Resolve Quiplash byline: team name from teamNumber; legacy author string if no number. */
function getQuiplashTeamDisplayName(q, teamsList) {
  const raw = q?.teamNumber;
  if (raw != null && raw !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) {
      const team = (teamsList || []).find((t) => Number(t.teamNumber) === n);
      if (team) return getTeamLabelCore(team);
      return `Team ${n}`;
    }
  }
  const legacy = (q?.author || '').toString().trim();
  return legacy || '';
}

function truncateMemoryBookTeaser(s, max = 96) {
  const t = (s || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1))}…`;
}

function QuiplashTeaserCarousel({ quotes, teamsList, expanded, onToggleExpanded }) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
  }, [quotes]);

  useEffect(() => {
    if (expanded || !quotes.length || quotes.length < 2) return undefined;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % quotes.length);
    }, 4000);
    return () => clearInterval(t);
  }, [expanded, quotes.length]);

  const q = quotes[index];
  if (!q) return null;

  const quoteText = (q.winningQuote || q.quote || '').trim();
  const teamLine = getQuiplashTeamDisplayName(q, teamsList);
  const prompt = (q.prompt || '').trim();

  return (
    <>
      <button
        type="button"
        className="memory-book-quiplash-teaser"
        aria-expanded={expanded}
        onClick={onToggleExpanded}
      >
        <span className="memory-book-quiplash-teaser-mark" aria-hidden>“</span>
        <span className="memory-book-quiplash-teaser-slide" key={`q-teaser-${index}`}>
          <span className="memory-book-quiplash-teaser-copy">
            <span className="memory-book-quiplash-teaser-prompt">
              {prompt ? truncateMemoryBookTeaser(prompt, 110) : '—'}
            </span>
            {quoteText && (
              <span className="memory-book-quiplash-teaser-lead">
                "{truncateMemoryBookTeaser(quoteText, 100)}"
              </span>
            )}
            {teamLine && <span className="memory-book-quiplash-teaser-meta">— {teamLine}</span>}
            <span className="memory-book-quiplash-teaser-hint">
              {expanded
                ? 'Hide all quotes'
                : quotes.length > 1
                  ? `Open all Mic Drop quotes (${quotes.length})`
                  : 'Open Mic Drop quote'}
            </span>
          </span>
        </span>
        <span className="memory-book-quiplash-teaser-chevron" aria-hidden>
          {expanded ? '▲' : '▼'}
        </span>
      </button>
      {expanded && (
        <div className="memory-book-quiplash-expanded">
          {quotes.map((item, i) => {
            const tl = getQuiplashTeamDisplayName(item, teamsList);
            return (
              <div key={i} className="memory-book-card memory-book-quote memory-book-quote--stream">
                {item.prompt && <div className="memory-book-quiplash-prompt">{item.prompt}</div>}
                <p className="memory-book-quote-text memory-book-quote-text--micdrop">"{item.winningQuote || item.quote}"</p>
                {tl && <p className="memory-book-quote-byline">— {tl}</p>}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function WinnersTeaserCarousel({ slides, teamPhotosMap, expanded, onToggleExpanded }) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
  }, [slides]);

  useEffect(() => {
    if (expanded || !slides.length || slides.length < 2) return undefined;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 3400);
    return () => clearInterval(t);
  }, [expanded, slides.length]);

  const slide = slides[index];
  if (!slide) return null;
  const t = slide.team;
  const photo = teamPhotosMap[t.id];
  const divLabel = slide.division && slide.division.trim() && slide.division !== 'Unassigned'
    ? slide.division
    : 'Podium';

  return (
    <button
      type="button"
      className="memory-book-winners-teaser"
      aria-expanded={expanded}
      onClick={onToggleExpanded}
    >
      <span className="memory-book-winners-teaser-slide" key={`${slide.id}-${index}`}>
        <span className="memory-book-winners-teaser-thumb">
          {photo ? <img src={photo} alt="" /> : <span className="memory-book-winners-teaser-thumb-placeholder">?</span>}
        </span>
        <span className="memory-book-winners-teaser-copy">
          <span className="memory-book-winners-teaser-lead">
            #{t.rank} · {getTeamLabelCore(t)}
          </span>
          <span className="memory-book-winners-teaser-meta">
            {divLabel} · {t.total} pts
          </span>
          <span className="memory-book-winners-teaser-hint">
            {expanded ? 'Hide winners' : 'Open winners grid'}
          </span>
        </span>
      </span>
      <span className="memory-book-winners-teaser-chevron" aria-hidden>
        {expanded ? '▲' : '▼'}
      </span>
    </button>
  );
}

function TestimonialsTeaserCarousel({ items, expanded, onToggleExpanded }) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!list.length) {
      setIndex(0);
      return;
    }
    // Start on a random item so the teaser feels “sampled”.
    setIndex(Math.floor(Math.random() * list.length));
  }, [list.length]);

  useEffect(() => {
    if (expanded || list.length < 2) return undefined;
    const t = setInterval(() => {
      setIndex((prev) => {
        if (list.length < 2) return prev;
        let next = prev;
        // Pick a random next index; avoid immediate repeats.
        for (let tries = 0; tries < 6; tries += 1) {
          next = Math.floor(Math.random() * list.length);
          if (next !== prev) break;
        }
        return next;
      });
    }, 4200);
    return () => clearInterval(t);
  }, [expanded, list.length]);

  const cur = list[index];
  if (!cur) return null;

  const quote = normalizeWrapSpaces(cur.quote || cur.text || '');
  const by = (cur.name || 'Anonymous').trim() || 'Anonymous';
  const role = (cur.role || '').trim();

  return (
    <>
      <button
        type="button"
        className="memory-book-testimonials-teaser"
        aria-expanded={expanded}
        onClick={onToggleExpanded}
      >
        <span className="memory-book-testimonials-teaser-slide" key={`${by}-${index}`}>
          <span className="memory-book-testimonials-teaser-mark" aria-hidden>“</span>
          <span className="memory-book-testimonials-teaser-copy">
            <span className="memory-book-testimonials-teaser-quote">{quote}</span>
            <span className="memory-book-testimonials-teaser-meta">
              — {by}{role ? `, ${role}` : ''}
            </span>
            <span className="memory-book-testimonials-teaser-hint">
              {expanded ? 'Hide all quotes' : list.length > 1 ? `Open all quotes (${list.length})` : 'Open quote'}
            </span>
          </span>
        </span>
        <span className="memory-book-testimonials-teaser-chevron" aria-hidden>
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {expanded && (
        <div className="memory-book-testimonials-expanded">
          {list.map((t, i) => {
            const q = (t.quote || t.text || '').trim();
            if (!q) return null;
            const n = (t.name || 'Anonymous').trim() || 'Anonymous';
            const r = (t.role || '').trim();
            return (
              <div key={`${n}-${i}`} className="memory-book-quote memory-book-quote--prominent">
                <p className="memory-book-quote-text memory-book-quote-text--prominent">"{q}"</p>
                <p className="memory-book-quote-byline memory-book-quote-byline--prominent">
                  — {n}{r ? `, ${r}` : ''}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function shuffleCopy(list) {
  const a = [...(list || [])];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i];
    a[i] = a[j];
    a[j] = tmp;
  }
  return a;
}

function QuotesEngagementHub({ testimonials, micDropQuotes, teamsList }) {
  const safeTestimonials = useMemo(
    () => (Array.isArray(testimonials) ? testimonials.filter((t) => (t?.quote || t?.text)) : []),
    [testimonials]
  );
  const safeMicDrop = useMemo(
    () => (Array.isArray(micDropQuotes) ? micDropQuotes.filter((q) => (q?.winningQuote || q?.quote || q?.prompt)) : []),
    [micDropQuotes]
  );

  const [mode, setMode] = useState(() => (safeTestimonials.length ? 'testimonials' : 'micdrop'));
  useEffect(() => {
    if (mode === 'testimonials' && !safeTestimonials.length && safeMicDrop.length) setMode('micdrop');
    if (mode === 'micdrop' && !safeMicDrop.length && safeTestimonials.length) setMode('testimonials');
  }, [mode, safeTestimonials.length, safeMicDrop.length]);

  const [orderSeed, setOrderSeed] = useState(0);
  const [showCount, setShowCount] = useState(12);

  useEffect(() => {
    setShowCount(12);
  }, [mode, orderSeed]);

  const testimonialDeck = useMemo(() => {
    const base = safeTestimonials.map((t) => ({
      quote: normalizeWrapSpaces(t.quote || t.text || ''),
      by: (t.name || 'Anonymous').trim() || 'Anonymous',
      role: (t.role || '').trim(),
    }));
    return shuffleCopy(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeTestimonials, orderSeed]);

  const micDropDeck = useMemo(() => {
    const base = safeMicDrop.map((q) => ({
      prompt: normalizeWrapSpaces(q.prompt || ''),
      answer: normalizeWrapSpaces(q.winningQuote || q.quote || ''),
      by: getQuiplashTeamDisplayName(q, teamsList || []) || (q.author || 'Anonymous'),
    }));
    return shuffleCopy(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeMicDrop, orderSeed, teamsList]);

  const activeCount = mode === 'testimonials' ? testimonialDeck.length : micDropDeck.length;
  const canShowMore = showCount < activeCount;

  const featured = useMemo(() => {
    if (mode === 'testimonials') return testimonialDeck[0] || null;
    return micDropDeck[0] || null;
  }, [mode, testimonialDeck, micDropDeck]);

  return (
    <div className={`quotes-hub quotes-hub--${mode}`}>
      <div className="quotes-hub-sectionbar">
        <div className="quotes-hub-sectionbar-title">TESTIMONIALS</div>
        <div className="quotes-hub-sectionbar-meta" aria-label="Quote count">
          {activeCount} {activeCount === 1 ? 'QUOTE' : 'QUOTES'}
        </div>
      </div>
      <div className="quotes-hub-tabs" role="tablist" aria-label="Quote filters">
        {safeTestimonials.length > 0 && (
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'testimonials'}
            className={`memory-book-tab ${mode === 'testimonials' ? 'memory-book-tab--active' : ''}`}
            onClick={() => setMode('testimonials')}
          >
            Testimonials
          </button>
        )}
        {safeMicDrop.length > 0 && (
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'micdrop'}
            className={`memory-book-tab ${mode === 'micdrop' ? 'memory-book-tab--active' : ''}`}
            onClick={() => setMode('micdrop')}
          >
            Mic Drop
          </button>
        )}
        <button
          type="button"
          className="memory-book-tab"
          onClick={() => setOrderSeed((s) => s + 1)}
        >
          Shuffle
        </button>
      </div>

      {featured && (
        <div className="quotes-hub-feature">
          {mode === 'testimonials' ? (
            <>
              <p className="quotes-hub-feature-quote">"{featured.quote}"</p>
              <p className="quotes-hub-feature-byline">
                — {featured.by}{featured.role ? `, ${featured.role}` : ''}
              </p>
            </>
          ) : (
            <>
              {featured.prompt ? <p className="quotes-hub-feature-prompt">{featured.prompt}</p> : null}
              {featured.answer ? <p className="quotes-hub-feature-quote">"{featured.answer}"</p> : null}
              <p className="quotes-hub-feature-byline">— {featured.by}</p>
            </>
          )}
        </div>
      )}

      <div className="quotes-hub-grid">
        {(mode === 'testimonials' ? testimonialDeck : micDropDeck).slice(0, showCount).map((item, i) => {
          if (mode === 'testimonials') {
            return (
              <details key={`${item.by}-${i}`} className="quotes-hub-card">
                <summary className="quotes-hub-card-summary">
                  <span className="quotes-hub-card-quote">"{item.quote}"</span>
                  <span className="quotes-hub-card-byline">
                    — {item.by}{item.role ? `, ${item.role}` : ''}
                  </span>
                </summary>
                <div className="quotes-hub-card-body">
                  <p className="quotes-hub-card-quote-full">"{item.quote}"</p>
                </div>
              </details>
            );
          }
          return (
            <details key={`${item.by}-${i}`} className="quotes-hub-card">
              <summary className="quotes-hub-card-summary">
                {item.prompt ? <span className="quotes-hub-card-prompt">{item.prompt}</span> : null}
                {item.answer ? <span className="quotes-hub-card-quote">"{item.answer}"</span> : null}
                <span className="quotes-hub-card-byline">— {item.by}</span>
              </summary>
              <div className="quotes-hub-card-body">
                {item.prompt ? <p className="quotes-hub-card-prompt-full">{item.prompt}</p> : null}
                {item.answer ? <p className="quotes-hub-card-quote-full">"{item.answer}"</p> : null}
              </div>
            </details>
          );
        })}
      </div>

      <div className="quotes-hub-footer">
        {canShowMore ? (
          <button type="button" className="quotes-hub-more" onClick={() => setShowCount((c) => Math.min(activeCount, c + 12))}>
            Show more
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SponsorSectionCard({ sponsors }) {
  if (!Array.isArray(sponsors) || sponsors.length === 0) return null;
  return (
    <section className="memory-book-sponsor-card" aria-label="Sponsors">
      <h2 className="memory-book-sponsor-card-header">THANK YOU TO OUR SPONSORS</h2>
      <div className="memory-book-sponsor-card-body">
        <SponsorLogos sponsors={sponsors} />
      </div>
    </section>
  );
}

function MemoryBookShareButton({ url, title, text, className = '', children }) {
  const [hint, setHint] = useState('');
  const handle = async () => {
    if (!url) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        setHint('Link copied');
        window.setTimeout(() => setHint(''), 2200);
        return;
      } catch {
        // fall through
      }
    }
    window.prompt('Copy this link:', url);
    setHint('');
  };
  return (
    <span className="memory-book-share-control">
      <button
        type="button"
        className={`memory-book-share-btn ${className}`.trim()}
        onClick={handle}
        aria-label={typeof children === 'string' ? children : 'Copy team link'}
        title={typeof children === 'string' ? children : 'Copy team link'}
      >
        {children || 'Share'}
      </button>
      {hint ? <span className="memory-book-share-hint" role="status">{hint}</span> : null}
    </span>
  );
}

function LinkIcon({ size = 18 }) {
  return (
    <span className="fm-icon" aria-hidden>
      <span className="fm-icon-unicode" style={{ fontSize: size, lineHeight: 1 }}>⛓</span>
      <svg className="fm-icon-svg" width={size} height={size} viewBox="0 0 24 24" fill="none" focusable="false">
        <path
          d="M10.5 13.5l3-3"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.75 14.75l-1.5 1.5a4 4 0 1 1-5.66-5.66l1.5-1.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          transform="translate(6.66 0)"
        />
        <path
          d="M16.25 9.25l1.5-1.5a4 4 0 1 1 5.66 5.66l-1.5 1.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          transform="translate(-6.66 0)"
        />
      </svg>
    </span>
  );
}

function ShareIcon({ size = 18 }) {
  return (
    <span className="fm-icon" aria-hidden>
      <span className="fm-icon-unicode" style={{ fontSize: size, lineHeight: 1 }}>↗</span>
      <svg className="fm-icon-svg" width={size} height={size} viewBox="0 0 24 24" fill="none" focusable="false">
        <path
          d="M14 4h6v6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20 4L10 14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10 6H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function DownloadIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
      <path d="M12 3v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 11l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 21h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MemoryBookPageShareIcons({ url, title, text }) {
  const [hint, setHint] = useState('');

  const copy = useCallback(async () => {
    if (!url) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        setHint('Link copied');
        window.setTimeout(() => setHint(''), 2200);
        return;
      } catch {
        // fall through
      }
    }
    window.prompt('Copy this link:', url);
    setHint('');
  }, [url]);

  const share = useCallback(async () => {
    if (!url) return;
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: title || '', text: text || title || '', url });
        setHint('');
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      }
    }
    await copy();
  }, [copy, text, title, url]);

  if (!url) return null;

  return (
    <div className="memory-book-page-share" aria-label="Share this page">
      <button type="button" className="memory-book-page-share-btn" onClick={share} aria-label="Share this page" title="Share this page">
        <ShareIcon />
      </button>
      <button type="button" className="memory-book-page-share-btn" onClick={copy} aria-label="Copy link" title="Copy link">
        <LinkIcon />
      </button>
      {hint ? <span className="memory-book-page-share-hint" role="status">{hint}</span> : null}
    </div>
  );
}

function FocalPointEditorModal({
  open,
  title = 'Set focal point',
  imageUrl,
  initialFocalPoint,
  onChange,
  onClose,
  onSave,
  onPrev,
  onNext,
  canPrev,
  canNext,
  saving,
  footerHint,
}) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
  const fp = normalizeFocalPoint(initialFocalPoint) || { x: 0.5, y: 0.33 };

  if (!open) return null;

  const handlePick = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = e.clientX - r.left;
    const cy = e.clientY - r.top;

    // When using object-fit: contain, clicks must be mapped to the *rendered image area*,
    // not the full container (which may include letterboxing).
    const nw = imgNatural.w || imgRef.current?.naturalWidth || 0;
    const nh = imgNatural.h || imgRef.current?.naturalHeight || 0;
    if (nw > 0 && nh > 0) {
      const scale = Math.min(r.width / nw, r.height / nh);
      const rw = nw * scale;
      const rh = nh * scale;
      const ox = (r.width - rw) / 2;
      const oy = (r.height - rh) / 2;
      const x = clamp01((cx - ox) / Math.max(1, rw));
      const y = clamp01((cy - oy) / Math.max(1, rh));
      onChange?.({ x, y });
      return;
    }

    const x = clamp01(cx / Math.max(1, r.width));
    const y = clamp01(cy / Math.max(1, r.height));
    onChange?.({ x, y });
  };

  return (
    <div className="memory-book-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="focal-point-title">
      <div className="memory-book-modal memory-book-modal--focal" onClick={(e) => e.stopPropagation()}>
        <div className="memory-book-modal-header">
          <h3 id="focal-point-title">{title}</h3>
          <button type="button" className="memory-book-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="memory-book-modal-body">
          <p className="memory-book-focal-help">Click/tap the center of the faces. The dot shows the crop focus.</p>
          <div
            className="memory-book-focal-photo"
            ref={wrapRef}
            onClick={handlePick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handlePick(e);
            }}
            aria-label="Pick focal point"
          >
            {imageUrl ? (
              <img
                ref={imgRef}
                src={imageUrl}
                alt=""
                crossOrigin="anonymous"
                onLoad={(e) => {
                  const t = e.currentTarget;
                  setImgNatural({ w: t.naturalWidth || 0, h: t.naturalHeight || 0 });
                }}
              />
            ) : null}
            <div
              className="memory-book-focal-dot"
              style={{ left: `${fp.x * 100}%`, top: `${fp.y * 100}%` }}
              aria-hidden
            />
          </div>
          {footerHint ? <div className="memory-book-focal-hint">{footerHint}</div> : null}
        </div>
        <div className="memory-book-modal-footer">
          <div className="memory-book-focal-nav">
            <button type="button" className="memory-book-edit-btn" onClick={onPrev} disabled={!canPrev || saving}>Prev</button>
            <button type="button" className="memory-book-edit-btn" onClick={onNext} disabled={!canNext || saving}>Next</button>
          </div>
          <button type="button" className="memory-book-batch-save" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : (canNext ? 'Save & next' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function TeamStandingsDetailModal({
  team,
  teamPhotosMap,
  teamPosterUrl = '',
  bonusPuzzles,
  onClose,
  shareUrl,
  shareTitle,
  shareText,
}) {
  const posterRef = useRef(null);
  const teamPosterUrlRef = useRef(teamPosterUrl);
  teamPosterUrlRef.current = teamPosterUrl;

  const [imageHint, setImageHint] = useState('');
  const [generating, setGenerating] = useState(false);
  const [posterOmitPhoto, setPosterOmitPhoto] = useState(false);
  const [posterBlob, setPosterBlob] = useState(null);
  const [posterRemoteFetchFailed, setPosterRemoteFetchFailed] = useState(false);
  const [posterPreviewUrl, setPosterPreviewUrl] = useState(() => teamPosterUrl || '');
  const posterPreviewUrlForUnmountRef = useRef(posterPreviewUrl);
  posterPreviewUrlForUnmountRef.current = posterPreviewUrl;

  const teamId = team?.id || '';
  const photo = teamId ? teamPhotosMap[teamId] : '';
  const div = ((team?.division || '—').trim() || '—');

  useLayoutEffect(() => {
    setPosterOmitPhoto(false);
    setPosterBlob(null);
    setPosterRemoteFetchFailed(false);
    setGenerating(false);
    setPosterPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return teamPosterUrl || '';
    });
  }, [teamId, teamPosterUrl]);

  useEffect(() => () => {
    const u = posterPreviewUrlForUnmountRef.current;
    if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
  }, []);

  useEffect(() => {
    if (teamPosterUrl) return undefined;
    let cancelled = false;
    async function gen() {
      if (!shareUrl || !teamId) return;
      setGenerating(true);
      try {
        const waitForNodeImages = async (root, timeoutMs = 5000) => {
          if (!root) return;
          const start = Date.now();
          // Some mobile browsers (iOS Safari) finish loading images after first paint.
          // Poll until all current images are complete (or timeout).
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const imgs = Array.from(root.querySelectorAll('img'));
            const pending = imgs.filter((img) => !img.complete);
            if (!pending.length) return;
            if (Date.now() - start > timeoutMs) return;
            await new Promise((r) => window.setTimeout(r, 80));
          }
        };

        const tryRender = async (omitPhoto) => {
          setPosterOmitPhoto(!!omitPhoto);
          // Wait for the poster node to re-render.
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
          const node = posterRef.current?.querySelector?.('[data-poster]');
          if (!node) throw new Error('Poster node missing');
          // For the "photo" attempt, ensure images are loaded before snapshot.
          if (!omitPhoto) await waitForNodeImages(node, 5000);
          return await renderNodeToPngBlob(node, { width: 1080, height: 1080 });
        };

        let blob;
        try {
          blob = await tryRender(false);
        } catch (e) {
          if (photo) blob = await tryRender(true);
          else throw e;
        }

        if (cancelled) return;
        // A cached poster URL may have arrived while we were rendering (meta/storage).
        if (teamPosterUrlRef.current) return;
        setPosterBlob(blob);
        const url = URL.createObjectURL(blob);
        setPosterPreviewUrl(url);
      } catch {
        if (!cancelled) {
          if (teamPosterUrlRef.current) return;
          setPosterBlob(null);
          setPosterPreviewUrl('');
        }
      } finally {
        if (!cancelled) setGenerating(false);
      }
    }
    gen();
    return () => { cancelled = true; };
    // posterOmitPhoto intentionally excluded; we control omit via state inside gen()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareUrl, teamId, teamPosterUrl, photo]);

  // Prefetch cached (HTTPS) poster into memory so Share can run without await-fetch before
  // navigator.share — iOS Safari requires share() in the user-gesture chain.
  useEffect(() => {
    const url = posterPreviewUrl;
    if (!url) return undefined;
    const isHttp = url.startsWith('http://') || url.startsWith('https://');
    if (!isHttp) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url, { cache: 'no-store', mode: 'cors' });
        if (!res.ok) throw new Error(String(res.status));
        const b = await res.blob();
        if (!cancelled) {
          setPosterBlob(b);
          setPosterRemoteFetchFailed(false);
        }
      } catch {
        if (!cancelled) setPosterRemoteFetchFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [posterPreviewUrl, teamId]);

  if (!team) return null;

  const filename = `team-${team.teamNumber || team.id}-share.png`;

  const posterIsHttp =
    posterPreviewUrl &&
    (posterPreviewUrl.startsWith('http://') || posterPreviewUrl.startsWith('https://'));

  const runPosterShare = async () => {
    if (!shareUrl || generating || !posterPreviewUrl) return;
    setImageHint('');
    try {
      const blob = posterBlob;
      if (!blob && posterIsHttp && posterRemoteFetchFailed) {
        if (typeof navigator.share === 'function') {
          try {
            await navigator.share({
              title: shareTitle || 'Team',
              text: shareText || '',
              url: shareUrl,
            });
            setImageHint('Shared');
            window.setTimeout(() => setImageHint(''), 2500);
            return;
          } catch (e) {
            if (e && e.name === 'AbortError') return;
          }
        }
        window.alert(
          'Could not load the poster image. Use the link share button (chain icon) to share the team page.'
        );
        return;
      }
      if (!blob) return;
      const r = await shareOrDownloadPng({
        blob,
        filename,
        title: shareTitle || 'Team',
        text: shareText || '',
      });
      if (r === 'shared') setImageHint('Shared');
      else if (r === 'downloaded') setImageHint('Downloaded');
      window.setTimeout(() => setImageHint(''), 2500);
    } catch {
      window.alert('Could not generate the share image on this device. Try again, or use the Share team link.');
    }
  };

  const runPosterDownload = async () => {
    if (!posterPreviewUrl || generating) return;
    setImageHint('');
    try {
      const blob = posterBlob
        ? posterBlob
        : await (async () => {
            const res = await fetch(posterPreviewUrl, { cache: 'no-store' });
            return await res.blob();
          })();
      setPosterBlob(blob);
      downloadBlob(blob, filename);
    } catch {
      // Fallback: download via link (may open instead of saving on some browsers, esp. iOS).
      const a = document.createElement('a');
      a.href = posterPreviewUrl;
      a.download = filename;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setImageHint('Downloaded');
    window.setTimeout(() => setImageHint(''), 2500);
  };
  return (
    <div className="memory-book-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="memory-book-team-detail-title">
      <div className="memory-book-modal memory-book-modal--team-detail" onClick={(e) => e.stopPropagation()}>
        <div className="memory-book-modal-header memory-book-modal-header--team-detail">
          <h3 id="memory-book-team-detail-title">Team details</h3>
          <div className="memory-book-modal-header-actions">
            {shareUrl ? (
              <button
                type="button"
                className="memory-book-share-btn memory-book-share-btn--header"
                onClick={runPosterShare}
                disabled={generating || !posterPreviewUrl || (!posterBlob && !posterRemoteFetchFailed)}
                aria-label="Share image"
                title="Share image"
              >
                {generating ? 'Generating…' : <ShareIcon />}
              </button>
            ) : null}
            {shareUrl ? (
              <button
                type="button"
                className="memory-book-share-btn memory-book-share-btn--header memory-book-share-btn--icon"
                onClick={runPosterDownload}
                disabled={generating || !posterPreviewUrl}
                aria-label="Download image"
                title="Download image"
              >
                <DownloadIcon />
              </button>
            ) : null}
            {shareUrl ? (
              <MemoryBookShareButton
                url={shareUrl}
                title={shareTitle}
                text={shareText}
                className="memory-book-share-btn--header memory-book-share-btn--icon"
              >
                <LinkIcon />
              </MemoryBookShareButton>
            ) : null}
            {imageHint ? <span className="memory-book-share-hint" role="status">{imageHint}</span> : null}
            <button type="button" className="memory-book-modal-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>
        <div className="memory-book-modal-body memory-book-team-detail-body">
          <div className="memory-book-team-detail-poster">
            {posterPreviewUrl ? (
              <button
                type="button"
                className="memory-book-team-detail-poster-btn"
                onClick={runPosterShare}
                disabled={!posterPreviewUrl || generating || (!posterBlob && !posterRemoteFetchFailed)}
                aria-label="Share image"
              >
                <img className="memory-book-team-detail-poster-img" src={posterPreviewUrl} alt="" />
              </button>
            ) : (
              <div className="memory-book-team-detail-poster-skeleton" aria-hidden />
            )}
          </div>

          <div className="memory-book-team-detail-main">
            <h4 className="memory-book-team-detail-name">{getTeamLabelCore(team)}</h4>
            <p className="memory-book-team-detail-line">
              Rank <strong>#{team.rank}</strong>
              {' · '}
              Team #<strong>{team.teamNumber}</strong>
              {' · '}
              {div}
            </p>
            <dl className="memory-book-team-detail-scores">
              <div><dt>Total</dt><dd>{team.total}</dd></div>
              <div><dt>Sky subtotal</dt><dd>{team.skySubtotal}</dd></div>
              <div><dt>Bonus subtotal</dt><dd>{team.bonusSubtotal}</dd></div>
            </dl>
            {Array.isArray(team.skyScores) && team.skyScores.some(Boolean) && (
              <div className="memory-book-team-detail-rounds">
                <div className="memory-book-team-detail-rounds-title">Sky rounds</div>
                <div className="memory-book-team-detail-rounds-grid">
                  {['R1', 'R2', 'R3', 'R4'].map((label, i) => (
                    <div key={label} className="memory-book-team-detail-round">
                      <span>{label}</span>
                      <strong>{team.skyScores[i] !== '' && team.skyScores[i] != null ? team.skyScores[i] : '—'}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {Array.isArray(team.bonusScores) && team.bonusScores.some((v) => v > 0) && bonusPuzzles?.length > 0 && (
              <div className="memory-book-team-detail-bonus">
                <div className="memory-book-team-detail-rounds-title">Bonus puzzles</div>
                <div className="memory-book-team-detail-bonus-row" role="list">
                  {bonusPuzzles.flatMap((puzzle, i) => {
                    const v = team.bonusScores[i];
                    if (!v) return [];
                    const n = puzzle.number ?? i + 1;
                    return [(
                      <div
                        key={puzzle.id || i}
                        className="memory-book-team-detail-bonus-cell"
                        role="listitem"
                        title={puzzle.title ? `${puzzle.title} (${v} pts)` : undefined}
                      >
                        <span className="memory-book-team-detail-bonus-num">P{n}</span>
                        <strong>{v}</strong>
                      </div>
                    )];
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        {!teamPosterUrl ? (
          <div className="memory-book-share-poster-stage" aria-hidden="true" ref={posterRef}>
            <TeamSharePoster
              teamName={getTeamLabelCore(team)}
              teamNumber={team.teamNumber}
              division={div === '—' ? '' : div}
              rank={team.rank}
              totalPoints={team.total}
              photoUrl={posterOmitPhoto ? '' : photo}
              shareUrl={shareUrl}
              brandName="Fresh Meet"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function NumbersTeaserCarousel({ slides, expanded, onToggleExpanded }) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
  }, [slides]);

  useEffect(() => {
    if (expanded || !slides.length) return undefined;
    if (slides.length < 2) return undefined;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 3200);
    return () => clearInterval(t);
  }, [expanded, slides.length]);

  const slide = slides[index] || slides[0];
  if (!slide) return null;

  return (
    <button
      type="button"
      className="memory-book-numbers-teaser"
      aria-expanded={expanded}
      onClick={onToggleExpanded}
    >
      <span className="memory-book-numbers-teaser-slide" key={slide.id}>
        <span className="memory-book-numbers-teaser-value">
          {slide.kind === 'text' ? (
            <span className="memory-book-stat-value--teaser memory-book-teaser-value-text">{slide.display}</span>
          ) : (
            <AnimatedNumber key={`${slide.id}-${index}`} value={slide.value} className="memory-book-stat-value--teaser" />
          )}
        </span>
        <span className="memory-book-numbers-teaser-copy">
          <span className="memory-book-numbers-teaser-lead">{slide.label}</span>
          <span className="memory-book-numbers-teaser-hint">
            {expanded ? 'Hide full stats' : 'Open stats, map & more'}
          </span>
        </span>
      </span>
      <span className="memory-book-numbers-teaser-chevron" aria-hidden>
        {expanded ? '▲' : '▼'}
      </span>
    </button>
  );
}

const HERO_CAROUSEL_INTERVAL_MS = 6500;

/** Pack hero stat teasers into slides of 2–3 items (4 items → 2+2; 5 → 3+2; etc.). */
function chunkHeroStatSlides(items) {
  if (!items.length) return [];
  const out = [];
  let i = 0;
  while (i < items.length) {
    const left = items.length - i;
    if (left <= 3) {
      out.push(items.slice(i));
      break;
    }
    if (left === 4) {
      out.push(items.slice(i, i + 2));
      out.push(items.slice(i + 2, i + 4));
      break;
    }
    out.push(items.slice(i, i + 3));
    i += 3;
  }
  return out;
}

/** Top-of-page hero: cover + one slide per division’s podium (up to 3), text left / photo right. */
function MemoryBookHeroCarousel({
  eventTitle,
  editionYear,
  dateRangeWithYear,
  location,
  heroImageUrl,
  podiumDivisionEntries,
  teamPhotosMap,
  teamFocalMap,
  statsSlides = [],
  testimonials = [],
  galleryItems = [],
  basePath = '',
  memoryMeta,
  shareUrl = '',
  shareTitle = '',
  shareText = '',
  /** When true, share/copy controls live in the sticky public header — hide hero duplicates (V2 Step 4). */
  shareIconsInHeader = false,
  onTeamSelect,
}) {
  const heroGalleryPreview = useMemo(() => {
    const list = (galleryItems || []).filter((g) => g && g.url);
    const featured = Array.isArray(memoryMeta?.featuredGalleryPhotos) ? memoryMeta.featuredGalleryPhotos : [];
    const featuredItems = featured
      .map((fp) => list.find((g) => g.fullPath === fp))
      .filter(Boolean);
    const rest = list.filter((g) => !featured.includes(g.fullPath));
    const fill = sampleUnique(rest, Math.max(0, 7 - featuredItems.length), `${eventTitle || ''}|${dateRangeWithYear || ''}|hero-gallery-preview`);
    return [...featuredItems.slice(0, 7), ...fill].slice(0, 7);
  }, [galleryItems, eventTitle, dateRangeWithYear, memoryMeta?.featuredGalleryPhotos]);

  /** Per mount: random stat/quote hero backgrounds and one random short quote; stable until the carousel unmounts. */
  const heroCarouselRandRef = useRef(null);
  if (heroCarouselRandRef.current === null) {
    heroCarouselRandRef.current = mulberry32((Math.random() * 0xffffffff) >>> 0);
  }

  const slides = useMemo(() => {
    const rand = heroCarouselRandRef.current;
    const out = [];

    const coverUrl = (heroImageUrl || '').trim();
    out.push({ type: 'cover', key: 'cover', imageUrl: coverUrl });

    const galleryList = (galleryItems || []).filter((g) => g && g.url && g.fullPath);
    const featuredPaths = Array.isArray(memoryMeta?.featuredGalleryPhotos) ? memoryMeta.featuredGalleryPhotos : [];
    const featuredBgPool = featuredPaths
      .map((fp) => galleryList.find((g) => g.fullPath === fp))
      .filter(Boolean)
      .map((g) => ({ url: g.url, fullPath: g.fullPath }));

    const bgPool =
      featuredBgPool.length > 0
        ? featuredBgPool
        : galleryList.map((g) => ({ url: g.url, fullPath: g.fullPath }));

    const pickRandomBg = () => {
      if (!bgPool.length) return null;
      const idx = Math.floor(rand() * bgPool.length);
      return bgPool[idx];
    };

    // Build a pool of prominent photos (winner/team photos) to show full-bleed.
    const photoPool = [];
    (podiumDivisionEntries || []).forEach(([divisionRaw, divTeams]) => {
      (divTeams || []).forEach((t, placeIdx) => {
        if (placeIdx !== 0) return; // hero: first place only
        const url = teamPhotosMap?.[t.id];
        if (!url) return;
        photoPool.push({
          key: `photo-${t.id}`,
          teamId: t.id,
          url,
          caption: getTeamLabelCore(t),
          meta: `${ordinalPlace(placeIdx + 1)} place${divisionRaw && divisionRaw !== 'Unassigned' ? ` · ${divisionDisplayLabel(divisionRaw)}` : ''}`,
        });
      });
    });

    // De-dupe by url, preserve order.
    const seen = new Set();
    const photos = photoPool.filter((p) => {
      if (!p.url || seen.has(p.url)) return false;
      seen.add(p.url);
      return true;
    });

    // Use up to 12 photo slides so the carousel stays snappy.
    const photoSlides = photos.slice(0, 12).map((p) => ({
      type: 'photo',
      key: p.key,
      teamId: p.teamId,
      imageUrl: p.url,
      caption: p.caption,
      meta: p.meta,
    }));

    const statItemRows = (statsSlides || [])
      .filter(Boolean)
      .map((s, i) => ({
        id: s.id != null ? s.id : i,
        label: s.label || '',
        display: s.display != null ? String(s.display) : null,
        value: typeof s.value === 'number' && Number.isFinite(s.value) ? s.value : null,
      }))
      .filter((s) => s.label && (s.display != null || s.value != null));

    const statDeck = chunkHeroStatSlides(statItemRows).map((group) => {
      const bg = pickRandomBg();
      return {
        type: 'stat',
        key: group.map((g) => String(g.id)).join('+'),
        items: group.map((g) => ({
          key: `stat-${g.id}`,
          label: g.label,
          display: g.display,
          value: g.value,
        })),
        bgImageUrl: bg?.url || '',
        bgFullPath: bg?.fullPath || '',
      };
    });

    const testimonialEntries = (testimonials || [])
      .filter(Boolean)
      .map((t, i) => ({
        type: 'testimonial',
        key: `test-${t.name || 'anon'}-${i}`,
        quote: normalizeWrapSpaces(t.quote || t.text || ''),
        by: (t.name || 'Anonymous').trim(),
        role: (t.role || '').trim(),
      }))
      .filter((t) => t.quote);
    const shortPool = testimonialEntries.filter((t) => t.quote.length <= 140);
    const quotePickPool = shortPool.length ? shortPool : testimonialEntries;
    let testimonialDeck = [];
    if (quotePickPool.length) {
      const picked = quotePickPool[Math.floor(rand() * quotePickPool.length)];
      const bg = pickRandomBg();
      testimonialDeck = [
        {
          ...picked,
          bgImageUrl: bg?.url || '',
          bgFullPath: bg?.fullPath || '',
        },
      ];
    }

    // Prominence rule: mostly photos, with occasional stat/testimonial cards.
    // Roughly: insert 1 non-photo slide per 6 photo slides (cap to 3 each).
    const nonPhoto = [];
    const capStats = statDeck.slice(0, 3);
    const capTests = testimonialDeck.slice(0, 3);
    const maxNonPhoto = Math.min(6, capStats.length + capTests.length);
    for (let i = 0; i < maxNonPhoto; i += 1) {
      const pickStat = i % 2 === 0;
      const st = capStats.shift();
      const te = capTests.shift();
      if (pickStat && st) nonPhoto.push(st);
      else if (!pickStat && te) nonPhoto.push(te);
      else if (st) nonPhoto.push(st);
      else if (te) nonPhoto.push(te);
    }

    // Weave: cover, then (photo x N), inserting non-photo every 6 photos.
    let np = 0;
    photoSlides.forEach((p, i) => {
      out.push(p);
      if ((i + 1) % 6 === 0 && np < nonPhoto.length) {
        out.push(nonPhoto[np]);
        np += 1;
      }
    });
    while (np < nonPhoto.length) {
      out.push(nonPhoto[np]);
      np += 1;
    }

    return out.slice(0, 18);
  }, [heroImageUrl, podiumDivisionEntries, teamPhotosMap, statsSlides, testimonials, galleryItems, memoryMeta?.featuredGalleryPhotos]);

  const hasDivisionPodium = slides.some((s) => s.type === 'divisionPodium');

  const [index, setIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [paused, setPaused] = useState(false);
  const [shareHint, setShareHint] = useState('');

  const copyShareUrl = useCallback(async () => {
    if (!shareUrl) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareHint('Link copied');
        window.setTimeout(() => setShareHint(''), 2200);
        return;
      } catch {
        // fall through
      }
    }
    window.prompt('Copy this link:', shareUrl);
    setShareHint('');
  }, [shareUrl]);

  const openShareSheet = useCallback(async () => {
    if (!shareUrl) return;
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: shareTitle || '', text: shareText || shareTitle || '', url: shareUrl });
        setShareHint('');
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        // fall through to copy
      }
    }
    await copyShareUrl();
  }, [copyShareUrl, shareText, shareTitle, shareUrl]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fn = () => setReducedMotion(!!mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  useEffect(() => {
    setIndex(0);
  }, [slides.length, heroImageUrl]);

  useEffect(() => {
    if (slides.length < 2 || reducedMotion || paused) return undefined;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, HERO_CAROUSEL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [slides.length, reducedMotion, paused]);

  const slideAction = (sl) => {
    if (!basePath) return null;
    if (sl.type === 'cover') return { to: basePath, label: 'Open main page →' };
    if (sl.type === 'photo' && sl.teamId) return { to: `${basePath}/leaderboard?teamId=${encodeURIComponent(sl.teamId)}`, label: 'View team details →' };
    if (sl.type === 'stat') return { to: `${basePath}#numbers`, label: 'Open full stats →' };
    if (sl.type === 'testimonial') return { to: `${basePath}/quotes`, label: 'Read more quotes →' };
    if (sl.type === 'divisionPodium') return { to: `${basePath}/leaderboard`, label: 'Open leaderboard →' };
    return null;
  };

  const renderDisplayCaption = (caption) => {
    const raw = (caption || '').toString().trim();
    if (!raw) return null;
    const parts = raw.split(/\s+/);
    if (parts.length <= 1) return <span className="memory-book-hero-display">{raw}</span>;
    const last = parts.pop();
    const head = parts.join(' ');
    return (
      <span className="memory-book-hero-display">
        {head} <span className="accent">{last}</span>
      </span>
    );
  };

  return (
    <div
      className={`memory-book-hero-carousel${hasDivisionPodium ? ' memory-book-hero-carousel--division-podium' : ''}`}
      role="region"
      aria-roledescription="carousel"
      aria-label="Event header"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
    >
      <div className="memory-book-hero-carousel-viewport">
        {shareUrl && !shareIconsInHeader ? (
          <div className="memory-book-hero-share" aria-label="Share this page">
            <button
              type="button"
              className="memory-book-hero-share-btn"
              onClick={openShareSheet}
              aria-label="Share this page"
              title="Share this page"
            >
              <ShareIcon />
            </button>
            <button
              type="button"
              className="memory-book-hero-share-btn"
              onClick={copyShareUrl}
              aria-label="Copy link"
              title="Copy link"
            >
              <LinkIcon />
            </button>
            {shareHint ? <span className="memory-book-hero-share-hint" role="status">{shareHint}</span> : null}
          </div>
        ) : null}
        {basePath && heroGalleryPreview.length > 0 ? (
          <div className="memory-book-hero-gallery-overlay" aria-label="Gallery preview">
            <NavLink className="memory-book-hero-gallery-overlay-link" to={`${basePath}/gallery`} title="Open gallery">
              <span className="memory-book-hero-gallery-overlay-label">Gallery</span>
              <span className="memory-book-hero-gallery-overlay-grid" aria-hidden>
                {heroGalleryPreview.map((g, i) => (
                  <span key={`${g.fullPath || g.url}-${i}`} className="memory-book-hero-gallery-overlay-thumb">
                    <img
                      src={g.thumbUrl || g.url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      style={{ objectPosition: galleryPhotoObjectPosition(g.fullPath, memoryMeta) }}
                    />
                  </span>
                ))}
              </span>
            </NavLink>
          </div>
        ) : null}
        {slides.map((sl, i) => {
          const active = i === index;
          const action = active ? slideAction(sl) : null;
          if (sl.type === 'cover') {
            return (
              <div
                key={sl.key}
                className={`memory-book-hero-slide memory-book-hero-slide--cover${sl.imageUrl ? '' : ' memory-book-hero-slide--cover-noimg'}${active ? ' memory-book-hero-slide--active' : ''}`}
                aria-hidden={!active}
              >
                <div className="memory-book-hero-badge" aria-hidden>
                  <span className="memory-book-hero-badge-brand">FRESH MEET</span>
                  {editionYear ? <span className="memory-book-hero-badge-year"> {editionYear}</span> : null}
                </div>
                {sl.imageUrl ? (
                  <img
                    src={sl.imageUrl}
                    alt=""
                    className="memory-book-hero-slide-img memory-book-hero-slide-img--cover"
                    decoding="async"
                  />
                ) : null}
                <div className="memory-book-hero-slide-scrim" aria-hidden />
                <div className="memory-book-hero-titleblock">
                  <h1 className="memory-book-hero-title memory-book-hero-title--overlay">{eventTitle || 'Event'}</h1>
                  {dateRangeWithYear && (
                    <p className="memory-book-hero-dates memory-book-hero-dates--overlay">{dateRangeWithYear}</p>
                  )}
                  {location && <p className="memory-book-hero-location memory-book-hero-location--overlay">{location}</p>}
                </div>
                {action ? (
                  <NavLink className="memory-book-hero-slide-action" to={action.to}>
                    {action.label}
                  </NavLink>
                ) : null}
              </div>
            );
          }
          if (sl.type === 'photo') {
            return (
              <div
                key={sl.key}
                className={`memory-book-hero-slide memory-book-hero-slide--photo${active ? ' memory-book-hero-slide--active' : ''}`}
                aria-hidden={!active}
              >
                <div className="memory-book-hero-badge" aria-hidden>
                  <span className="memory-book-hero-badge-brand">FRESH MEET</span>
                  {editionYear ? <span className="memory-book-hero-badge-year"> {editionYear}</span> : null}
                </div>
                {sl.teamId && typeof onTeamSelect === 'function' ? (
                  <button
                    type="button"
                    className="memory-book-hero-photo-btn"
                    onClick={() => onTeamSelect(sl.teamId)}
                    aria-label="Open team details"
                  >
                    <img
                      src={sl.imageUrl}
                      alt=""
                      className="memory-book-hero-slide-img memory-book-hero-slide-img--photo"
                      style={{ objectPosition: teamPhotoHeroObjectPosition(sl.teamId, memoryMeta, teamFocalMap) }}
                      decoding="async"
                    />
                  </button>
                ) : (
                  <img
                    src={sl.imageUrl}
                    alt=""
                    className="memory-book-hero-slide-img memory-book-hero-slide-img--photo"
                    style={{ objectPosition: teamPhotoHeroObjectPosition(sl.teamId, memoryMeta, teamFocalMap) }}
                    decoding="async"
                  />
                )}
                <div className="memory-book-hero-slide-scrim memory-book-hero-slide-scrim--soft" aria-hidden />
                <div className="memory-book-hero-titleblock memory-book-hero-titleblock--compact">
                  {sl.meta ? <p className="memory-book-hero-kicker">{sl.meta}</p> : null}
                  {sl.caption ? <p className="memory-book-hero-caption">{renderDisplayCaption(sl.caption)}</p> : null}
                </div>
                {action ? (
                  <NavLink className="memory-book-hero-slide-action" to={action.to}>
                    {action.label}
                  </NavLink>
                ) : null}
              </div>
            );
          }
          if (sl.type === 'stat') {
            return (
              <div
                key={sl.key}
                className={`memory-book-hero-slide memory-book-hero-slide--stat${active ? ' memory-book-hero-slide--active' : ''}`}
                aria-hidden={!active}
              >
                {sl.bgImageUrl ? (
                  <img
                    src={sl.bgImageUrl}
                    alt=""
                    className="memory-book-hero-slide-img memory-book-hero-slide-img--bg"
                    decoding="async"
                    loading="lazy"
                    style={sl.bgFullPath ? { objectPosition: galleryPhotoObjectPosition(sl.bgFullPath, memoryMeta) } : undefined}
                  />
                ) : null}
                <div className="memory-book-hero-slide-scrim memory-book-hero-slide-scrim--bg" aria-hidden />
                <div className="memory-book-hero-stat-card">
                  <div
                    className={`memory-book-hero-stat-grid memory-book-hero-stat-grid--${(sl.items || []).length || 1}`}
                  >
                    {(sl.items || []).map((it) => (
                      <div key={it.key} className="memory-book-hero-stat-block">
                        <div className="memory-book-hero-stat-value">
                          {it.display != null ? (
                            <span>{it.display}</span>
                          ) : (
                            <AnimatedNumber
                              key={`${it.key}-${index}`}
                              value={it.value}
                              className="memory-book-hero-stat-value-number"
                            />
                          )}
                        </div>
                        <div className="memory-book-hero-stat-label">{it.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {action ? (
                  <NavLink className="memory-book-hero-slide-action" to={action.to}>
                    {action.label}
                  </NavLink>
                ) : null}
              </div>
            );
          }
          if (sl.type === 'testimonial') {
            return (
              <div
                key={sl.key}
                className={`memory-book-hero-slide memory-book-hero-slide--testimonial${active ? ' memory-book-hero-slide--active' : ''}`}
                aria-hidden={!active}
              >
                {sl.bgImageUrl ? (
                  <img
                    src={sl.bgImageUrl}
                    alt=""
                    className="memory-book-hero-slide-img memory-book-hero-slide-img--bg"
                    decoding="async"
                    loading="lazy"
                    style={sl.bgFullPath ? { objectPosition: galleryPhotoObjectPosition(sl.bgFullPath, memoryMeta) } : undefined}
                  />
                ) : null}
                <div className="memory-book-hero-slide-scrim memory-book-hero-slide-scrim--bg" aria-hidden />
                <div className="memory-book-hero-testimonial-card">
                  <div className="memory-book-hero-testimonial-copy">
                    <p className="memory-book-hero-testimonial-quote">"{sl.quote}"</p>
                    <p className="memory-book-hero-testimonial-byline">
                      — {sl.by}
                      {sl.role ? `, ${sl.role}` : ''}
                    </p>
                    {action ? (
                      <NavLink
                        className="memory-book-hero-slide-action memory-book-hero-slide-action--in-testimonial"
                        to={action.to}
                      >
                        {action.label}
                      </NavLink>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          }
          if (sl.type === 'divisionPodium') {
            return (
              <div
                key={sl.key}
                className={`memory-book-hero-slide memory-book-hero-slide--division-podium${active ? ' memory-book-hero-slide--active' : ''}`}
                aria-hidden={!active}
              >
                <div className="memory-book-hero-podium-inner">
                  <header className="memory-book-hero-podium-division-banner">{sl.division}</header>
                  <div className="memory-book-hero-podium-rows">
                    {sl.teams.map((t, placeIdx) => {
                      const place = placeIdx + 1;
                      const url = teamPhotosMap[t.id];
                      return (
                        <div key={t.id} className="memory-book-hero-podium-row">
                          <div className="memory-book-hero-podium-row-text">
                            <div className="memory-book-hero-podium-division-place">
                              <span className="memory-book-hero-podium-place">{ordinalPlace(place)} place</span>
                              <span className="memory-book-hero-podium-division-chip">{sl.division}</span>
                            </div>
                            <div className="memory-book-hero-podium-team">{getTeamLabelCore(t)}</div>
                            <div className="memory-book-hero-podium-pts">{t.total} pts</div>
                          </div>
                          <div className="memory-book-hero-podium-row-photo">
                            <div className="memory-book-hero-podium-row-photo-inner">
                              {url ? (
                                <button
                                  type="button"
                                  className="memory-book-hero-podium-photo-btn"
                                  onClick={() => (typeof onTeamSelect === 'function' ? onTeamSelect(t.id) : null)}
                                  aria-label="Open team details"
                                >
                                  <img
                                    src={url}
                                    alt=""
                                    decoding="async"
                                    style={{ objectPosition: teamPhotoHeroObjectPosition(t.id, memoryMeta, teamFocalMap) }}
                                  />
                                </button>
                              ) : (
                                <span className="memory-book-hero-podium-photo-placeholder" aria-hidden />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {action ? (
                  <NavLink className="memory-book-hero-slide-action" to={action.to}>
                    {action.label}
                  </NavLink>
                ) : null}
              </div>
            );
          }
          return null;
        })}
      </div>

      {slides.length > 1 && (
        <div className="memory-book-hero-carousel-dots" role="tablist" aria-label="Header slides">
          {slides.map((sl, i) => (
            <button
              key={sl.key}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={`memory-book-hero-carousel-dot${i === index ? ' memory-book-hero-carousel-dot--active' : ''}`}
              onClick={() => setIndex(i)}
              aria-label={
                sl.type === 'cover'
                  ? 'Title and cover image'
                  : sl.type === 'photo'
                    ? `Photo — ${sl.caption || 'team'}`
                    : sl.type === 'stat'
                      ? `Stats — ${(sl.items || []).map((it) => it.label).filter(Boolean).join(', ')}`
                      : sl.type === 'testimonial'
                        ? `Testimonial — ${sl.by || 'quote'}`
                        : `Podium — ${sl.division}`
              }
            />
          ))}
        </div>
      )}

      {slides.length > 1 && (
        <>
          <button
            type="button"
            className="memory-book-hero-carousel-nav memory-book-hero-carousel-nav--prev"
            onClick={() => setIndex((j) => (j - 1 + slides.length) % slides.length)}
            aria-label="Previous slide"
          >
            ‹
          </button>
          <button
            type="button"
            className="memory-book-hero-carousel-nav memory-book-hero-carousel-nav--next"
            onClick={() => setIndex((j) => (j + 1) % slides.length)}
            aria-label="Next slide"
          >
            ›
          </button>
        </>
      )}

      <p className="memory-book-hero-scroll-nudge" aria-hidden="true">
        <span className="memory-book-hero-scroll-nudge-text">Scroll for more</span>
        <span className="memory-book-hero-scroll-nudge-arrow">↓</span>
      </p>
    </div>
  );
}

function EventMemoryBook({ isPublic = false, forcedEventId = '', publicPage = PUBLIC_PAGES.MAIN }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventIdFromUrl = searchParams.get('eventId') || '';
  const resolvedEventId = forcedEventId || eventIdFromUrl;
  const tabFromUrl = searchParams.get('tab') || '';

  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [eventsLoading, setEventsLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [teams, setTeams] = useState([]);
  const [scores, setScores] = useState([]);
  const [bonusPuzzles, setBonusPuzzles] = useState([]);
  const [bonusScores, setBonusScores] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [memoryMeta, setMemoryMeta] = useState(null);
  const [flyers, setFlyers] = useState([]);
  const [dzMappings, setDzMappings] = useState({});
  const [galleryItems, setGalleryItems] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState('');

  const [activeTab, setActiveTab] = useState(TABS.PREVIEW);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [teamPhotoUploading, setTeamPhotoUploading] = useState({});
  const [numbersExpanded, setNumbersExpanded] = useState(false);
  const [statsAnimKey, setStatsAnimKey] = useState(0);
  const [geoListModal, setGeoListModal] = useState(null);
  const [winnersExpanded, setWinnersExpanded] = useState(false);
  const [winnersCarouselKey, setWinnersCarouselKey] = useState(0);
  const [quiplashExpanded, setQuiplashExpanded] = useState(false);
  const [testimonialsExpanded, setTestimonialsExpanded] = useState(false);
  const [standingsDetailTeam, setStandingsDetailTeam] = useState(null);
  /** When Firestore meta has no posterUrl, try canonical Storage path (same as upload/backfill). */
  const [teamDetailStoragePosterUrl, setTeamDetailStoragePosterUrl] = useState('');
  const [galleryModalIndex, setGalleryModalIndex] = useState(null);
  const [galleryPage, setGalleryPage] = useState(0);

  const [testimonialForm, setTestimonialForm] = useState({ quote: '', name: '', role: '' });
  const [editingTestimonialIndex, setEditingTestimonialIndex] = useState(null);
  const [introForm, setIntroForm] = useState({
    intro: '',
    heroImageUrl: '',
    highlights: [],
    statsOverrideForm: emptyMemoryBookStatsOverrideForm(),
  });
  const [quiplashForm, setQuiplashForm] = useState({ prompt: '', winningQuote: '', teamNumber: '' });
  const [editingQuiplashIndex, setEditingQuiplashIndex] = useState(null);

  const [focalEditorOpen, setFocalEditorOpen] = useState(false);
  const [focalEditorQueue, setFocalEditorQueue] = useState([]);
  const [focalEditorIndex, setFocalEditorIndex] = useState(0);
  const [focalEditorPoint, setFocalEditorPoint] = useState(null);
  const [focalEditorSaving, setFocalEditorSaving] = useState(false);

  useEffect(() => {
    if (isPublic && resolvedEventId) {
      setSelectedEventId(resolvedEventId);
      setEventsLoading(false);
      return;
    }
    let cancelled = false;
    async function loadEvents() {
      setEventsLoading(true);
      try {
        const snap = await getDocs(collection(firestore, 'events'));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!cancelled) setEvents(list);
        if (list.length && !cancelled) {
          const activeId = await getActiveEventId();
          if (cancelled) return;
          setSelectedEventId(
            resolveAdminDefaultEventId({
              eventIdParam: resolvedEventId,
              activeEventId: activeId,
              events: list,
            })
          );
        }
      } catch (e) {
        if (!cancelled) setDataError(e.message || 'Failed to load events.');
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    }
    loadEvents();
    return () => { cancelled = true; };
  }, [resolvedEventId, isPublic]);

  useEffect(() => {
    if (isPublic && resolvedEventId) return;
    if (resolvedEventId && events.some((e) => e.id === resolvedEventId)) {
      setSelectedEventId(resolvedEventId);
    } else if (events.length && !selectedEventId) {
      let cancelled = false;
      (async () => {
        const activeId = await getActiveEventId();
        if (cancelled) return;
        setSelectedEventId(
          resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events })
        );
      })();
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [resolvedEventId, events, isPublic, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    setDataLoading(true);
    setDataError('');
    let cancelled = false;

    async function load() {
      try {
        const [
          eventSnap,
          teamsSnap,
          scoresSnap,
          puzzlesSnap,
          bonusScoresSnap,
          metaSnap,
          flyersSnap,
          dzMappingsSnap,
        ] = await Promise.all([
          getDoc(doc(firestore, 'events', selectedEventId)),
          getDocs(query(collection(firestore, 'teams'), where('eventId', '==', selectedEventId))),
          getDocs(query(collection(firestore, 'scores'), where('eventId', '==', selectedEventId))),
          getDocs(query(collection(firestore, 'bonusPuzzles'), where('eventId', '==', selectedEventId))),
          getDocs(query(collection(firestore, 'bonusScores'), where('eventId', '==', selectedEventId))),
          getDoc(doc(firestore, 'eventMemoryMeta', selectedEventId)),
          getDocs(query(collection(firestore, 'flyers'), where('eventId', '==', selectedEventId))),
          getDoc(doc(firestore, '_config', 'dzMappings')),
        ]);
        const sponsorsMerged = await fetchSponsorsMergedForEvent(selectedEventId);

        if (cancelled) return;

        const ev = eventSnap.exists() ? { id: eventSnap.id, ...eventSnap.data() } : null;
        setEvent(ev);

        const teamsList = teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTeams(teamsList);
        setScores(scoresSnap.docs.map((d) => d.data()));
        const puzzles = puzzlesSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));
        setBonusPuzzles(puzzles);
        setBonusScores(bonusScoresSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        const allowedMemoryBookSponsorTypes = new Set(['interactive', 'exhibit', 'exhibitor']);
        const sponsorList = sponsorsMerged
          .filter((s) => {
            if (!s || s.hidden) return false;
            const t = String(s.type || 'none').toLowerCase().trim();
            if (t === 'none') return false;
            return allowedMemoryBookSponsorTypes.has(t);
          });
        sponsorList.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        setSponsors(sponsorList);
        const metaData = metaSnap.exists() ? metaSnap.data() : null;
        setMemoryMeta(metaData);
        setFlyers(flyersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setDzMappings(dzMappingsSnap.exists() ? (dzMappingsSnap.data().mappings || {}) : {});
        if (!cancelled) {
          setGalleryItems([]);
          setGalleryPage(0);
        }
      } catch (e) {
        if (!cancelled) setDataError(e.message || 'Failed to load memory book data.');
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    if (!memoryMeta) return;
    const isGalleryView =
      (isPublic && publicPage === PUBLIC_PAGES.GALLERY) ||
      (!isPublic && activeTab === TABS.GALLERY);
    if (!isGalleryView) return;
    let cancelled = false;
    const loadGallery = async () => {
      try {
        const creditsMap =
          memoryMeta.galleryCredits && typeof memoryMeta.galleryCredits === 'object'
            ? memoryMeta.galleryCredits
            : {};
        const items = await fetchGalleryItemsFromStorage(selectedEventId, creditsMap);
        if (!cancelled) {
          setGalleryItems(items);
          setGalleryPage(0);
        }
      } catch {
        if (!cancelled) {
          setGalleryItems([]);
          setGalleryPage(0);
        }
      }
    };
    loadGallery();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId, memoryMeta, isPublic, publicPage, activeTab]);

  useEffect(() => {
    if (isPublic) return;
    const t = tabFromUrl && Object.values(TABS).includes(tabFromUrl) ? tabFromUrl : TABS.PREVIEW;
    setActiveTab(t);
  }, [tabFromUrl, isPublic]);

  const nonTestTeams = useMemo(() => teams.filter((t) => !t.test), [teams]);

  const teamPhotosMap = useMemo(() => {
    const arr = Array.isArray(memoryMeta?.teamPhotos) ? memoryMeta.teamPhotos : [];
    const map = {};
    arr.forEach((e) => {
      if (e && e.teamId) map[e.teamId] = e.url;
    });
    return map;
  }, [memoryMeta?.teamPhotos]);

  const teamPostersMap = useMemo(() => {
    const arr = Array.isArray(memoryMeta?.teamPhotos) ? memoryMeta.teamPhotos : [];
    const map = {};
    arr.forEach((e) => {
      if (e && e.teamId && e.posterUrl) map[e.teamId] = e.posterUrl;
    });
    return map;
  }, [memoryMeta?.teamPhotos]);

  const standingsDetailMetaPoster = standingsDetailTeam ? teamPostersMap[standingsDetailTeam.id] : '';

  useEffect(() => {
    if (!standingsDetailTeam?.id || !selectedEventId) {
      setTeamDetailStoragePosterUrl('');
      return;
    }
    if (standingsDetailMetaPoster) {
      setTeamDetailStoragePosterUrl('');
      return;
    }
    let cancelled = false;
    setTeamDetailStoragePosterUrl('');
    (async () => {
      try {
        const posterRef = ref(storage, `${MEMORY_BOOK_STORAGE_PREFIX}/${selectedEventId}/teamPosters/${standingsDetailTeam.id}.png`);
        const url = await getDownloadURL(posterRef);
        if (!cancelled) setTeamDetailStoragePosterUrl(url);
      } catch {
        if (!cancelled) setTeamDetailStoragePosterUrl('');
      }
    })();
    return () => { cancelled = true; };
  }, [standingsDetailTeam?.id, selectedEventId, standingsDetailMetaPoster]);

  const rankedTeams = useMemo(() => {
    if (!nonTestTeams.length) return [];
    return calculateRank(nonTestTeams, scores, bonusScores, bonusPuzzles);
  }, [nonTestTeams, scores, bonusScores, bonusPuzzles]);

  const podiumByDivision = useMemo(() => {
    const byDiv = {};
    rankedTeams.forEach((t) => {
      const div = (t.division || 'Unassigned').trim() || 'Unassigned';
      if (!byDiv[div]) byDiv[div] = [];
      if (byDiv[div].length < 3) byDiv[div].push(t);
    });
    return byDiv;
  }, [rankedTeams]);

  const sortedPodiumDivisionEntries = useMemo(
    () => Object.entries(podiumByDivision).sort(([a], [b]) => compareDivisionLabel(a, b)),
    [podiumByDivision]
  );

  const winnersPodiumDivisionEntries = useMemo(
    () =>
      Object.entries(podiumByDivision).sort(([a], [b]) => {
        const A = (a || '').trim().toLowerCase();
        const B = (b || '').trim().toLowerCase();
        const rank = (d) => {
          if (d === 'intermediate') return 0;
          if (d === 'beginner') return 1;
          if (!d || d === 'unassigned') return 99;
          return 2;
        };
        const ra = rank(A);
        const rb = rank(B);
        if (ra !== rb) return ra - rb;
        return (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });
      }),
    [podiumByDivision]
  );

  const winnerTeaserSlides = useMemo(
    () =>
      winnersPodiumDivisionEntries.flatMap(([division, divTeams]) =>
        divTeams.map((t) => ({
          id: `${division}-${t.id}`,
          team: t,
          division,
        }))
      ),
    [winnersPodiumDivisionEntries]
  );

  const standingsByDivision = useMemo(() => {
    const m = new Map();
    rankedTeams.forEach((t) => {
      const d = (t.division || '').trim() || 'Unassigned';
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(t);
    });
    return Array.from(m.entries())
      .sort(([a], [b]) => compareDivisionLabel(a, b))
      .map(([division, teams]) => ({
        division,
        teams: [...teams].sort((a, b) => a.rank - b.rank),
      }));
  }, [rankedTeams]);

  const countryDisplayNames = useMemo(() => {
    try {
      // Use the browser's current language if possible; fall back to English.
      const locale = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'en';
      return typeof Intl !== 'undefined' && Intl.DisplayNames
        ? new Intl.DisplayNames([locale], { type: 'region' })
        : null;
    } catch {
      return null;
    }
  }, []);

  const numbersDataBase = useMemo(() => {
    const totalTeams = nonTestTeams.length;
    const activeActivityFlyers = flyers.filter(isActiveForActivityStats);
    const pairedEmails = new Set(
      nonTestTeams.flatMap((t) => (t.freshlings || []).map((e) => (e || '').trim().toLowerCase())).filter(Boolean)
    );
    const pairedFreshlings = flyers.filter(
      (f) =>
        f.type === 'freshling' &&
        !f.test &&
        !f.cancelled &&
        !isWithdrawnOrDuplicate(f) &&
        pairedEmails.has((f.email || '').trim().toLowerCase())
    );
    const pairedFreshlingCount = pairedFreshlings.length;
    const jumpNumbers = pairedFreshlings
      .map((f) => parseInt(f.jumpNumber, 10))
      .filter((n) => Number.isFinite(n) && n >= 0);
    const avgJumps = jumpNumbers.length > 0
      ? Math.round(jumpNumbers.reduce((a, b) => a + b, 0) / jumpNumbers.length)
      : null;
    const pairedMentors = new Set(
      nonTestTeams
        .map((t) => (t.mentor || '').toString().trim().toLowerCase())
        .filter(Boolean)
    ).size;
    const canopyUsed =
      activeActivityFlyers.reduce((sum, f) => {
        return sum + usageOrManifestCount(f, CANOPY_THUR) + usageOrManifestCount(f, CANOPY_FRI);
      }, 0) || 0;
    const waterUsed =
      activeActivityFlyers.reduce((sum, f) => {
        return sum + usageOrManifestCount(f, WATER_THUR) + usageOrManifestCount(f, WATER_FRI);
      }, 0) || 0;

    const ages = pairedFreshlings
      .map((f) => getAgeFromYear(f.yearBorn))
      .filter((a) => a != null && a > 0 && a < 120);
    const youngest = ages.length > 0 ? Math.min(...ages) : null;
    const oldest = ages.length > 0 ? Math.max(...ages) : null;
    const ageRangeLabel =
      youngest != null && oldest != null
        ? youngest === oldest
          ? `${youngest} yrs`
          : `${youngest}–${oldest} yrs`
        : null;

    const highAltFromUsage = sumUsageOrManifest(activeActivityFlyers, TICKET_HIGH_ALTITUDE);
    const highAltitudeJumps = highAltFromUsage;
    const jetJumps = sumUsageOrManifest(activeActivityFlyers, TICKET_JET);

    const geoFreshlings = flyers.filter(
      (f) => f.type === 'freshling' && !f.test && !f.cancelled && !isWithdrawnOrDuplicate(f)
    );
    const statesSet = new Set();
    const countriesSet = new Set();
    const countryCounts = {};
    const stateCountsLocal = {};
    geoFreshlings.forEach((f) => {
      const raw = (f.homeDz || '').trim();
      if (!raw) return;
      const m = dzMappings[raw.toLowerCase()];
      if (!m) return;
      const country = (m.country || 'US').trim().toUpperCase() || 'US';
      countriesSet.add(country);
      countryCounts[country] = (countryCounts[country] || 0) + 1;
      if (country === 'US' && m.state) {
        const st = m.state.toUpperCase();
        statesSet.add(st);
        stateCountsLocal[st] = (stateCountsLocal[st] || 0) + 1;
      }
    });
    const usStateCount = statesSet.size;
    const countryCount = countriesSet.size;
    const stateList = Object.keys(stateCountsLocal)
      .sort((a, b) => (stateCountsLocal[b] || 0) - (stateCountsLocal[a] || 0) || a.localeCompare(b))
      .map((code) => ({
        code,
        name: US_STATE_NAMES[code] || code,
        count: stateCountsLocal[code] || 0,
      }));
    const countryList = Object.keys(countryCounts)
      .sort((a, b) => (countryCounts[b] || 0) - (countryCounts[a] || 0) || a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map((code) => ({
        code,
        name: countryDisplayNames?.of(code) || code,
        count: countryCounts[code] || 0,
      }));

    const activities = {
      highAltitude: highAltitudeJumps,
      jet: jetJumps,
      canopy: canopyUsed,
      water: waterUsed,
    };
    const hasAnyActivity =
      activities.highAltitude > 0 ||
      activities.jet > 0 ||
      activities.canopy > 0 ||
      activities.water > 0;

    const teaserSlides = [];
    const pushNum = (id, value, label) => {
      teaserSlides.push({ id, kind: 'number', value, label });
    };
    pushNum('freshlings', pairedFreshlingCount, 'Freshlings');
    pushNum('teams', totalTeams, 'Teams');
    pushNum('mentors', pairedMentors, 'Mentors');
    if (avgJumps != null) pushNum('avgJumps', avgJumps, 'Avg. jumps at start');
    if (ageRangeLabel) {
      teaserSlides.push({
        id: 'ages',
        kind: 'text',
        display: ageRangeLabel,
        label: 'Freshling ages',
      });
    }
    if (activities.highAltitude > 0) {
      pushNum('ha', activities.highAltitude, 'High altitude jumps');
    }
    if (activities.jet > 0) pushNum('jet', activities.jet, 'Jet jumps');
    if (activities.canopy > 0) pushNum('canopy', activities.canopy, 'Canopy courses');
    if (activities.water > 0) pushNum('water', activities.water, 'Water training');
    if (usStateCount > 0) pushNum('states', usStateCount, 'States');
    if (countryCount > 0) pushNum('countries', countryCount, 'Countries');

    return {
      teaserSlides,
      hero: { value: pairedFreshlingCount, label: 'Freshlings' },
      chips: [
        { id: 'teams', label: 'Teams', value: totalTeams, tone: 'slate' },
        { id: 'mentors', label: 'Mentors', value: pairedMentors, tone: 'amber' },
        ...(avgJumps != null
          ? [{ id: 'avgJumps', label: 'Avg. jumps at start', value: avgJumps, tone: 'violet' }]
          : []),
        ...(usStateCount > 0
          ? [{ id: 'states', label: 'States', value: usStateCount, tone: 'teal' }]
          : []),
        ...(countryCount > 0
          ? [{ id: 'countries', label: 'Countries', value: countryCount, tone: 'indigo' }]
          : []),
      ],
      ageRange: ageRangeLabel ? { label: 'Freshling ages', text: ageRangeLabel } : null,
      activities,
      hasAnyActivity,
      stateList,
      countryList,
    };
  }, [nonTestTeams, flyers, dzMappings, countryDisplayNames]);

  const numbersData = useMemo(
    () => applyMemoryBookStatsOverrides(numbersDataBase, memoryMeta?.statsOverrides),
    [numbersDataBase, memoryMeta?.statsOverrides]
  );

  const stateCounts = useMemo(() => {
    const counts = {};
    const activeFreshlings = flyers.filter(
      (f) => f.type === 'freshling' && !f.test && !f.cancelled && !isWithdrawnOrDuplicate(f)
    );
    activeFreshlings.forEach((f) => {
      const raw = (f.homeDz || '').trim();
      if (!raw) return;
      const m = dzMappings[raw.toLowerCase()];
      if (m && m.state && (!m.country || m.country === 'US')) {
        const st = m.state.toUpperCase();
        counts[st] = (counts[st] || 0) + 1;
      }
    });
    return counts;
  }, [flyers, dzMappings]);

  const galleryPhotosForStream = useMemo(
    () =>
      galleryItems.map((i) => ({
        url: i.url,
        credit: (i.credit || '').trim(),
      })),
    [galleryItems]
  );

  const testimonialsList = useMemo(
    () => (Array.isArray(memoryMeta?.testimonials) ? memoryMeta.testimonials : []),
    [memoryMeta?.testimonials]
  );

  const highlightsList = useMemo(
    () => (Array.isArray(memoryMeta?.highlights) ? memoryMeta.highlights : []),
    [memoryMeta?.highlights]
  );

  const memoryStreamItems = useMemo(() => {
    const includeGalleryStream = !(isPublic && publicPage === PUBLIC_PAGES.MAIN);
    return buildMemoryBookStream({
      galleryPhotos: includeGalleryStream ? galleryPhotosForStream : [],
      testimonials: testimonialsList,
      highlights: highlightsList,
    });
  }, [galleryPhotosForStream, testimonialsList, highlightsList, isPublic, publicPage]);

  const quiplashQuotesList = useMemo(
    () => (Array.isArray(memoryMeta?.quiplashQuotes) ? memoryMeta.quiplashQuotes : []),
    [memoryMeta?.quiplashQuotes]
  );
  const videoCategories = useMemo(() => {
    const videos = memoryMeta?.videos && typeof memoryMeta.videos === 'object' ? memoryMeta.videos : {};
    const beginnerMeet = normalizeVideoUrl(videos.beginnerMeet);
    const intermediateMeet = normalizeVideoUrl(videos.intermediateMeet);
    const loadOrganizer = normalizeVideoUrl(videos.loadOrganizer);
    const canopyControlCourse = dedupeVideoUrls(
      Array.isArray(videos.canopyControlCourse) ? videos.canopyControlCourse : []
    );
    return [
      { key: 'beginner', title: 'Beginner Meet Videos', links: beginnerMeet ? [beginnerMeet] : [] },
      { key: 'intermediate', title: 'Intermediate Meet Videos', links: intermediateMeet ? [intermediateMeet] : [] },
      { key: 'loadOrganizer', title: 'Load Organizer Videos', links: loadOrganizer ? [loadOrganizer] : [] },
      { key: 'canopy', title: 'Canopy Control Course Videos', links: canopyControlCourse },
    ];
  }, [memoryMeta?.videos]);
  const hasVideoLinks = useMemo(
    () => videoCategories.some((c) => c.links.length > 0),
    [videoCategories]
  );

  const memoryStreamWithWinners = useMemo(() => {
    const items = [...memoryStreamItems];
    const baseInsert = items.length === 0 ? 0 : Math.min(2, items.length);
    const hasWinners = rankedTeams.length > 0 && winnerTeaserSlides.length > 0;
    const hasQuiplash = quiplashQuotesList.length > 0;
    if (hasWinners) {
      items.splice(baseInsert, 0, { type: 'winners', key: 'winners-block' });
    }
    if (hasQuiplash) {
      const qInsert = hasWinners ? baseInsert + 1 : baseInsert;
      items.splice(qInsert, 0, { type: 'quiplashSection', key: 'quiplash-block' });
    }
    return items;
  }, [memoryStreamItems, rankedTeams.length, winnerTeaserSlides.length, quiplashQuotesList.length]);

  const dateRangeWithYear = event ? getEventDateRangeWithYear(event) : null;

  const publicGalleryCreditQuery =
    isPublic && publicPage === PUBLIC_PAGES.GALLERY
      ? (searchParams.get('galleryCredit') || '').trim()
      : '';

  const setPublicGalleryCreditFilter = useCallback(
    (nextRaw) => {
      setGalleryModalIndex(null);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          const v = (nextRaw ?? '').toString().trim();
          if (!v) p.delete('galleryCredit');
          else p.set('galleryCredit', v);
          p.delete('photo');
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const galleryModalList = useMemo(
    () => filterGalleryItemsByCreditParam(galleryItems, publicGalleryCreditQuery),
    [galleryItems, publicGalleryCreditQuery]
  );

  const publicGalleryCreditOptions = useMemo(
    () => uniqueTrimmedGalleryCredits(galleryItems),
    [galleryItems]
  );

  const closeGalleryModal = useCallback(() => {
    setGalleryModalIndex(null);
    if (!isPublic) return;
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('photo');
        return p;
      },
      { replace: true }
    );
  }, [isPublic, setSearchParams]);

  const openGalleryModalByFullPath = useCallback(
    (fullPath) => {
      if (!fullPath) return;
      const idx = galleryModalList.findIndex((g) => g.fullPath === fullPath);
      if (idx < 0) {
        if (isPublic) {
          setSearchParams(
            (prev) => {
              const p = new URLSearchParams(prev);
              p.delete('photo');
              return p;
            },
            { replace: true }
          );
        }
        return;
      }
      setGalleryModalIndex(idx);
      if (!isPublic) return;
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('eventId', selectedEventId || '');
          p.set('photo', fullPath);
          return p;
        },
        { replace: true }
      );
    },
    [galleryModalList, isPublic, setSearchParams, selectedEventId]
  );

  const teamFocalMap = useMemo(() => {
    const out = {};
    (teams || []).forEach((t) => {
      const fp = teamDocFocalPoint(t);
      if (fp) out[t.id] = fp;
    });
    return out;
  }, [teams]);

  const syncTeamDetailInUrl = useCallback(
    (team) => {
      if (!selectedEventId) return;
      if (isPublic) {
        setSearchParams(
          (prev) => {
            const p = new URLSearchParams(prev);
            p.set('eventId', selectedEventId);
            if (team?.id) p.set('teamId', team.id);
            else p.delete('teamId');
            return p;
          },
          { replace: true }
        );
        return;
      }
      const p = new URLSearchParams(searchParams);
      p.set('eventId', selectedEventId);
      if (team?.id) p.set('teamId', team.id);
      else p.delete('teamId');
      if (activeTab !== TABS.PREVIEW) p.set('tab', activeTab);
      else p.delete('tab');
      navigate(`/admin/event-memory-book?${p.toString()}`, { replace: true });
    },
    [isPublic, selectedEventId, setSearchParams, navigate, searchParams, activeTab]
  );

  const openFocalPointEditor = useCallback(
    ({ kind, teamId, fullPath } = {}) => {
      if (kind === 'team' && teamId) {
        const list = (rankedTeams.length ? rankedTeams : nonTestTeams).filter((t) => teamPhotosMap?.[t.id]);
        const idx = Math.max(0, list.findIndex((t) => t.id === teamId));
        const q = list.map((t) => ({
          kind: 'team',
          key: t.id,
          label: getTeamLabelCore(t),
          imageUrl: teamPhotosMap[t.id],
          focalPoint: teamDocFocalPoint(teams.find((x) => x.id === t.id)) || null,
          teamId: t.id,
        }));
        setFocalEditorQueue(q);
        setFocalEditorIndex(idx);
        setFocalEditorPoint(q[idx]?.focalPoint || { x: 0.5, y: 0.33 });
        setFocalEditorOpen(true);
        return;
      }
      if (kind === 'gallery' && fullPath) {
        const list = (galleryItems || []).filter((i) => i?.fullPath);
        const idx = Math.max(0, list.findIndex((i) => i.fullPath === fullPath));
        const q = list.map((i) => ({
          kind: 'gallery',
          key: i.fullPath,
          label: i.fullPath.split('/').pop() || 'Gallery photo',
          imageUrl: i.url,
          focalPoint: normalizeFocalPoint(memoryMeta?.galleryFocalPoints?.[i.fullPath]) || null,
          fullPath: i.fullPath,
        }));
        setFocalEditorQueue(q);
        setFocalEditorIndex(idx);
        setFocalEditorPoint(q[idx]?.focalPoint || { x: 0.5, y: 0.5 });
        setFocalEditorOpen(true);
      }
    },
    [rankedTeams, nonTestTeams, teamPhotosMap, teams, galleryItems, memoryMeta?.galleryFocalPoints]
  );

  const saveFocalPoint = useCallback(async () => {
    const item = focalEditorQueue[focalEditorIndex];
    const fp = normalizeFocalPoint(focalEditorPoint);
    if (!item || !fp) return;
    setFocalEditorSaving(true);
    setSaveError('');
    try {
      if (item.kind === 'team' && item.teamId) {
        await setDoc(
          doc(firestore, 'teams', item.teamId),
          { photoFocalPoint: fp, photoFocalPointUpdatedAt: serverTimestamp() },
          { merge: true }
        );
        setTeams((prev) => (prev || []).map((t) => (t.id === item.teamId ? { ...t, photoFocalPoint: fp } : t)));
      } else if (item.kind === 'gallery' && item.fullPath && selectedEventId) {
        await setDoc(
          doc(firestore, 'eventMemoryMeta', selectedEventId),
          { galleryFocalPoints: { [item.fullPath]: fp } },
          { merge: true }
        );
        setMemoryMeta((prev) => {
          const base = prev || {};
          const gfp = { ...(base.galleryFocalPoints || {}) };
          gfp[item.fullPath] = fp;
          return { ...base, galleryFocalPoints: gfp };
        });
      }

      setFocalEditorQueue((prev) =>
        prev.map((q, i) => (i === focalEditorIndex ? { ...q, focalPoint: fp } : q))
      );

      if (focalEditorIndex < focalEditorQueue.length - 1) {
        const nextIndex = focalEditorIndex + 1;
        setFocalEditorIndex(nextIndex);
        setFocalEditorPoint(focalEditorQueue[nextIndex]?.focalPoint || fp);
      }
    } catch (e) {
      setSaveError(e.message || 'Failed to save focal point');
    } finally {
      setFocalEditorSaving(false);
    }
  }, [focalEditorQueue, focalEditorIndex, focalEditorPoint, selectedEventId, setSaveError]);

  const openTeamDetailById = useCallback(
    (teamId) => {
      if (!teamId) return;
      const t = rankedTeams.find((x) => x.id === teamId) || teams.find((x) => x.id === teamId) || null;
      if (t) syncTeamDetailInUrl(t);
    },
    [rankedTeams, teams, syncTeamDetailInUrl]
  );

  useEffect(() => {
    if (!selectedEventId || dataLoading) return;
    const tid = searchParams.get('teamId');
    if (!tid) {
      setStandingsDetailTeam(null);
      return;
    }
    if (!rankedTeams.length) return;
    const t = rankedTeams.find((x) => x.id === tid);
    setStandingsDetailTeam(t || null);
  }, [selectedEventId, dataLoading, searchParams, rankedTeams]);

  useEffect(() => {
    if (!isPublic || !selectedEventId || dataLoading) return;
    if (publicPage !== PUBLIC_PAGES.GALLERY) return;
    const fp = searchParams.get('photo');
    if (!fp) {
      setGalleryModalIndex(null);
      return;
    }
    openGalleryModalByFullPath(fp);
  }, [isPublic, selectedEventId, dataLoading, searchParams, publicPage, openGalleryModalByFullPath]);

  useEffect(() => {
    if (!event || !selectedEventId) return;
    const base = event.title ? `${event.title} — Memory Book` : 'Memory Book';
    document.title = standingsDetailTeam
      ? `${getTeamLabelCore(standingsDetailTeam)} · ${base}`
      : base;

    if (isPublic && event.memoryBookPublished === true) {
      const url = buildPublicMemoryBookUrl({
        year: getEventEditionYear(event),
        page: PUBLIC_PAGES.MAIN,
        teamId: standingsDetailTeam?.id,
      });
      const desc = standingsDetailTeam
        ? `Rank #${standingsDetailTeam.rank} · ${standingsDetailTeam.total} pts — ${event.title || 'event'}`
        : (dateRangeWithYear ? `${event.title} · ${dateRangeWithYear}` : (event.title || 'Event memory book'));
      const setOg = (prop, content) => {
        let el = document.querySelector(`meta[property="${prop}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('property', prop);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };
      setOg('og:url', url);
      setOg('og:title', document.title);
      setOg('og:description', desc);
    }

    return () => {
      document.title = 'Fresh Meet';
    };
  }, [event, selectedEventId, standingsDetailTeam, isPublic, dateRangeWithYear]);

  if (isPublic && event && event.memoryBookPublished !== true) {
    return (
      <div className="memory-book memory-book-unpublished">
        <h2>Event Memory Book</h2>
        <p>This memory book is not published yet.</p>
        <p>Check back later or contact the event organizers.</p>
      </div>
    );
  }

  if (eventsLoading || (selectedEventId && dataLoading && !event)) {
    return (
      <div className="memory-book memory-book-loading">
        <h2>Event Memory Book</h2>
        <p>Loading…</p>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="memory-book memory-book-error">
        <h2>Event Memory Book</h2>
        <p>{dataError}</p>
      </div>
    );
  }

  if (!selectedEventId || !event) {
    return (
      <div className="memory-book memory-book-error">
        <h2>Event Memory Book</h2>
        <p>{isPublic ? 'No event specified or event not found.' : 'Select an event or no event data found.'}</p>
      </div>
    );
  }

  const intro = (memoryMeta && memoryMeta.intro) || '';
  const heroImageUrl = memoryMeta?.heroImageUrl || '';
  const publicYear = getEventEditionYear(event);
  const publicBasePath = publicYear ? `/${publicYear}/archive` : '';
  const resolvedPublicPage = Object.values(PUBLIC_PAGES).includes(publicPage) ? publicPage : PUBLIC_PAGES.MAIN;
  const pageShareUrl =
    (isPublic || (activeTab === TABS.PREVIEW && event.memoryBookPublished))
      ? buildPublicMemoryBookUrl({ year: publicYear, page: resolvedPublicPage })
      : '';
  const pageShareTitle = event.title ? `${event.title} — Memory Book` : 'Memory Book';
  const pageShareText = event.title ? `Memory book: ${event.title}` : 'Event memory book';
  const featuredGalleryItems = (() => {
    const fp = Array.isArray(memoryMeta?.featuredGalleryPhotos) ? memoryMeta.featuredGalleryPhotos : [];
    if (!fp.length) return [];
    let out = fp
      .map((path) => galleryItems.find((g) => g.fullPath === path))
      .filter(Boolean);
    if (isPublic && publicPage === PUBLIC_PAGES.GALLERY && publicGalleryCreditQuery) {
      if (publicGalleryCreditQuery === GALLERY_CREDIT_FILTER_NONE) {
        out = out.filter((g) => !(g.credit || '').trim());
      } else {
        out = out.filter((g) => (g.credit || '').trim() === publicGalleryCreditQuery);
      }
    }
    return out;
  })();

  const GALLERY_PAGE_SIZE = 72;
  const publicGalleryBaseItems =
    isPublic && publicPage === PUBLIC_PAGES.GALLERY
      ? filterGalleryItemsByCreditParam(galleryItems, publicGalleryCreditQuery)
      : galleryItems;
  const publicGalleryGridItems = publicGalleryBaseItems.slice(
    0,
    (galleryPage + 1) * GALLERY_PAGE_SIZE
  );
  const galleryModalShareUrl = (() => {
    if (!isPublic || galleryModalIndex == null) return '';
    const item = galleryModalList[galleryModalIndex];
    if (!item?.fullPath) return '';
    const base = buildPublicMemoryBookUrl({ year: publicYear, page: PUBLIC_PAGES.GALLERY });
    try {
      const u = new URL(base, window.location.origin);
      u.searchParams.set('eventId', selectedEventId || '');
      u.searchParams.set('photo', item.fullPath);
      return u.toString();
    } catch {
      return base;
    }
  })();

  const setTab = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set('eventId', selectedEventId);
    if (tab !== TABS.PREVIEW) {
      params.set('tab', tab);
      params.delete('teamId');
      setStandingsDetailTeam(null);
    } else {
      params.delete('tab');
    }
    navigate(`/admin/event-memory-book?${params.toString()}`, { replace: true });
  };

  return (
    <div className={`memory-book ${isPublic ? 'memory-book--public' : 'memory-book--admin'}`}>
      {!isPublic && (
        <>
          <div className="memory-book-header">
            <h2>Event Memory Book</h2>
            <div>
              <select
                className="memory-book-select"
                value={selectedEventId}
                onChange={(e) => {
                  const id = e.target.value;
                  setStandingsDetailTeam(null);
                  navigate(id ? `/admin/event-memory-book?eventId=${encodeURIComponent(id)}` : '/admin/event-memory-book');
                  setSelectedEventId(id);
                }}
              >
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title || ev.id}
                  </option>
                ))}
              </select>
              {event.memoryBookPublished && (
                <span className="memory-book-public-link" style={{ marginLeft: '1rem' }}>
                  <a
                    href={publicBasePath || `/memory-book?eventId=${encodeURIComponent(selectedEventId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View public page →
                  </a>
                </span>
              )}
            </div>
          </div>
          <div className="memory-book-tabs">
            <button type="button" className={`memory-book-tab ${activeTab === TABS.PREVIEW ? 'memory-book-tab--active' : ''}`} onClick={() => setTab(TABS.PREVIEW)}>Preview</button>
            <button type="button" className={`memory-book-tab ${activeTab === TABS.TEAM_PHOTOS ? 'memory-book-tab--active' : ''}`} onClick={() => setTab(TABS.TEAM_PHOTOS)}>Team photos</button>
            <button type="button" className={`memory-book-tab ${activeTab === TABS.GALLERY ? 'memory-book-tab--active' : ''}`} onClick={() => setTab(TABS.GALLERY)}>Gallery</button>
            <button type="button" className={`memory-book-tab ${activeTab === TABS.TESTIMONIALS ? 'memory-book-tab--active' : ''}`} onClick={() => setTab(TABS.TESTIMONIALS)}>Testimonials</button>
            <button type="button" className={`memory-book-tab ${activeTab === TABS.INTRO_HERO ? 'memory-book-tab--active' : ''}`} onClick={() => setTab(TABS.INTRO_HERO)}>Intro & hero</button>
            <button type="button" className={`memory-book-tab ${activeTab === TABS.QUIPLASH ? 'memory-book-tab--active' : ''}`} onClick={() => setTab(TABS.QUIPLASH)}>Quiplash</button>
            <button type="button" className={`memory-book-tab ${activeTab === TABS.VIDEOS ? 'memory-book-tab--active' : ''}`} onClick={() => setTab(TABS.VIDEOS)}>Videos</button>
            <button type="button" className={`memory-book-tab ${activeTab === TABS.NEXT_YEAR_INTEREST ? 'memory-book-tab--active' : ''}`} onClick={() => setTab(TABS.NEXT_YEAR_INTEREST)}>Next year</button>
          </div>
          {saveError && <p className="memory-book-save-error">{saveError}</p>}
        </>
      )}

      {!isPublic && activeTab !== TABS.PREVIEW && (
        <div className="memory-book-tab-panel">
          {activeTab === TABS.TEAM_PHOTOS && (
            <TeamPhotosTab
              eventId={selectedEventId}
              event={event}
              nonTestTeams={nonTestTeams}
              rankedTeams={rankedTeams}
              flyers={flyers}
              teamPhotosMap={teamPhotosMap}
              teamPostersMap={teamPostersMap}
              teamFocalMap={teamFocalMap}
              memoryMeta={memoryMeta}
              setMemoryMeta={setMemoryMeta}
              setTeamPhotoUploading={setTeamPhotoUploading}
              teamPhotoUploading={teamPhotoUploading}
              setSaveError={setSaveError}
              onOpenFocalPointEditor={openFocalPointEditor}
            />
          )}
          {activeTab === TABS.GALLERY && (
            <GalleryTab
              eventId={selectedEventId}
              galleryItems={galleryItems}
              setGalleryItems={setGalleryItems}
              memoryMeta={memoryMeta}
              setMemoryMeta={setMemoryMeta}
              galleryUploading={galleryUploading}
              setGalleryUploading={setGalleryUploading}
              setSaveError={setSaveError}
              onOpenFocalPointEditor={openFocalPointEditor}
            />
          )}
          {activeTab === TABS.TESTIMONIALS && (
            <TestimonialsTab
              eventId={selectedEventId}
              flyers={flyers}
              memoryMeta={memoryMeta}
              setMemoryMeta={setMemoryMeta}
              testimonialForm={testimonialForm}
              setTestimonialForm={setTestimonialForm}
              editingTestimonialIndex={editingTestimonialIndex}
              setEditingTestimonialIndex={setEditingTestimonialIndex}
              saving={saving}
              setSaving={setSaving}
              setSaveError={setSaveError}
            />
          )}
          {activeTab === TABS.INTRO_HERO && (
            <IntroHeroTab
              eventId={selectedEventId}
              memoryMeta={memoryMeta}
              setMemoryMeta={setMemoryMeta}
              introForm={introForm}
              setIntroForm={setIntroForm}
              computedNumbers={numbersDataBase}
              saving={saving}
              setSaving={setSaving}
              setSaveError={setSaveError}
            />
          )}
          {activeTab === TABS.QUIPLASH && (
            <QuiplashTab
              eventId={selectedEventId}
              teams={nonTestTeams}
              memoryMeta={memoryMeta}
              setMemoryMeta={setMemoryMeta}
              quiplashForm={quiplashForm}
              setQuiplashForm={setQuiplashForm}
              editingQuiplashIndex={editingQuiplashIndex}
              setEditingQuiplashIndex={setEditingQuiplashIndex}
              saving={saving}
              setSaving={setSaving}
              setSaveError={setSaveError}
            />
          )}
          {activeTab === TABS.VIDEOS && (
            <VideosTab
              eventId={selectedEventId}
              memoryMeta={memoryMeta}
              setMemoryMeta={setMemoryMeta}
              saving={saving}
              setSaving={setSaving}
              setSaveError={setSaveError}
            />
          )}
          {activeTab === TABS.NEXT_YEAR_INTEREST && (
            <NextYearInterestTab />
          )}
        </div>
      )}

      {((isPublic || activeTab === TABS.PREVIEW)) && (
      <div className="memory-book-container">
        {isPublic && publicBasePath && (
          <header className="memory-book-public-header">
            <div className="memory-book-public-header-inner">
              <div className="memory-book-public-header-brand">
                <div className="memory-book-logo-text" aria-label="Fresh Meet">
                  Fresh <span>Meet</span>
                </div>
                {pageShareUrl ? (
                  <div className="memory-book-public-brand-share">
                    <MemoryBookPageShareIcons url={pageShareUrl} title={pageShareTitle} text={pageShareText} />
                  </div>
                ) : null}
              </div>
              <div className="memory-book-public-header-toolbar">
                <nav className="memory-book-public-nav" aria-label="Memory book pages">
                  <NavLink
                    to={publicBasePath}
                    end
                    className={({ isActive }) => `memory-book-navlink ${isActive ? 'active' : ''}`}
                  >
                    Main
                  </NavLink>
                  <NavLink
                    to={`${publicBasePath}/leaderboard`}
                    className={({ isActive }) => `memory-book-navlink ${isActive ? 'active' : ''}`}
                  >
                    Scores
                  </NavLink>
                  <NavLink
                    to={`${publicBasePath}/quotes`}
                    className={({ isActive }) => `memory-book-navlink ${isActive ? 'active' : ''}`}
                  >
                    Quotes
                  </NavLink>
                  <NavLink
                    to={`${publicBasePath}/gallery`}
                    className={({ isActive }) => `memory-book-navlink ${isActive ? 'active' : ''}`}
                  >
                    Gallery
                  </NavLink>
                  <NavLink
                    to={`${publicBasePath}/videos`}
                    className={({ isActive }) => `memory-book-navlink ${isActive ? 'active' : ''}`}
                  >
                    Videos
                  </NavLink>
                </nav>
              </div>
            </div>
          </header>
        )}

        {(isPublic ? resolvedPublicPage === PUBLIC_PAGES.MAIN : true) && (
          <section className="memory-book-section memory-book-hero">
            <MemoryBookHeroCarousel
              eventTitle={event.title || 'Event'}
              editionYear={publicYear}
              dateRangeWithYear={dateRangeWithYear}
              location={event.location}
              heroImageUrl={heroImageUrl}
              podiumDivisionEntries={sortedPodiumDivisionEntries}
              teamPhotosMap={teamPhotosMap}
              teamFocalMap={teamFocalMap}
              statsSlides={numbersData?.teaserSlides || []}
              testimonials={testimonialsList}
              galleryItems={galleryItems}
              basePath={isPublic ? publicBasePath : ''}
              memoryMeta={memoryMeta}
              shareUrl={pageShareUrl}
              shareTitle={pageShareTitle}
              shareText={pageShareText}
              shareIconsInHeader={isPublic && !!publicBasePath}
              onTeamSelect={openTeamDetailById}
            />
            {isPublic && (
              <div className="memory-book-interest-cta">
                <InterestListModal
                  fromEditionYear={getEventEditionYear(event) || new Date().getFullYear()}
                  source="archive"
                  triggerText={`Get updates for ${(getEventEditionYear(event) || new Date().getFullYear()) + 1}`}
                />
              </div>
            )}
            {intro && !isPublic && <p className="memory-book-hero-intro memory-book-hero-intro--below">{intro}</p>}
          </section>
        )}

        {(isPublic ? resolvedPublicPage === PUBLIC_PAGES.MAIN : true) && (
        <section className="memory-book-section memory-book-infographic" id="numbers">
          <h2 className="memory-book-section-title">By the numbers</h2>
          <NumbersTeaserCarousel
            slides={numbersData.teaserSlides}
            expanded={numbersExpanded}
            onToggleExpanded={() => {
              setNumbersExpanded((exp) => {
                const next = !exp;
                if (next) setStatsAnimKey((k) => k + 1);
                return next;
              });
            }}
          />
          {numbersExpanded && (
            <>
              <div className="memory-book-stats memory-book-stats--expanded">
                <div
                  className="memory-book-stat memory-book-stat--hero"
                  style={{ animationDelay: '0s' }}
                >
                  <AnimatedNumber
                    key={`hero-${statsAnimKey}`}
                    value={numbersData.hero.value}
                    className="memory-book-stat-value--hero"
                  />
                  <div className="memory-book-stat-label memory-book-stat-label--hero">{numbersData.hero.label}</div>
                </div>
                {numbersData.chips.map((c, i) => {
                  const isGeo = c.id === 'states' || c.id === 'countries';
                  const Tag = isGeo ? 'button' : 'div';
                  const geoLabel = c.id === 'states' ? 'States' : 'Countries';
                  const aria = isGeo ? `Show ${geoLabel.toLowerCase()} list` : undefined;
                  const onClick = !isGeo
                    ? undefined
                    : () => {
                        if (c.id === 'states') {
                          setGeoListModal({
                            id: 'states',
                            title: 'States',
                            items: numbersData.stateList || [],
                          });
                        } else {
                          setGeoListModal({
                            id: 'countries',
                            title: 'Countries',
                            items: numbersData.countryList || [],
                          });
                        }
                      };
                  return (
                    <Tag
                      key={c.id}
                      type={isGeo ? 'button' : undefined}
                      className={`memory-book-stat memory-book-stat--chip${isGeo ? ' memory-book-stat--chipButton' : ''} memory-book-stat--tone-${c.tone}`}
                      style={{ animationDelay: `${0.06 + i * 0.05}s` }}
                      onClick={onClick}
                      aria-label={aria}
                    >
                      <AnimatedNumber key={`${c.id}-${statsAnimKey}`} value={c.value} className={`memory-book-stat-value--tone-${c.tone}`} />
                      <div className="memory-book-stat-label">{c.label}</div>
                    </Tag>
                  );
                })}
                {numbersData.ageRange && (
                  <div className="memory-book-stat memory-book-stat--age" style={{ animationDelay: '0.22s' }}>
                    <div className="memory-book-stat-age-range">{numbersData.ageRange.text}</div>
                    <div className="memory-book-stat-label">{numbersData.ageRange.label}</div>
                  </div>
                )}
                {numbersData.hasAnyActivity && (
                  <div className="memory-book-stat memory-book-stat--activities" style={{ animationDelay: '0.28s' }}>
                    <div className="memory-book-activities-grid">
                      <div className="memory-book-activity-cell">
                        <AnimatedNumber
                          key={`act-ha-${statsAnimKey}`}
                          value={numbersData.activities.highAltitude}
                          className="memory-book-stat-value--activity"
                        />
                        <span className="memory-book-activity-sublabel">High altitude jumps</span>
                      </div>
                      <div className="memory-book-activity-cell">
                        <AnimatedNumber
                          key={`act-jet-${statsAnimKey}`}
                          value={numbersData.activities.jet}
                          className="memory-book-stat-value--activity"
                        />
                        <span className="memory-book-activity-sublabel">Jet jumps</span>
                      </div>
                      <div className="memory-book-activity-cell">
                        <AnimatedNumber
                          key={`act-canopy-${statsAnimKey}`}
                          value={numbersData.activities.canopy}
                          className="memory-book-stat-value--activity"
                        />
                        <span className="memory-book-activity-sublabel">Canopy courses</span>
                      </div>
                      <div className="memory-book-activity-cell">
                        <AnimatedNumber
                          key={`act-water-${statsAnimKey}`}
                          value={numbersData.activities.water}
                          className="memory-book-stat-value--activity"
                        />
                        <span className="memory-book-activity-sublabel">Water training</span>
                      </div>
                    </div>
                    <div className="memory-book-stat-label memory-book-stat-label--activities-foot">Jumps & training</div>
                  </div>
                )}
              </div>
              {Object.keys(stateCounts).length > 0 && (
                <div className="memory-book-map-wrap">
                  <h3 className="memory-book-map-title">Where everyone came from</h3>
                  <UsHeatMap stateCounts={stateCounts} />
                </div>
              )}
            </>
          )}
        </section>
        )}

        {geoListModal && (
          <div className="memory-book-modal-overlay" onClick={() => setGeoListModal(null)} role="dialog" aria-modal="true">
            <div className="memory-book-modal memory-book-modal--geo" onClick={(e) => e.stopPropagation()}>
              <div className="memory-book-modal-header">
                <h3 className="memory-book-modal-title">{geoListModal.title}</h3>
                <button type="button" className="memory-book-modal-close" onClick={() => setGeoListModal(null)} aria-label="Close">
                  ✕
                </button>
              </div>
              <div className="memory-book-modal-body">
                <div className="memory-book-geo-list" role="list">
                  {(geoListModal.items || []).map((it) => (
                    <div key={it.code} className="memory-book-geo-list-item" role="listitem">
                      <div className="memory-book-geo-left">
                        <span className="memory-book-geo-code">{it.code}</span>
                        <span className="memory-book-geo-name">{it.name || it.code}</span>
                      </div>
                      <span className="memory-book-geo-count">{Number.isFinite(it.count) ? it.count : 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {(isPublic ? resolvedPublicPage === PUBLIC_PAGES.MAIN : true) && testimonialsList.length > 0 && (
          <section className="memory-book-section memory-book-testimonials-section" id="testimonials">
            <h2 className="memory-book-section-title">Voices</h2>
            <TestimonialsTeaserCarousel
              items={testimonialsList}
              expanded={testimonialsExpanded}
              onToggleExpanded={() => setTestimonialsExpanded((v) => !v)}
            />
            {isPublic && publicBasePath && (
              <div style={{ marginTop: '0.65rem' }}>
                <NavLink to={`${publicBasePath}/quotes`} className="memory-book-linkish">
                  Open quotes page →
                </NavLink>
              </div>
            )}
          </section>
        )}

        {(isPublic ? resolvedPublicPage === PUBLIC_PAGES.MAIN : true) && quiplashQuotesList.length > 0 && (
          <section className="memory-book-section memory-book-quiplash-section" id="mic-drop">
            <h2 className="memory-book-section-title">Mic Drop</h2>
            <QuiplashTeaserCarousel
              quotes={quiplashQuotesList}
              teamsList={nonTestTeams}
              expanded={quiplashExpanded}
              onToggleExpanded={() => setQuiplashExpanded((v) => !v)}
            />
            {isPublic && publicBasePath && (
              <div style={{ marginTop: '0.65rem' }}>
                <NavLink to={`${publicBasePath}/quotes`} className="memory-book-linkish">
                  Open Mic Drop page →
                </NavLink>
              </div>
            )}
          </section>
        )}

        {(isPublic ? resolvedPublicPage === PUBLIC_PAGES.MAIN : true) && memoryStreamWithWinners.length > 0 && (
          <section
            className="memory-book-section memory-book-stream-root"
            aria-label="Photos, quotes, and teams"
          >
            {(isPublic && resolvedPublicPage === PUBLIC_PAGES.MAIN
              ? memoryStreamWithWinners.filter((b) => b.type !== 'testimonial' && b.type !== 'quiplashSection')
              : memoryStreamWithWinners
            ).map((block) => {
              if (block.type === 'winners') {
                return (
                  <div
                    key={block.key}
                    className="memory-book-stream memory-book-stream--winners-wrap memory-book-winners-section"
                  >
                    <h2 className="memory-book-section-title memory-book-stream-inline-title">
                      Winners
                      {!isPublic && (
                        <button
                          type="button"
                          className="memory-book-leaderboard-link"
                          onClick={() => setShowLeaderboardModal(true)}
                        >
                          View full leaderboard
                        </button>
                      )}
                    </h2>
                    <WinnersTeaserCarousel
                      slides={winnerTeaserSlides}
                      teamPhotosMap={teamPhotosMap}
                      expanded={winnersExpanded}
                      onToggleExpanded={() => {
                        setWinnersExpanded((v) => {
                          const next = !v;
                          if (next) setWinnersCarouselKey((k) => k + 1);
                          return next;
                        });
                      }}
                    />
                    {isPublic && publicBasePath && (
                      <div style={{ marginTop: '0.65rem' }}>
                        <NavLink to={`${publicBasePath}/leaderboard`} className="memory-book-linkish">
                          Open full leaderboard →
                        </NavLink>
                      </div>
                    )}
                    {winnersExpanded && (
                      <div className="memory-book-podium-sections" key={winnersCarouselKey}>
                        {winnersPodiumDivisionEntries.map(([division, divTeams]) => (
                          <div key={division} className="memory-book-podium-division-row">
                            <h3 className="memory-book-podium-division-row-title">
                              <span className="memory-book-podium-division-row-title-main">
                                <span className="memory-book-podium-division-row-dot" aria-hidden />
                                {divisionDisplayLabel(division)}
                              </span>
                              <span className="memory-book-podium-division-row-sub">
                                {divTeams.length} {divTeams.length === 1 ? 'team' : 'teams'}
                              </span>
                            </h3>
                            <div className="memory-book-podium memory-book-podium--expanded">
                              {divTeams.map((t, placeIdx) => (
                            <button
                              key={t.id}
                              type="button"
                              className={`memory-book-podium-card memory-book-podium-card--clickable ${
                                placeIdx === 0 ? 'memory-book-podium-card--first' : (placeIdx < 3 ? 'memory-book-podium-card--podium' : '')
                              }`.trim()}
                              onClick={() => openTeamDetailById(t.id)}
                            >
                              <div className="memory-book-podium-photo-wrap">
                                {placeIdx < 3 ? (
                                  <div className="memory-book-podium-place-badge">{podiumPlaceLabel(placeIdx + 1)}</div>
                                ) : null}
                              {teamPhotosMap[t.id] ? (
                                <img
                                  src={teamPhotosMap[t.id]}
                                  alt=""
                                  className="memory-book-podium-photo"
                                  style={{ objectPosition: teamPhotoObjectPosition(t.id, memoryMeta, teamFocalMap) }}
                                />
                                ) : (
                                  <div className="memory-book-podium-photo memory-book-podium-photo--placeholder" aria-hidden />
                                )}
                              </div>
                              <div className="memory-book-podium-rank">
                                #{t.rank}
                                {division !== 'Unassigned' ? ` ${division}` : ''}
                              </div>
                              <div className="memory-book-podium-team">{getTeamLabelCore(t)}</div>
                              <div className="memory-book-podium-score">
                                <div className="memory-book-podium-scorelabel">PTS</div>
                                <div className="memory-book-podium-scorevalue">{t.total}</div>
                              </div>
                              <div className="memory-book-podium-division">Team {t.teamNumber}</div>
                            </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (block.type === 'gallery') {
                return (
                  <div key={block.key} className="memory-book-stream memory-book-stream--gallery">
                    <p className="memory-book-stream-eyebrow">Photos</p>
                    <div className="memory-book-stream-gallery-row">
                      {block.photos.map((photo, pi) => (
                        <figure key={`${photo.url}-${pi}`} className="memory-book-stream-gallery-figure">
                          <img src={photo.url} alt="" className="memory-book-stream-gallery-img" />
                          {photo.credit ? (
                            <figcaption className="memory-book-stream-gallery-credit">{photo.credit}</figcaption>
                          ) : null}
                        </figure>
                      ))}
                    </div>
                    {isPublic && publicBasePath && (
                      <div style={{ marginTop: '0.65rem' }}>
                        <NavLink to={`${publicBasePath}/gallery`} className="memory-book-linkish">
                          View full gallery →
                        </NavLink>
                      </div>
                    )}
                  </div>
                );
              }
              if (block.type === 'testimonial') {
                const t = block.item;
                return (
                  <div key={block.key} className="memory-book-stream memory-book-stream--quote">
                    <p className="memory-book-stream-eyebrow">Voices</p>
                    <div className="memory-book-quote memory-book-quote--stream">
                      <p className="memory-book-quote-text">"{t.quote || t.text}"</p>
                      <p className="memory-book-quote-byline">
                        — {t.name || 'Anonymous'}
                        {t.role ? `, ${t.role}` : ''}
                      </p>
                    </div>
                    {isPublic && publicBasePath && (
                      <div style={{ marginTop: '0.65rem' }}>
                        <NavLink to={`${publicBasePath}/quotes`} className="memory-book-linkish">
                          Read more quotes →
                        </NavLink>
                      </div>
                    )}
                  </div>
                );
              }
              if (block.type === 'quiplashSection') {
                return (
                  <div
                    key={block.key}
                    className="memory-book-stream memory-book-stream--quiplash-wrap memory-book-quiplash-section"
                  >
                    <h2 className="memory-book-section-title memory-book-stream-inline-title">Mic Drop</h2>
                    <QuiplashTeaserCarousel
                      quotes={quiplashQuotesList}
                      teamsList={nonTestTeams}
                      expanded={quiplashExpanded}
                      onToggleExpanded={() => setQuiplashExpanded((v) => !v)}
                    />
                    {isPublic && publicBasePath && (
                      <div style={{ marginTop: '0.65rem' }}>
                        <NavLink to={`${publicBasePath}/quotes`} className="memory-book-linkish">
                          See all Mic Drop quotes →
                        </NavLink>
                      </div>
                    )}
                  </div>
                );
              }
              if (block.type === 'highlight') {
                const h = block.item;
                return (
                  <div key={block.key} className="memory-book-stream memory-book-stream--highlight">
                    <p className="memory-book-stream-eyebrow">Highlight</p>
                    <div className="memory-book-card memory-book-card--stream">
                      {typeof h === 'string' ? h : (h.text || h.content || JSON.stringify(h))}
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </section>
        )}

        {isPublic && resolvedPublicPage === PUBLIC_PAGES.LEADERBOARD && sortedPodiumDivisionEntries.length > 0 && (
          <section className="memory-book-section memory-book-winners-section">
            <h2 className="memory-book-section-title">Winners</h2>
            <div className="memory-book-podium-sections">
              {winnersPodiumDivisionEntries.map(([division, divTeams]) => (
                <div key={division} className="memory-book-podium-division-row">
                  <h3 className="memory-book-podium-division-row-title">
                    <span className="memory-book-podium-division-row-title-main">
                      <span className="memory-book-podium-division-row-dot" aria-hidden />
                      {divisionDisplayLabel(division)}
                    </span>
                    <span className="memory-book-podium-division-row-sub">
                      {divTeams.length} {divTeams.length === 1 ? 'team' : 'teams'}
                    </span>
                  </h3>
                  <div className="memory-book-podium memory-book-podium--expanded">
                    {divTeams.map((t, placeIdx) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`memory-book-podium-card memory-book-podium-card--clickable ${
                      placeIdx === 0 ? 'memory-book-podium-card--first' : (placeIdx < 3 ? 'memory-book-podium-card--podium' : '')
                    }`.trim()}
                    onClick={() => openTeamDetailById(t.id)}
                  >
                    <div className="memory-book-podium-photo-wrap">
                      {placeIdx < 3 ? (
                        <div className="memory-book-podium-place-badge">{podiumPlaceLabel(placeIdx + 1)}</div>
                      ) : null}
                      {teamPhotosMap[t.id] ? (
                        <img
                          src={teamPhotosMap[t.id]}
                          alt=""
                          className="memory-book-podium-photo"
                          style={{ objectPosition: teamPhotoObjectPosition(t.id, memoryMeta, teamFocalMap) }}
                        />
                      ) : (
                        <div className="memory-book-podium-photo memory-book-podium-photo--placeholder" aria-hidden />
                      )}
                    </div>
                    <div className="memory-book-podium-rank">
                      #{t.rank}
                      {division !== 'Unassigned' ? ` ${division}` : ''}
                    </div>
                    <div className="memory-book-podium-team">{getTeamLabelCore(t)}</div>
                    <div className="memory-book-podium-score">
                      <div className="memory-book-podium-scorelabel">PTS</div>
                      <div className="memory-book-podium-scorevalue">{t.total}</div>
                    </div>
                    <div className="memory-book-podium-division">Team {t.teamNumber}</div>
                  </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {(isPublic ? resolvedPublicPage === PUBLIC_PAGES.LEADERBOARD : true) && rankedTeams.length > 0 && (
          <section className="memory-book-section memory-book-standings-section">
            <h2 className="memory-book-section-title">
              Final standings
              {!isPublic && (
                <button type="button" className="memory-book-leaderboard-link" onClick={() => setShowLeaderboardModal(true)}>
                  View full leaderboard
                </button>
              )}
            </h2>
            <p className="memory-book-standings-hint">Select a team for scores and round breakdown.</p>
            {standingsByDivision.map(({ division, teams }) => (
              <div key={division} className="memory-book-standings-division-block">
                <h3 className="memory-book-standings-division-title">
                  {division === 'Unassigned' ? 'Unassigned' : division}
                </h3>
                <div className="memory-book-standings-grid">
                  {teams.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="memory-book-standings-card"
                      onClick={() => syncTeamDetailInUrl(t)}
                    >
                      <div className="memory-book-standings-card-photo">
                        {teamPhotosMap[t.id] ? (
                          <img src={teamPhotosMap[t.id]} alt="" style={{ objectPosition: teamPhotoObjectPosition(t.id, memoryMeta, teamFocalMap) }} />
                        ) : (
                          <span className="memory-book-standings-card-placeholder">No photo</span>
                        )}
                      </div>
                      <div className="memory-book-standings-card-body">
                        <span className="memory-book-standings-card-name">{getTeamLabelCore(t)}</span>
                        <span className="memory-book-standings-card-meta">
                          #{t.rank} · {t.total} pts
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {(isPublic ? resolvedPublicPage !== PUBLIC_PAGES.GALLERY : true) && sponsors.length > 0 && (
          <SponsorSectionCard sponsors={sponsors} />
        )}

        {isPublic && resolvedPublicPage === PUBLIC_PAGES.QUOTES && testimonialsList.length > 0 && (
          <section className="memory-book-section memory-book-section--quotes">
            <QuotesEngagementHub
              testimonials={testimonialsList}
              micDropQuotes={quiplashQuotesList}
              teamsList={nonTestTeams}
            />
          </section>
        )}

        {isPublic && resolvedPublicPage === PUBLIC_PAGES.QUOTES && testimonialsList.length === 0 && quiplashQuotesList.length > 0 && (
          <section className="memory-book-section memory-book-section--quotes">
            <QuotesEngagementHub
              testimonials={[]}
              micDropQuotes={quiplashQuotesList}
              teamsList={nonTestTeams}
            />
          </section>
        )}

        {isPublic && resolvedPublicPage === PUBLIC_PAGES.GALLERY && (
          <section className="memory-book-section">
            <h2 className="memory-book-section-title">Gallery</h2>
            <p className="memory-book-gallery-notice">
              Photos are still being uploaded—check back over the next few weeks to see more great shots!
            </p>
            {galleryItems.length > 0 ? (
              <div className="memory-book-gallery-public-filter">
                <label className="memory-book-gallery-public-filter-label" htmlFor="memory-book-gallery-credit-filter">
                  Photo credit
                </label>
                <select
                  id="memory-book-gallery-credit-filter"
                  className="memory-book-select"
                  value={publicGalleryCreditQuery}
                  onChange={(e) => setPublicGalleryCreditFilter(e.target.value)}
                  aria-label="Filter gallery by photo credit"
                >
                  <option value="">All photographers</option>
                  {galleryItems.some((g) => !(g.credit || '').trim()) ? (
                    <option value={GALLERY_CREDIT_FILTER_NONE}>No credit listed</option>
                  ) : null}
                  {publicGalleryCreditOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {featuredGalleryItems.length > 0 ? (
              <FeaturedGalleryCarousel
                items={featuredGalleryItems}
                onOpenPhoto={openGalleryModalByFullPath}
              />
            ) : null}
            {galleryItems.length === 0 ? (
              <div className="memory-book-gallery-placeholder">No photos yet.</div>
            ) : publicGalleryBaseItems.length === 0 ? (
              <div className="memory-book-gallery-placeholder">No photos match this credit filter.</div>
            ) : (
              <div className="memory-book-gallery-wrap">
                <div className="memory-book-gallery-grid">
                  {publicGalleryGridItems.map((p, i) => (
                    <figure key={`${p.fullPath || p.url}-${i}`} style={{ margin: 0 }}>
                      <button
                        type="button"
                        className="memory-book-gallery-photo-btn"
                        onClick={() => openGalleryModalByFullPath(p.fullPath)}
                        aria-label="Open photo"
                      >
                        <img
                          src={p.thumbUrl || p.url}
                          alt=""
                          style={{ objectPosition: galleryPhotoObjectPosition(p.fullPath, memoryMeta) }}
                        />
                      </button>
                      {p.credit ? (
                        <figcaption className="memory-book-stream-gallery-credit">{p.credit}</figcaption>
                      ) : null}
                    </figure>
                  ))}
                </div>
                {publicGalleryBaseItems.length > publicGalleryGridItems.length && (
                  <div className="memory-book-gallery-load-more">
                    <button
                      type="button"
                      className="memory-book-btn memory-book-btn--ghost"
                      onClick={() => setGalleryPage((p) => p + 1)}
                    >
                      Load more photos
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
        {isPublic && resolvedPublicPage === PUBLIC_PAGES.VIDEOS && (
          <section className="memory-book-section memory-book-section--videos">
            <h2 className="memory-book-section-title">Videos</h2>
            {!hasVideoLinks ? (
              <div className="memory-book-tab-empty">No videos added yet.</div>
            ) : (
              <div className="memory-book-videos-grid">
                {videoCategories.map((cat) => (
                  <article key={cat.key} className="memory-book-video-card">
                    <h3 className="memory-book-video-card-title">{cat.title}</h3>
                    {cat.links.length === 0 ? (
                      <p className="memory-book-video-empty">No links added.</p>
                    ) : (
                      <ul className="memory-book-video-links">
                        {cat.links.map((url, idx) => (
                          <li key={`${cat.key}-${idx}`}>
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              {cat.links.length > 1 ? `Video ${idx + 1}` : 'Watch video'}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
      )}

      {!isPublic && showLeaderboardModal && rankedTeams.length > 0 && (
        <div className="memory-book-modal-overlay" onClick={() => setShowLeaderboardModal(false)} role="dialog" aria-modal="true">
          <div className="memory-book-modal memory-book-modal--leaderboard" onClick={(e) => e.stopPropagation()}>
            <div className="memory-book-modal-header">
              <h3>Full leaderboard</h3>
              <button type="button" className="memory-book-modal-close" onClick={() => setShowLeaderboardModal(false)} aria-label="Close">✕</button>
            </div>
            <div className="memory-book-modal-body table-container">
              <table className="leaderboard-table memory-book-leaderboard-table">
                <thead>
                  <tr>
                    <th className="narrow">Rank</th>
                    <th className="sticky-col-name">Team name</th>
                    <th className="narrow">Team #</th>
                    <th className="narrow">Total</th>
                    <th className="narrow">Div.</th>
                  </tr>
                </thead>
                <tbody>
                  {rankedTeams.map((t) => (
                    <tr key={t.id}>
                      <td className="narrow">{t.rank}</td>
                      <td className="sticky-col-name">{getTeamLabelCore(t)}</td>
                      <td className="narrow">{t.teamNumber}</td>
                      <td className="narrow">{t.total}</td>
                      <td className="narrow">{(t.division || '—').trim() || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {standingsDetailTeam && (
        <TeamStandingsDetailModal
          key={standingsDetailTeam.id}
          team={standingsDetailTeam}
          teamPhotosMap={teamPhotosMap}
          teamPosterUrl={standingsDetailMetaPoster || teamDetailStoragePosterUrl || ''}
          bonusPuzzles={bonusPuzzles}
          onClose={() => syncTeamDetailInUrl(null)}
          shareUrl={
            event?.memoryBookPublished === true
              ? buildPublicMemoryBookUrl({
                  year: getEventEditionYear(event),
                  page: PUBLIC_PAGES.MAIN,
                  teamId: standingsDetailTeam.id,
                })
              : buildPublicLeaderboardUrl({
                  year: getEventEditionYear(event),
                  eventId: selectedEventId,
                })
          }
          shareTitle={`${getTeamLabelCore(standingsDetailTeam)} · ${event.title || 'Memory Book'}`}
          shareText={`Rank #${standingsDetailTeam.rank} · ${standingsDetailTeam.total} pts — ${event.title || 'memory book'}`}
        />
      )}

      <GalleryPhotoModal
        open={galleryModalIndex != null}
        items={galleryModalList}
        index={galleryModalIndex ?? 0}
        onClose={closeGalleryModal}
        canPrev={galleryModalIndex != null && galleryModalIndex > 0}
        canNext={galleryModalIndex != null && galleryModalIndex < galleryModalList.length - 1}
        onPrev={() => {
          if (galleryModalIndex == null) return;
          const next = Math.max(0, galleryModalIndex - 1);
          setGalleryModalIndex(next);
          if (isPublic) {
            const fp = galleryModalList[next]?.fullPath;
            if (fp) {
              setSearchParams(
                (prev) => {
                  const p = new URLSearchParams(prev);
                  p.set('eventId', selectedEventId || '');
                  p.set('photo', fp);
                  return p;
                },
                { replace: true }
              );
            }
          }
        }}
        onNext={() => {
          if (galleryModalIndex == null) return;
          const next = Math.min(galleryModalList.length - 1, galleryModalIndex + 1);
          setGalleryModalIndex(next);
          if (isPublic) {
            const fp = galleryModalList[next]?.fullPath;
            if (fp) {
              setSearchParams(
                (prev) => {
                  const p = new URLSearchParams(prev);
                  p.set('eventId', selectedEventId || '');
                  p.set('photo', fp);
                  return p;
                },
                { replace: true }
              );
            }
          }
        }}
        shareUrl={galleryModalShareUrl}
        shareTitle={event?.title ? `${event.title} — Photo` : 'Fresh Meet photo'}
      />

      <FocalPointEditorModal
        open={!isPublic && focalEditorOpen}
        title={focalEditorQueue[focalEditorIndex]?.label ? `Set focal point — ${focalEditorQueue[focalEditorIndex].label}` : 'Set focal point'}
        imageUrl={focalEditorQueue[focalEditorIndex]?.imageUrl || ''}
        initialFocalPoint={focalEditorPoint}
        onChange={(p) => setFocalEditorPoint(p)}
        onClose={() => setFocalEditorOpen(false)}
        onSave={saveFocalPoint}
        onPrev={() => {
          const next = Math.max(0, focalEditorIndex - 1);
          setFocalEditorIndex(next);
          setFocalEditorPoint(focalEditorQueue[next]?.focalPoint || focalEditorPoint);
        }}
        onNext={() => {
          const next = Math.min(focalEditorQueue.length - 1, focalEditorIndex + 1);
          setFocalEditorIndex(next);
          setFocalEditorPoint(focalEditorQueue[next]?.focalPoint || focalEditorPoint);
        }}
        canPrev={focalEditorIndex > 0}
        canNext={focalEditorIndex < focalEditorQueue.length - 1}
        saving={focalEditorSaving}
        footerHint={
          focalEditorQueue.length
            ? `Photo ${focalEditorIndex + 1} of ${focalEditorQueue.length}`
            : ''
        }
      />
    </div>
  );
}

async function waitForSubtreeImages(root, timeoutMs = 6000) {
  if (!root) return;
  const start = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const imgs = Array.from(root.querySelectorAll('img'));
    const pending = imgs.filter((img) => !img.complete);
    if (!pending.length) return;
    if (Date.now() - start > timeoutMs) return;
    await new Promise((r) => window.setTimeout(r, 80));
  }
}

function GalleryPhotoModal({
  open,
  items,
  index,
  onClose,
  onPrev,
  onNext,
  canPrev,
  canNext,
  shareUrl,
  shareTitle,
}) {
  const cardRef = useRef(null);
  const [hint, setHint] = useState('');
  const [generating, setGenerating] = useState(false);
  const [shareBlob, setShareBlob] = useState(null);
  const [sharePrepBusy, setSharePrepBusy] = useState(false);
  const [sharePrepFailed, setSharePrepFailed] = useState(false);
  const fmLogoUrl = `${process.env.PUBLIC_URL || ''}/${encodeURIComponent('FM logo ORANGE WH BG_small_transparent.png')}`;

  const item = Array.isArray(items) && Number.isFinite(index) ? items[index] : null;
  const fullPath = item?.fullPath || '';
  const url = item?.url || '';
  const credit = (item?.credit || '').trim();
  const galleryShareImgCrossOrigin =
    url.startsWith('http://') || url.startsWith('https://') ? 'anonymous' : undefined;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft') onPrev?.();
      else if (e.key === 'ArrowRight') onNext?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, onPrev, onNext]);

  // Pre-render PNG while the modal is open so Share does not await html-to-image before
  // navigator.share (iOS Safari requires share() in the user-gesture chain).
  useEffect(() => {
    if (!open || !url) {
      setShareBlob(null);
      setSharePrepFailed(false);
      setSharePrepBusy(false);
      return undefined;
    }
    let cancelled = false;
    setShareBlob(null);
    setSharePrepFailed(false);
    setSharePrepBusy(true);
    (async () => {
      try {
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        const node = cardRef.current?.querySelector?.('[data-photo-share]');
        if (!node) throw new Error('Photo share node missing');
        await waitForSubtreeImages(node, 6000);
        const blob = await renderNodeToPngBlob(node, { width: 1080, height: 1080 });
        if (!cancelled) {
          setShareBlob(blob);
          setSharePrepFailed(false);
        }
      } catch {
        if (!cancelled) {
          setShareBlob(null);
          setSharePrepFailed(true);
        }
      } finally {
        if (!cancelled) setSharePrepBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, url, fullPath, index, credit, fmLogoUrl]);

  const buildFilename = () =>
    `fresh-meet-photo-${(fullPath.split('/').pop() || 'gallery').replace(/[^\w.-]+/g, '-')}.png`;

  const renderShareBlob = async () => {
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    const node = cardRef.current?.querySelector?.('[data-photo-share]');
    if (!node) throw new Error('Photo share node missing');
    await waitForSubtreeImages(node, 6000);
    return await renderNodeToPngBlob(node, { width: 1080, height: 1080 });
  };

  const shareImageDisabled =
    sharePrepBusy || (!shareBlob && !sharePrepFailed);

  const runShare = async () => {
    if (!url || generating || shareImageDisabled) return;
    setHint('');
    if (sharePrepFailed) {
      if (shareUrl && typeof navigator.share === 'function') {
        try {
          await navigator.share({
            title: shareTitle || 'Fresh Meet photo',
            text: shareUrl ? `Gallery: ${shareUrl}` : '',
            url: shareUrl,
          });
          setHint('Shared');
          window.setTimeout(() => setHint(''), 2500);
        } catch (e) {
          if (e && e.name === 'AbortError') return;
          window.alert('Could not share from this device. Try the link button or Download.');
        }
      } else {
        window.alert('Could not prepare the share image. Try Download or the link button.');
      }
      return;
    }
    setGenerating(true);
    try {
      const r = await shareOrDownloadPng({
        blob: shareBlob,
        filename: buildFilename(),
        title: shareTitle || 'Fresh Meet photo',
        text: shareUrl ? `Gallery: ${shareUrl}` : '',
      });
      if (r === 'shared') setHint('Shared');
      else if (r === 'downloaded') setHint('Downloaded');
      window.setTimeout(() => setHint(''), 2500);
    } catch {
      window.alert('Could not generate the share image on this device. Try Download instead.');
    } finally {
      setGenerating(false);
    }
  };

  const runDownload = async () => {
    if (!url || generating) return;
    setHint('');
    if (shareBlob) {
      try {
        downloadBlob(shareBlob, buildFilename());
        setHint('Downloaded');
        window.setTimeout(() => setHint(''), 2500);
      } catch {
        window.alert('Could not download the image on this device.');
      }
      return;
    }
    setGenerating(true);
    try {
      const blob = await renderShareBlob();
      downloadBlob(blob, buildFilename());
      setHint('Downloaded');
      window.setTimeout(() => setHint(''), 2500);
    } catch {
      window.alert('Could not generate the download image on this device.');
    } finally {
      setGenerating(false);
    }
  };

  if (!open || !item) return null;

  return (
    <div className="memory-book-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Photo viewer">
      <div className="memory-book-modal memory-book-modal--photo" onClick={(e) => e.stopPropagation()}>
        <div className="memory-book-modal-header memory-book-modal-header--photo">
          <h3 className="memory-book-modal-title">Photo</h3>
          <div className="memory-book-modal-header-actions">
            {shareUrl ? (
              <button
                type="button"
                className="memory-book-share-btn memory-book-share-btn--header"
                onClick={runShare}
                disabled={generating || shareImageDisabled}
                aria-label="Share image"
                title="Share image"
              >
                {generating ? 'Sharing…' : sharePrepBusy ? 'Preparing…' : <ShareIcon />}
              </button>
            ) : null}
            <button
              type="button"
              className="memory-book-share-btn memory-book-share-btn--header memory-book-share-btn--icon"
              onClick={runDownload}
              disabled={generating}
              aria-label="Download image"
              title="Download image"
            >
              ⤓
            </button>
            {shareUrl ? (
              <MemoryBookShareButton url={shareUrl} title="Copy link" text="">
                <LinkIcon />
              </MemoryBookShareButton>
            ) : null}
            <button type="button" className="memory-book-modal-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="memory-book-photo-modal-body" ref={cardRef}>
          <div className="memory-book-photo-stage">
            <img src={url} alt="" className="memory-book-photo-stage-img" />
            <img
              src={fmLogoUrl}
              alt=""
              className="memory-book-photo-stage-logo"
              aria-hidden
            />
            {credit ? <div className="memory-book-photo-stage-credit">{credit}</div> : null}

            <button
              type="button"
              className="memory-book-photo-stage-nav memory-book-photo-stage-nav--prev"
              onClick={onPrev}
              disabled={!canPrev}
              aria-label="Previous photo"
              title="Previous photo"
            >
              ‹
            </button>
            <button
              type="button"
              className="memory-book-photo-stage-nav memory-book-photo-stage-nav--next"
              onClick={onNext}
              disabled={!canNext}
              aria-label="Next photo"
              title="Next photo"
            >
              ›
            </button>
          </div>

          <div className="memory-book-photo-share-card" data-photo-share aria-hidden>
            <img
              src={url}
              alt=""
              className="memory-book-photo-share-img"
              crossOrigin={galleryShareImgCrossOrigin}
            />
            <img
              src={fmLogoUrl}
              alt=""
              className="memory-book-photo-share-logo"
              aria-hidden
            />
            {credit ? <div className="memory-book-photo-share-credit">{credit}</div> : null}
          </div>
        </div>

        <div className="memory-book-modal-footer memory-book-modal-footer--photo">
          {hint ? (
            <span className="memory-book-share-hint" role="status">
              {hint}
            </span>
          ) : (
            <span />
          )}
          <span className="memory-book-photo-index">
            {Array.isArray(items) ? `${index + 1} / ${items.length}` : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

function FeaturedGalleryCarousel({ items = [], onOpenPhoto }) {
  const [index, setIndex] = useState(0);
  const list = Array.isArray(items) ? items.filter((i) => i?.url || i?.thumbUrl) : [];

  useEffect(() => {
    setIndex(0);
  }, [list.length]);

  useEffect(() => {
    if (list.length < 2) return undefined;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % list.length);
    }, 4200);
    return () => clearInterval(t);
  }, [list.length]);

  if (!list.length) return null;
  const active = list[index] || list[0];

  return (
    <div className="memory-book-featured-carousel" aria-label="Featured photos">
      <button
        type="button"
        className="memory-book-featured-carousel-stage"
        onClick={() => onOpenPhoto?.(active.fullPath)}
        aria-label="Open featured photo"
      >
        <img
          src={active.thumbUrl || active.url}
          alt=""
          className="memory-book-featured-carousel-image"
          loading="lazy"
          decoding="async"
        />
        <div className="memory-book-featured-carousel-scrim" aria-hidden />
        <div className="memory-book-featured-carousel-label">Featured</div>
        {active.credit ? <div className="memory-book-featured-carousel-credit">{active.credit}</div> : null}
      </button>
      {list.length > 1 ? (
        <div className="memory-book-featured-carousel-controls">
          <button
            type="button"
            className="memory-book-featured-carousel-btn"
            onClick={() => setIndex((i) => (i - 1 + list.length) % list.length)}
            aria-label="Previous featured photo"
          >
            ‹
          </button>
          <span className="memory-book-featured-carousel-pos">{index + 1}/{list.length}</span>
          <button
            type="button"
            className="memory-book-featured-carousel-btn"
            onClick={() => setIndex((i) => (i + 1) % list.length)}
            aria-label="Next featured photo"
          >
            ›
          </button>
        </div>
      ) : null}
    </div>
  );
}

function mentorFlyerForTeam(team, flyersList) {
  const email = (team.mentor || '').trim().toLowerCase();
  if (!email) return null;
  return (flyersList || []).find((f) => (f.email || '').trim().toLowerCase() === email) || null;
}

/** Mentor label for team-photo UI: first name + last initial, e.g. "Alex J." */
function mentorDisplayNameWithInitial(team, flyersList) {
  const flyer = mentorFlyerForTeam(team, flyersList);
  if (!flyer) return '';
  const first = getFirstName(flyer).trim();
  const last = (flyer.lastName || '').toString().trim();
  if (first && last) {
    return `${first} ${last.charAt(0).toUpperCase()}.`;
  }
  if (first) return first;
  return (flyer.properName || flyer.name || '').toString().trim() || '';
}

/** Sort mentors by last name, then first; flyers without mentor email last; tie-break team #. */
function compareTeamsByMentorName(a, b, flyersList) {
  const fa = mentorFlyerForTeam(a, flyersList);
  const fb = mentorFlyerForTeam(b, flyersList);
  if (!fa && !fb) return (Number(a.teamNumber) || 0) - (Number(b.teamNumber) || 0);
  if (!fa) return 1;
  if (!fb) return -1;
  const la = (fa.lastName || '').toString().trim().toLowerCase();
  const lb = (fb.lastName || '').toString().trim().toLowerCase();
  const na = getFirstName(fa).trim().toLowerCase();
  const nb = getFirstName(fb).trim().toLowerCase();
  if (la && lb) {
    const c = la.localeCompare(lb, undefined, { sensitivity: 'base' });
    if (c !== 0) return c;
  } else if (la !== lb) {
    return la ? -1 : 1;
  }
  const cFirst = na.localeCompare(nb, undefined, { sensitivity: 'base' });
  if (cFirst !== 0) return cFirst;
  return (Number(a.teamNumber) || 0) - (Number(b.teamNumber) || 0);
}

/** Team photo UI: label shows #n — mentor (with last initial) · team name */
function teamPhotoAssignmentLabel(team, flyersList) {
  const num = team.teamNumber != null && team.teamNumber !== '' ? team.teamNumber : '—';
  const mentor = mentorDisplayNameWithInitial(team, flyersList);
  const mentorPart = mentor || 'Mentor TBD';
  const tn = getTeamLabelCore(team);
  const numDisp = getTeamNumberDisplay(team);
  const synthetic = numDisp !== '?' ? `Team ${numDisp}` : '';
  const mentorLo = (mentor || '').trim().toLowerCase();
  const tnLo = (tn || '').trim().toLowerCase();
  if (tn === synthetic || (mentor && tnLo === mentorLo)) {
    return `#${num} — ${mentorPart}`;
  }
  return `#${num} — ${mentorPart} · ${tn}`;
}

function mergeTeamPhotoEntry(existingList, teamId, url, posterUrl) {
  const arr = Array.isArray(existingList) ? existingList : [];
  const prev = arr.find((e) => e.teamId === teamId) || null;
  const finalPosterUrl = posterUrl !== undefined ? posterUrl : prev?.posterUrl;
  const entry = { teamId, url };
  if (finalPosterUrl) entry.posterUrl = finalPosterUrl;
  return arr.filter((e) => e.teamId !== teamId).concat([entry]);
}

function dataTransferHasFilePayload(dt) {
  if (!dt || !dt.types) return false;
  try {
    return dt.types.contains ? dt.types.contains('Files') : [...dt.types].includes('Files');
  } catch {
    return false;
  }
}

/** Guess team id from filename (e.g. team12.jpg, Team #5, IMG_0042 — last number matched to teamNumber). */
function guessTeamIdFromFilename(fileName, teamsSorted) {
  const base = (fileName || '').replace(/\.[^.]+$/, '');
  const nums = base.match(/\d+/g);
  if (!nums || !teamsSorted.length) return null;
  for (let i = nums.length - 1; i >= 0; i--) {
    const n = parseInt(nums[i], 10);
    if (!Number.isFinite(n)) continue;
    const hit = teamsSorted.find((t) => Number(t.teamNumber) === n);
    if (hit) return hit.id;
  }
  return null;
}

function TeamPhotosTab({
  eventId,
  event,
  nonTestTeams,
  rankedTeams,
  flyers,
  teamPhotosMap,
  teamPostersMap = {},
  teamFocalMap,
  memoryMeta,
  setMemoryMeta,
  setTeamPhotoUploading,
  teamPhotoUploading,
  setSaveError,
  onOpenFocalPointEditor,
}) {
  const teamsToShow = rankedTeams.length ? rankedTeams : nonTestTeams;
  const teamsByNumber = useMemo(
    () => [...teamsToShow].sort((a, b) => (Number(a.teamNumber) || 0) - (Number(b.teamNumber) || 0)),
    [teamsToShow]
  );

  const [teamPhotoListSort, setTeamPhotoListSort] = useState('number');
  /** poster = saved share poster composite (1080 PNG); falls back to raw photo when poster URL missing */
  const [thumbView, setThumbView] = useState('photo');
  const teamsDisplayOrder = useMemo(() => {
    const list = [...teamsByNumber];
    if (teamPhotoListSort === 'mentor') {
      const flyersList = flyers || [];
      list.sort((a, b) => compareTeamsByMentorName(a, b, flyersList));
    }
    return list;
  }, [teamsByNumber, teamPhotoListSort, flyers]);

  const metaRef = useRef(memoryMeta);
  useEffect(() => {
    metaRef.current = memoryMeta;
  }, [memoryMeta]);

  const persistChainRef = useRef(Promise.resolve());

  // (Focal point editing UI removed; we still read `memoryMeta.teamPhotoFocus` via `teamPhotoObjectPosition`.)

  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingTeamPick, setPendingTeamPick] = useState({});
  const [clickAssignTeamId, setClickAssignTeamId] = useState('');
  const [dragPendingId, setDragPendingId] = useState(null);
  const [fileDropHighlightTeamId, setFileDropHighlightTeamId] = useState(null);
  const fileDragDepthRef = useRef({});
  const [clearingAllTeamPhotos, setClearingAllTeamPhotos] = useState(false);
  /** When false, queued photos start with no team selected; filename guesses are shown as hints only. */
  const [preselectTeamFromFilename, setPreselectTeamFromFilename] = useState(false);

  /** Teams available in a queued row’s dropdown: no saved photo yet, not picked on another row / click-assign. */
  const teamsForPendingSelect = useCallback(
    (pendingId) => {
      const reserved = new Set();
      Object.entries(pendingTeamPick).forEach(([pid, tid]) => {
        if (pid !== pendingId && tid) reserved.add(tid);
      });
      if (clickAssignTeamId) reserved.add(clickAssignTeamId);

      const base = teamsDisplayOrder.filter((t) => {
        if (teamPhotosMap[t.id]) return false;
        if (reserved.has(t.id)) return false;
        return true;
      });

      const cur = pendingTeamPick[pendingId];
      const curTeam = cur ? teamsDisplayOrder.find((te) => te.id === cur) : null;
      if (curTeam && !base.some((te) => te.id === cur)) {
        return [curTeam, ...base];
      }
      return base;
    },
    [teamsDisplayOrder, pendingTeamPick, clickAssignTeamId, teamPhotosMap]
  );

  useEffect(() => {
    setPendingTeamPick((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((pid) => {
        const tid = next[pid];
        if (tid && teamPhotosMap[tid]) {
          delete next[pid];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [teamPhotosMap]);

  useEffect(() => {
    if (clickAssignTeamId && teamPhotosMap[clickAssignTeamId]) {
      setClickAssignTeamId('');
    }
  }, [clickAssignTeamId, teamPhotosMap]);

  const revokePreview = useCallback((previewUrl) => {
    if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
  }, []);

  const [teamPosterBackfillBusy, setTeamPosterBackfillBusy] = useState(false);
  const [teamPosterBackfillProgress, setTeamPosterBackfillProgress] = useState({ done: 0, total: 0 });
  const teamPosterBackfillCancelRef = useRef(false);

  const teamPosterGenStageRef = useRef(null);
  const [teamPosterGenProps, setTeamPosterGenProps] = useState(null);

  const buildTeamPosterShareUrl = useCallback(
    (tid) => {
      const y = getEventEditionYear(event) || new Date().getFullYear() || 2026;
      if (event?.memoryBookPublished === true) {
        return buildPublicMemoryBookUrl({ year: y, page: PUBLIC_PAGES.MAIN, teamId: tid });
      }
      return buildPublicLeaderboardUrl({ year: y, eventId });
    },
    [event, eventId]
  );

  const waitForPosterToBeReady = useCallback(async (root, { requireQr, timeoutMs = 8000 } = {}) => {
    if (!root) return;
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const imgs = Array.from(root.querySelectorAll('img'));
      const pending = imgs.filter((img) => !img.complete);
      const qrImg = root.querySelector?.('img.team-share-poster-qr') || null;
      const qrOk = !requireQr || (qrImg && qrImg.complete && qrImg.naturalWidth > 0);
      if (qrOk && !pending.length) return;
      await new Promise((r) => window.setTimeout(r, 80));
    }
  }, []);

  const generateTeamPosterBlob = useCallback(
    async ({ teamId, team, photoUrl }) => {
      const shareUrl = buildTeamPosterShareUrl(teamId);
      const div = ((team?.division || '—').trim() || '—');
      const division = div === '—' ? '' : div;

      setTeamPosterGenProps({
        posterGenKey: `${teamId}_${Date.now()}`,
        teamName: getTeamLabelCore(team),
        teamNumber: team?.teamNumber,
        division: division,
        rank: team?.rank,
        totalPoints: team?.total,
        photoUrl,
        shareUrl,
        brandName: 'Fresh Meet',
      });

      // Wait for React to paint the poster and for async QR generation to insert the QR img.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const node = teamPosterGenStageRef.current?.querySelector?.('[data-poster]');
      if (!node) throw new Error('Poster generation node missing');
      await waitForPosterToBeReady(node, { requireQr: !!shareUrl, timeoutMs: 9000 });

      const blob = await renderNodeToPngBlob(node, { width: 1080, height: 1080 });
      return blob;
    },
    [buildTeamPosterShareUrl, waitForPosterToBeReady]
  );

  const storeTeamPosterForTeam = useCallback(
    async ({ teamId, team, photoUrl }) => {
      if (!eventId) return;
      const blob = await generateTeamPosterBlob({ teamId, team, photoUrl });
      const posterPath = `${MEMORY_BOOK_STORAGE_PREFIX}/${eventId}/teamPosters/${teamId}.png`;
      const posterRef = ref(storage, posterPath);
      await uploadBytes(posterRef, blob, { contentType: 'image/png' });
      const posterUrl = await getDownloadURL(posterRef);

      const merged = mergeTeamPhotoEntry(metaRef.current?.teamPhotos, teamId, photoUrl, posterUrl);
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { teamPhotos: merged }, { merge: true });
      metaRef.current = { ...metaRef.current, teamPhotos: merged };
      setMemoryMeta((prev) => ({ ...prev, teamPhotos: merged }));

      return posterUrl;
    },
    [eventId, generateTeamPosterBlob, setMemoryMeta]
  );

  const pendingPhotosRef = useRef(pendingPhotos);
  pendingPhotosRef.current = pendingPhotos;
  useEffect(() => () => {
    pendingPhotosRef.current.forEach((p) => revokePreview(p.previewUrl));
  }, [revokePreview]);

  const enqueuePersist = useCallback(
    (teamId, file) => {
      if (!eventId || !file) return Promise.resolve();
      persistChainRef.current = persistChainRef.current.then(async () => {
        setTeamPhotoUploading((prev) => ({ ...prev, [teamId]: true }));
        setSaveError('');
        try {
          const transcode = imageNeedsJpegTranscodeForWebImg(file);
          let ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/gi, '') || 'jpg';
          let uploadBody = file;
          if (transcode) {
            uploadBody = await imageFileToJpegBlob(file, {
              maxDim: MEMORY_BOOK_TRANSCODE_MAX_DIM,
              quality: 0.9,
            });
            ext = 'jpg';
          }
          const storageRef = ref(storage, `${MEMORY_BOOK_STORAGE_PREFIX}/${eventId}/teams/${teamId}.${ext}`);
          await uploadBytes(
            storageRef,
            uploadBody,
            transcode || ext === 'jpg' ? { contentType: 'image/jpeg' } : undefined
          );
          const url = await getDownloadURL(storageRef);
          const merged = mergeTeamPhotoEntry(metaRef.current?.teamPhotos, teamId, url);
          await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { teamPhotos: merged }, { merge: true });
          metaRef.current = { ...metaRef.current, teamPhotos: merged };
          setMemoryMeta((prev) => ({ ...prev, teamPhotos: merged }));

          // Best-effort: generate and store the 1080x1080 team-details poster for this team.
          // This avoids client-side PNG generation on public/mobile.
          const posterTeam = rankedTeams.find((t) => t.id === teamId) || nonTestTeams.find((t) => t.id === teamId) || null;
          if (posterTeam) {
            try {
              await storeTeamPosterForTeam({ teamId, team: posterTeam, photoUrl: url });
            } catch (e) {
              console.warn('Team poster generation failed', teamId, e);
              setSaveError(e?.message || 'Poster generation failed (photo upload succeeded)');
            }
          }
        } catch (e) {
          setSaveError(e.message || 'Upload failed');
        } finally {
          setTeamPhotoUploading((prev) => ({ ...prev, [teamId]: false }));
        }
      });
      return persistChainRef.current;
    },
    [eventId, setMemoryMeta, setSaveError, setTeamPhotoUploading, nonTestTeams, rankedTeams, storeTeamPosterForTeam]
  );

  const handleTeamPhotoUpload = (teamId, file) => enqueuePersist(teamId, file);

  const handleRemoveTeamPhoto = async (teamId) => {
    if (!eventId) return;
    setSaveError('');
    const existing = Array.isArray(memoryMeta?.teamPhotos) ? memoryMeta.teamPhotos : [];
    const merged = existing.filter((e) => e.teamId !== teamId);
    try {
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { teamPhotos: merged }, { merge: true });
      metaRef.current = { ...metaRef.current, teamPhotos: merged };
      setMemoryMeta((prev) => ({ ...prev, teamPhotos: merged }));
    } catch (e) {
      setSaveError(e.message || 'Remove failed');
    }
  };

  const savedTeamPhotoCount = (memoryMeta?.teamPhotos || []).filter((e) => e && e.url).length;

  const handleResetAllTeamPhotos = async () => {
    if (!eventId) return;
    if (savedTeamPhotoCount === 0) {
      window.alert('No team photos are saved yet.');
      return;
    }
    const msg =
      `Remove all ${savedTeamPhotoCount} saved team photo(s) from storage and the memory book?\n\n` +
      'You can upload and assign again from scratch. This cannot be undone.';
    if (!window.confirm(msg)) return;
    setClearingAllTeamPhotos(true);
    setSaveError('');
    try {
      const folderRef = ref(storage, `${MEMORY_BOOK_STORAGE_PREFIX}/${eventId}/teams/`);
      const listResult = await listAll(folderRef);
      await Promise.all(listResult.items.map((itemRef) => deleteObject(itemRef)));
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { teamPhotos: [] }, { merge: true });
      metaRef.current = { ...metaRef.current, teamPhotos: [] };
      setMemoryMeta((prev) => ({ ...prev, teamPhotos: [] }));
      setClickAssignTeamId('');
    } catch (e) {
      setSaveError(e.message || 'Failed to reset team photos');
    } finally {
      setClearingAllTeamPhotos(false);
    }
  };

  const addFilesFromInput = (fileList) => {
    if (!fileList?.length) return;
    const images = Array.from(fileList).filter((f) => (f.type || '').startsWith('image/'));
    if (!images.length) return;
    const next = images.map((file) => {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const guessed = guessTeamIdFromFilename(file.name, teamsByNumber);
      return { id, file, previewUrl: URL.createObjectURL(file), guessedTeamId: guessed };
    });
    setPendingPhotos((prev) => [...prev, ...next]);
    setPendingTeamPick((prev) => {
      const o = { ...prev };
      const claimed = new Set();
      Object.values(o).forEach((tid) => {
        if (tid) claimed.add(tid);
      });
      Object.keys(teamPhotosMap).forEach((id) => claimed.add(id));
      if (preselectTeamFromFilename) {
        next.forEach((p) => {
          if (!p.guessedTeamId || claimed.has(p.guessedTeamId)) return;
          o[p.id] = p.guessedTeamId;
          claimed.add(p.guessedTeamId);
        });
      }
      return o;
    });
  };

  const removePending = (id) => {
    setPendingPhotos((prev) => {
      const p = prev.find((x) => x.id === id);
      if (p) revokePreview(p.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
    setPendingTeamPick((prev) => {
      const o = { ...prev };
      delete o[id];
      return o;
    });
  };

  const clearPending = () => {
    pendingPhotos.forEach((p) => revokePreview(p.previewUrl));
    setPendingPhotos([]);
    setPendingTeamPick({});
  };

  const assignPendingToTeam = async (pendingId, teamId) => {
    const item = pendingPhotos.find((p) => p.id === pendingId);
    if (!item || !teamId) return;
    await enqueuePersist(teamId, item.file);
    removePending(pendingId);
  };

  const uploadAllWithSelection = async () => {
    const snapshot = pendingPhotos.filter((p) => pendingTeamPick[p.id]);
    if (!snapshot.length) return;
    for (const p of snapshot) {
      const tid = pendingTeamPick[p.id];
      await enqueuePersist(tid, p.file);
      revokePreview(p.previewUrl);
    }
    const ids = new Set(snapshot.map((p) => p.id));
    setPendingPhotos((prev) => prev.filter((p) => !ids.has(p.id)));
    setPendingTeamPick((prev) => {
      const o = { ...prev };
      ids.forEach((id) => { delete o[id]; });
      return o;
    });
  };

  const readyPendingCount = pendingPhotos.filter((p) => pendingTeamPick[p.id]).length;
  const anyBusy = Object.values(teamPhotoUploading).some(Boolean) || teamPosterBackfillBusy;
  const missingTeamPosterCount = useMemo(
    () => (Array.isArray(memoryMeta?.teamPhotos) ? memoryMeta.teamPhotos : []).filter((e) => e?.teamId && e?.url && !e?.posterUrl).length,
    [memoryMeta?.teamPhotos]
  );

  const startTeamPosterBackfill = useCallback(async ({ force = false } = {}) => {
    if (!eventId || teamPosterBackfillBusy) return;
    const all = Array.isArray(memoryMeta?.teamPhotos) ? memoryMeta.teamPhotos : [];
    const targets = force
      ? all.filter((e) => e?.teamId && e?.url)
      : all.filter((e) => e?.teamId && e?.url && !e?.posterUrl);
    if (!targets.length) return;

    setSaveError('');
    setTeamPosterBackfillBusy(true);
    teamPosterBackfillCancelRef.current = false;
    setTeamPosterBackfillProgress({ done: 0, total: targets.length });

    try {
      for (let i = 0; i < targets.length; i += 1) {
        if (teamPosterBackfillCancelRef.current) break;
        const t = targets[i];
        try {
          const posterTeam = rankedTeams.find((x) => x.id === t.teamId) || nonTestTeams.find((x) => x.id === t.teamId) || null;
          if (!posterTeam) throw new Error(`Team not found: ${t.teamId}`);
          await storeTeamPosterForTeam({ teamId: t.teamId, team: posterTeam, photoUrl: t.url });
        } catch (e) {
          console.warn('Team poster backfill failed for', t.teamId, e);
        } finally {
          setTeamPosterBackfillProgress((p) => ({ done: Math.min(p.total, p.done + 1), total: p.total }));
        }
      }
    } catch (e) {
      setSaveError(e?.message || 'Team poster backfill failed');
    } finally {
      setTeamPosterBackfillBusy(false);
      teamPosterBackfillCancelRef.current = false;
    }
  }, [eventId, teamPosterBackfillBusy, memoryMeta?.teamPhotos, rankedTeams, nonTestTeams, setSaveError, storeTeamPosterForTeam]);

  const cancelTeamPosterBackfill = useCallback(() => {
    teamPosterBackfillCancelRef.current = true;
  }, []);
  const selectedAssignTeam = clickAssignTeamId
    ? teamsDisplayOrder.find((x) => x.id === clickAssignTeamId)
    : null;
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div className="memory-book-tab-content">
      <div className="memory-book-team-photos-title-row">
        <h3 className="memory-book-section-title">Team photos</h3>
        <button
          type="button"
          className="memory-book-help-btn"
          onClick={() => setShowHelp(true)}
          aria-label="Team photo help"
          title="Help"
        >
          ?
        </button>
      </div>
      <p className="memory-book-tab-desc">
        Upload a folder or images, then assign each photo to a team.
      </p>

      {showHelp && (
        <div className="memory-book-modal-overlay" onClick={() => setShowHelp(false)} role="dialog" aria-modal="true" aria-labelledby="team-photo-help-title">
          <div className="memory-book-modal" onClick={(e) => e.stopPropagation()}>
            <div className="memory-book-modal-header">
              <h3 id="team-photo-help-title">Team photo help</h3>
              <button type="button" className="memory-book-modal-close" onClick={() => setShowHelp(false)} aria-label="Close">✕</button>
            </div>
            <div className="memory-book-modal-body">
              <p className="memory-book-tab-desc" style={{ margin: 0 }}>
                Add a folder or many images at once, then assign each photo to a team. Queued files stay <strong>on your device</strong> until you assign them—nothing is uploaded until then. <strong>Skip</strong> drops a file from the queue only.
                <br /><br />
                Assign by using the dropdown + Assign &amp; upload, by <strong>dragging</strong> a queued photo onto a team card, by <strong>dropping a file from your desktop</strong> onto the team’s photo slot, or with <strong>Select for click-assign</strong> then <strong>click the queued photo</strong>.
                <br /><br />
                Optional: turn on <strong>Pre-fill from filenames</strong> to guess team numbers from names like <code>team12.jpg</code>.
                <br /><br />
                <strong>Reset all team photos</strong> deletes every saved team photo in Storage and clears assignments (not undoable).
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="memory-book-team-batch-toolbar">
        <label className="memory-book-upload-label memory-book-upload-label--inline">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              addFilesFromInput(e.target.files);
              e.target.value = '';
            }}
          />
          Add images
        </label>
        <label className="memory-book-upload-label memory-book-upload-label--inline">
          <input
            type="file"
            accept="image/*"
            webkitdirectory=""
            directory=""
            multiple
            onChange={(e) => {
              addFilesFromInput(e.target.files);
              e.target.value = '';
            }}
          />
          Add folder
        </label>
        <label className="memory-book-preselect-filename">
          <input
            type="checkbox"
            checked={preselectTeamFromFilename}
            onChange={(e) => setPreselectTeamFromFilename(e.target.checked)}
          />
          Pre-fill from filenames
        </label>
        {pendingPhotos.length > 0 && (
          <>
            <button type="button" className="memory-book-batch-clear" onClick={clearPending} disabled={anyBusy}>
              Clear queue ({pendingPhotos.length})
            </button>
            <button
              type="button"
              className="memory-book-batch-save"
              onClick={() => uploadAllWithSelection()}
              disabled={readyPendingCount === 0 || anyBusy}
            >
              {anyBusy
                ? 'Uploading…'
                : `Upload ${readyPendingCount} assigned photo${readyPendingCount !== 1 ? 's' : ''}`}
            </button>
          </>
        )}
        <button
          type="button"
          className="memory-book-team-reset-all"
          onClick={() => handleResetAllTeamPhotos()}
          disabled={anyBusy || clearingAllTeamPhotos || savedTeamPhotoCount === 0}
          title="Delete every saved team photo from storage and clear assignments so you can re-upload and match by hand."
        >
          {clearingAllTeamPhotos ? 'Resetting…' : `Reset all team photos (${savedTeamPhotoCount})`}
        </button>
        {missingTeamPosterCount > 0 ? (
          <button
            type="button"
            className="memory-book-batch-save"
            onClick={() => startTeamPosterBackfill({ force: false })}
            disabled={anyBusy}
            title="Generate and cache the 1080x1080 team-details poster PNGs for public viewing."
          >
            {teamPosterBackfillBusy ? 'Generating posters…' : `Backfill team posters (${missingTeamPosterCount})`}
          </button>
        ) : (
          <>
            <button type="button" className="memory-book-batch-save" disabled title="All team posters are already generated">
              Posters up to date
            </button>
            <button
              type="button"
              className="memory-book-batch-save"
              onClick={() => startTeamPosterBackfill({ force: true })}
              disabled={anyBusy}
              title="Re-generate posters for all teams (use after changing poster sizing/format)."
            >
              Rebuild posters
            </button>
          </>
        )}
        {teamPosterBackfillBusy ? (
          <button
            type="button"
            className="memory-book-team-reset-all"
            onClick={cancelTeamPosterBackfill}
            disabled={clearingAllTeamPhotos}
            title="Stop after the current poster finishes generating."
          >
            Cancel
          </button>
        ) : null}
      </div>

      {teamPosterBackfillBusy ? (
        <p className="memory-book-tab-desc" style={{ marginTop: '0.55rem' }}>
          Generating team posters… {teamPosterBackfillProgress.done}/{teamPosterBackfillProgress.total}
        </p>
      ) : null}

      {selectedAssignTeam && (
        <p className="memory-book-click-assign-hint">
          Selected team:{' '}
          <strong>{teamPhotoAssignmentLabel(selectedAssignTeam, flyers || [])}</strong>
          {' '}
          — click a queued photo below to assign, or{' '}
          <button type="button" className="memory-book-linkish" onClick={() => setClickAssignTeamId('')}>cancel</button>
        </p>
      )}

      {pendingPhotos.length > 0 && (
        <div className="memory-book-pending-section">
          <h4 className="memory-book-pending-title">Queued photos — assign to teams</h4>
          <div className="memory-book-pending-grid">
            {pendingPhotos.map((p) => {
              const guessTeam = p.guessedTeamId ? teamsByNumber.find((t) => t.id === p.guessedTeamId) : null;
              const guessMentor = guessTeam ? mentorDisplayNameWithInitial(guessTeam, flyers || []) : '';
              return (
              <div
                key={p.id}
                className={`memory-book-pending-card${dragPendingId === p.id ? ' memory-book-pending-card--dragging' : ''}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/pending-id', p.id);
                  e.dataTransfer.effectAllowed = 'move';
                  setDragPendingId(p.id);
                }}
                onDragEnd={() => setDragPendingId(null)}
                onClick={() => {
                  if (clickAssignTeamId) {
                    assignPendingToTeam(p.id, clickAssignTeamId);
                    setClickAssignTeamId('');
                  }
                }}
                title={clickAssignTeamId ? 'Click to assign to selected team' : 'Drag onto a team card, or pick a team and Assign'}
              >
                <div className="memory-book-pending-thumb">
                  <img src={p.previewUrl} alt="" />
                </div>
                <div className="memory-book-pending-meta">
                  <span className="memory-book-pending-filename" title={p.file.name}>{p.file.name}</span>
                  {guessTeam && (
                    <span className="memory-book-pending-guess">
                      Guess: #{guessTeam.teamNumber}{guessMentor ? ` — ${guessMentor}` : ''}
                    </span>
                  )}
                  <select
                    className="memory-book-pending-select"
                    value={pendingTeamPick[p.id] || ''}
                    onChange={(e) => setPendingTeamPick((prev) => ({ ...prev, [p.id]: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">Team # — mentor…</option>
                    {teamsForPendingSelect(p.id).map((t) => (
                      <option key={t.id} value={t.id}>
                        {teamPhotoAssignmentLabel(t, flyers || [])}
                      </option>
                    ))}
                  </select>
                  <div className="memory-book-pending-actions">
                    <button
                      type="button"
                      className="memory-book-pending-assign"
                      disabled={!pendingTeamPick[p.id] || anyBusy}
                      onClick={(e) => {
                        e.stopPropagation();
                        assignPendingToTeam(p.id, pendingTeamPick[p.id]);
                      }}
                    >
                      Assign & upload
                    </button>
                    <button type="button" className="memory-book-remove-btn" onClick={(e) => { e.stopPropagation(); removePending(p.id); }}>Skip</button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="memory-book-teams-header-row">
        <h4 className="memory-book-teams-grid-title">Teams</h4>
        <div className="memory-book-teams-header-controls">
          <div className="memory-book-team-sort-toggle" role="group" aria-label="Sort team list">
            <button
              type="button"
              className={`memory-book-team-sort-btn${teamPhotoListSort === 'number' ? ' memory-book-team-sort-btn--active' : ''}`}
              onClick={() => setTeamPhotoListSort('number')}
            >
              By team #
            </button>
            <button
              type="button"
              className={`memory-book-team-sort-btn${teamPhotoListSort === 'mentor' ? ' memory-book-team-sort-btn--active' : ''}`}
              onClick={() => setTeamPhotoListSort('mentor')}
            >
              By mentor
            </button>
          </div>
          <div className="memory-book-team-sort-toggle" role="group" aria-label="Thumbnail view">
            <button
              type="button"
              className={`memory-book-team-sort-btn${thumbView === 'photo' ? ' memory-book-team-sort-btn--active' : ''}`}
              onClick={() => setThumbView('photo')}
            >
              Photos
            </button>
            <button
              type="button"
              className={`memory-book-team-sort-btn${thumbView === 'poster' ? ' memory-book-team-sort-btn--active' : ''}`}
              onClick={() => setThumbView('poster')}
              title="Show saved share posters (composite). Teams without one yet fall back to the raw photo."
            >
              Posters
            </button>
          </div>
        </div>
      </div>
      <p className="memory-book-tab-desc memory-book-tab-desc--tight">
        {teamPhotoListSort === 'number'
          ? 'Alphabetical by team number. '
          : 'Alphabetical by mentor last name, then first; mentors without a last name follow those with one; teams with no matched mentor flyer appear last. '}
        Drop a queued photo on a card, or <strong>drag an image file from your computer</strong> onto the photo slot (replaces an existing photo too). Or use <em>Select for click-assign</em> then click a queued photo. <em>Upload one</em> still works per team.{thumbView === 'poster' ? ' Use Photos / Posters above to preview raw uploads vs saved share posters.' : ''}
      </p>
      <div className="memory-book-team-photos-grid">
        {teamsDisplayOrder.map((t) => {
          const mentorLabel = mentorDisplayNameWithInitial(t, flyers || []);
          const teamPhotoExtraName = getTeamLabelCore(t);
          const teamPhotoNumDisp = getTeamNumberDisplay(t);
          const teamPhotoSynthetic =
            teamPhotoNumDisp !== '?' ? `Team ${teamPhotoNumDisp}` : '';
          const teamPhotoExtraNorm = teamPhotoExtraName.trim().toLowerCase();
          const mentorLabelNorm = (mentorLabel || '').trim().toLowerCase();
          const showTeamPhotoExtraName =
            teamPhotoExtraName &&
            teamPhotoExtraName !== teamPhotoSynthetic &&
            teamPhotoExtraNorm !== mentorLabelNorm;
          const isDropTarget = dragPendingId;
          const isOsFileDropTarget = fileDropHighlightTeamId === t.id;
          const isClickTarget = clickAssignTeamId === t.id;
          const pickedOnAnotherQueuedRow = Object.entries(pendingTeamPick).some(
            ([pid, tid]) => tid === t.id && pendingPhotos.some((pp) => pp.id === pid)
          );
          const clickAssignDisabled =
            anyBusy ||
            !!teamPhotosMap[t.id] ||
            (pickedOnAnotherQueuedRow && !isClickTarget);
          const onTeamCardDragEnter = (e) => {
            if (!dataTransferHasFilePayload(e.dataTransfer) || teamPhotoUploading[t.id]) return;
            e.preventDefault();
            const m = fileDragDepthRef.current;
            const n = (m[t.id] || 0) + 1;
            m[t.id] = n;
            if (n === 1) setFileDropHighlightTeamId(t.id);
          };
          const onTeamCardDragLeave = (e) => {
            if (e.currentTarget.contains(e.relatedTarget)) return;
            const m = fileDragDepthRef.current;
            const n = Math.max(0, (m[t.id] || 0) - 1);
            if (n === 0) delete m[t.id];
            else m[t.id] = n;
            if (n === 0) setFileDropHighlightTeamId((cur) => (cur === t.id ? null : cur));
          };
          const clearOsFileDropUi = () => {
            fileDragDepthRef.current = {};
            setFileDropHighlightTeamId(null);
          };
          return (
            <div
              key={t.id}
              className={`memory-book-team-photo-card${isClickTarget ? ' memory-book-team-photo-card--active' : ''}${isOsFileDropTarget ? ' memory-book-team-photo-card--file-hover' : ''}`}
              onDragEnter={onTeamCardDragEnter}
              onDragLeave={onTeamCardDragLeave}
              onDragOver={(e) => {
                const fromDisk = dataTransferHasFilePayload(e.dataTransfer);
                if (fromDisk && !teamPhotoUploading[t.id]) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'copy';
                  return;
                }
                if (dragPendingId) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                clearOsFileDropUi();
                const pid = e.dataTransfer.getData('text/pending-id');
                setDragPendingId(null);
                const files = e.dataTransfer.files;
                if (files?.length) {
                  const f = Array.from(files).find((file) => (file.type || '').startsWith('image/'));
                  if (f && !teamPhotoUploading[t.id]) handleTeamPhotoUpload(t.id, f);
                  return;
                }
                if (pid) assignPendingToTeam(pid, t.id);
              }}
            >
              <div
                className={`memory-book-team-photo-thumb${isOsFileDropTarget ? ' memory-book-team-photo-thumb--os-drop' : ''}`}
              >
                {(() => {
                  const rawPhoto = teamPhotosMap[t.id];
                  const posterUrl = teamPostersMap[t.id];
                  const showingPosterComposite = thumbView === 'poster' && !!posterUrl;
                  const displayUrl =
                    thumbView === 'poster' ? posterUrl || rawPhoto : rawPhoto;
                  const posterFallbackNote =
                    thumbView === 'poster' && rawPhoto && !posterUrl;

                  if (!displayUrl) {
                    return (
                      <span
                        className={`memory-book-team-photo-placeholder${isDropTarget ? ' memory-book-team-photo-placeholder--drop' : ''}${isOsFileDropTarget && !isDropTarget ? ' memory-book-team-photo-placeholder--os-drop' : ''}`}
                      >
                        {isDropTarget ? 'Drop photo' : isOsFileDropTarget ? 'Drop image file' : 'No photo'}
                      </span>
                    );
                  }

                  return (
                    <>
                      <img
                        src={displayUrl}
                        alt=""
                        style={
                          showingPosterComposite
                            ? undefined
                            : { objectPosition: teamPhotoObjectPosition(t.id, memoryMeta, teamFocalMap) }
                        }
                      />
                      {posterFallbackNote && (
                        <div className="memory-book-team-photo-poster-note" title="Poster is generated after upload or via backfill.">
                          Raw photo · no poster URL yet
                        </div>
                      )}
                      {isOsFileDropTarget && (
                        <div className="memory-book-team-photo-drop-overlay" aria-hidden>
                          Drop to replace
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="memory-book-team-photo-info">
                <strong className="memory-book-team-photo-heading">
                  #{t.teamNumber}
                  {mentorLabel ? ` — ${mentorLabel}` : ' — Mentor TBD'}
                </strong>
                {showTeamPhotoExtraName && (
                  <span className="memory-book-team-photo-teamname">{teamPhotoExtraName}</span>
                )}
                <span className="memory-book-team-photo-meta">{(t.division || '—').trim() || '—'}</span>
              </div>
              <div className="memory-book-team-photo-actions">
                {teamPhotosMap[t.id] && typeof onOpenFocalPointEditor === 'function' && (
                  <button
                    type="button"
                    className="memory-book-edit-btn"
                    disabled={anyBusy}
                    onClick={() => onOpenFocalPointEditor({ kind: 'team', teamId: t.id })}
                  >
                    Set focal point
                  </button>
                )}
                <button
                  type="button"
                  className={`memory-book-team-select-btn${clickAssignTeamId === t.id ? ' memory-book-team-select-btn--on' : ''}`}
                  disabled={clickAssignDisabled}
                  title={
                    teamPhotosMap[t.id]
                      ? 'Team already has a photo — use Upload one to replace or Remove first.'
                      : pickedOnAnotherQueuedRow && !isClickTarget
                        ? 'This team is already selected on another queued photo.'
                        : undefined
                  }
                  onClick={() => setClickAssignTeamId((id) => (id === t.id ? '' : t.id))}
                >
                  {clickAssignTeamId === t.id ? 'Selecting…' : 'Select for click-assign'}
                </button>
                <label className="memory-book-upload-label memory-book-upload-label--small">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleTeamPhotoUpload(t.id, f);
                      e.target.value = '';
                    }}
                    disabled={teamPhotoUploading[t.id]}
                  />
                  {teamPhotoUploading[t.id] ? 'Uploading…' : 'Upload one'}
                </label>
                {teamPhotosMap[t.id] && (
                  <button type="button" className="memory-book-remove-btn" onClick={() => handleRemoveTeamPhoto(t.id)}>Remove</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="memory-book-share-poster-stage" aria-hidden="true" ref={teamPosterGenStageRef}>
        {teamPosterGenProps ? <TeamSharePoster key={teamPosterGenProps.posterGenKey} {...teamPosterGenProps} /> : null}
      </div>
    </div>
  );
}

function GalleryTab({
  eventId,
  galleryItems,
  setGalleryItems,
  memoryMeta,
  setMemoryMeta,
  galleryUploading,
  setGalleryUploading,
  setSaveError,
  onOpenFocalPointEditor,
}) {
  const featuredSet = useMemo(() => new Set(memoryMeta?.featuredGalleryPhotos || []), [memoryMeta?.featuredGalleryPhotos]);
  const [featuredBusy, setFeaturedBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [bulkCredit, setBulkCredit] = useState('');
  const [applyBulkCreditOnUpload, setApplyBulkCreditOnUpload] = useState(true);
  const [selectedMap, setSelectedMap] = useState({});
  const [galleryFilter, setGalleryFilter] = useState('all');
  const [galleryCreditFilter, setGalleryCreditFilter] = useState('');
  const [thumbBackfillBusy, setThumbBackfillBusy] = useState(false);
  const [thumbBackfillProgress, setThumbBackfillProgress] = useState({ done: 0, total: 0 });
  const thumbBackfillCancelRef = useRef(false);
  const folderInputRef = useRef(null);

  useEffect(() => {
    const el = folderInputRef.current;
    if (!el) return;
    el.setAttribute('webkitdirectory', '');
    el.setAttribute('directory', '');
  }, []);

  const buildCreditsMapForRefresh = useCallback(() => {
    const cm = { ...(memoryMeta?.galleryCredits || {}) };
    galleryItems.forEach((i) => {
      const c = (i.credit || '').trim();
      if (c) cm[i.fullPath] = c;
    });
    return cm;
  }, [memoryMeta?.galleryCredits, galleryItems]);

  const uploadImageFiles = useCallback(
    async (rawList) => {
      const files = uniqueGalleryImageFiles(rawList);
      if (!files.length || !eventId) return;
      setGalleryUploading(true);
      setSaveError('');
      const base = `${MEMORY_BOOK_STORAGE_PREFIX}/${eventId}/gallery/`;
      const thumbsBase = `${MEMORY_BOOK_STORAGE_PREFIX}/${eventId}/galleryThumbs/`;
      const ts = Date.now();
      const justUploaded = [];
      try {
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i];
          const transcode = imageNeedsJpegTranscodeForWebImg(file);
          let ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/gi, '') || 'jpg';
          let uploadBody = file;
          if (transcode) {
            uploadBody = await imageFileToJpegBlob(file, {
              maxDim: MEMORY_BOOK_TRANSCODE_MAX_DIM,
              quality: 0.9,
            });
            ext = 'jpg';
          }
          const safeStem = (file.name || 'img').replace(/\.[^.]+$/, '').slice(0, 24).replace(/[^\w-]+/g, '_');
          const name = `${ts}_${i}_${safeStem}.${ext}`;
          const storageRef = ref(storage, base + name);
          await uploadBytes(
            storageRef,
            uploadBody,
            transcode || ext === 'jpg' ? { contentType: 'image/jpeg' } : undefined
          );
          if (storageRef?.fullPath) justUploaded.push(storageRef.fullPath);

          // Thumbnail (best-effort): store small JPEG for fast previews.
          if (storageRef?.fullPath) {
            try {
              const thumbBlob = await imageFileToThumbnailJpegBlob(uploadBody, { maxDim: 420, quality: 0.78 });
              const thumbRef = ref(storage, `${thumbsBase}${thumbKeyForFullPath(storageRef.fullPath)}`);
              await uploadBytes(thumbRef, thumbBlob, { contentType: 'image/jpeg' });
            } catch {
              // ignore thumbnail failures (unsupported formats / browser)
            }
          }
        }

        const uploadCredit = applyBulkCreditOnUpload ? String(bulkCredit || '').trim() : '';
        let creditsMap = buildCreditsMapForRefresh();
        if (uploadCredit && justUploaded.length) {
          const patch = {};
          justUploaded.forEach((p) => {
            patch[p] = uploadCredit;
          });
          await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { galleryCredits: patch }, { merge: true });
          setMemoryMeta((prev) => {
            const baseMeta = prev || {};
            const gc = { ...(baseMeta.galleryCredits || {}) };
            justUploaded.forEach((p) => {
              gc[p] = uploadCredit;
            });
            return { ...baseMeta, galleryCredits: gc };
          });
          creditsMap = { ...creditsMap, ...patch };
        }

        const items = await fetchGalleryItemsFromStorage(eventId, creditsMap);
        setGalleryItems(items);
      } catch (err) {
        setSaveError(err.message || 'Upload failed');
      } finally {
        setGalleryUploading(false);
      }
    },
    [eventId, setGalleryItems, setGalleryUploading, setSaveError, buildCreditsMapForRefresh, applyBulkCreditOnUpload, bulkCredit, setMemoryMeta]
  );

  const missingThumbCount = useMemo(
    () => (galleryItems || []).filter((g) => g?.fullPath && g?.url && !g?.thumbUrl).length,
    [galleryItems]
  );

  const startThumbBackfill = useCallback(async () => {
    if (!eventId || thumbBackfillBusy) return;
    const targets = (galleryItems || []).filter((g) => g?.fullPath && g?.url && !g?.thumbUrl);
    if (!targets.length) return;
    setSaveError('');
    setThumbBackfillBusy(true);
    thumbBackfillCancelRef.current = false;
    setThumbBackfillProgress({ done: 0, total: targets.length });
    const thumbsBase = `${MEMORY_BOOK_STORAGE_PREFIX}/${eventId}/galleryThumbs/`;
    try {
      for (let i = 0; i < targets.length; i += 1) {
        if (thumbBackfillCancelRef.current) break;
        const t = targets[i];
        try {
          const res = await fetch(t.url, { cache: 'no-store' });
          const blob = await res.blob();
          const thumbBlob = await imageFileToThumbnailJpegBlob(blob, { maxDim: 420, quality: 0.78 });
          const thumbRef = ref(storage, `${thumbsBase}${thumbKeyForFullPath(t.fullPath)}`);
          await uploadBytes(thumbRef, thumbBlob, { contentType: 'image/jpeg' });
        } catch {
          // ignore per-photo errors
        } finally {
          setThumbBackfillProgress((p) => ({ ...p, done: Math.min(p.total, p.done + 1) }));
        }
      }
    } catch (err) {
      setSaveError(err.message || 'Thumbnail backfill failed');
    } finally {
      try {
        const items = await fetchGalleryItemsFromStorage(eventId, buildCreditsMapForRefresh());
        setGalleryItems(items);
      } catch {
        // ignore refresh failures
      }
      setThumbBackfillBusy(false);
      thumbBackfillCancelRef.current = false;
    }
  }, [eventId, thumbBackfillBusy, galleryItems, setGalleryItems, setSaveError, buildCreditsMapForRefresh]);

  const cancelThumbBackfill = useCallback(() => {
    thumbBackfillCancelRef.current = true;
  }, []);

  const handleFileInputChange = (e) => {
    const fl = e.target.files;
    if (fl?.length) uploadImageFiles(fl);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!galleryUploading) e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!galleryUploading) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (galleryUploading || !eventId) return;
    try {
      const files = await collectGalleryFilesFromDataTransfer(e.dataTransfer);
      if (files.length) await uploadImageFiles(files);
    } catch (err) {
      setSaveError(err.message || 'Drop failed');
    }
  };

  const persistCredit = async (fullPath, creditText) => {
    const trimmed = String(creditText || '').trim();
    setGalleryItems((prev) => prev.map((i) => (i.fullPath === fullPath ? { ...i, credit: trimmed } : i)));
    const prevStored = (memoryMeta?.galleryCredits?.[fullPath] || '').toString().trim();
    if (trimmed === prevStored) return;
    setSaveError('');
    try {
      await setDoc(
        doc(firestore, 'eventMemoryMeta', eventId),
        trimmed
          ? { galleryCredits: { [fullPath]: trimmed } }
          : { galleryCredits: { [fullPath]: deleteField() } },
        { merge: true }
      );
      setMemoryMeta((prev) => {
        const base = prev || {};
        const gc = { ...(base.galleryCredits || {}) };
        if (trimmed) gc[fullPath] = trimmed;
        else delete gc[fullPath];
        return { ...base, galleryCredits: gc };
      });
    } catch (err) {
      setSaveError(err.message || 'Could not save photo credit');
    }
  };

  const selectedFullPaths = useMemo(
    () => Object.keys(selectedMap || {}).filter((k) => selectedMap[k]),
    [selectedMap]
  );
  const uniqueGalleryCredits = useMemo(
    () => uniqueTrimmedGalleryCredits(galleryItems),
    [galleryItems]
  );

  const filteredGalleryItems = useMemo(() => {
    let list =
      galleryFilter === 'featured'
        ? (galleryItems || []).filter((i) => featuredSet.has(i.fullPath))
        : (galleryItems || []);
    const c = (galleryCreditFilter || '').trim();
    if (!c) return list;
    if (c === GALLERY_CREDIT_FILTER_NONE) return list.filter((i) => !(i.credit || '').trim());
    return list.filter((i) => (i.credit || '').trim() === c);
  }, [galleryFilter, galleryItems, featuredSet, galleryCreditFilter]);

  /** Drop selections for photos hidden by All/Featured or photo-credit filters so bulk actions match the visible grid. */
  useEffect(() => {
    const allowed = new Set(
      (filteredGalleryItems || []).map((i) => i.fullPath).filter(Boolean)
    );
    setSelectedMap((prev) => {
      const base = prev && typeof prev === 'object' ? prev : {};
      const next = {};
      Object.keys(base).forEach((k) => {
        if (base[k] && allowed.has(k)) next[k] = true;
      });
      const prevSel = Object.keys(base)
        .filter((k) => base[k])
        .sort();
      const nextSel = Object.keys(next).sort();
      if (prevSel.length === nextSel.length && prevSel.every((k, i) => k === nextSel[i])) {
        return prev;
      }
      return next;
    });
  }, [filteredGalleryItems]);

  const setSelectedForPath = (fullPath, nextOn) => {
    setSelectedMap((prev) => {
      const base = prev && typeof prev === 'object' ? prev : {};
      if (!nextOn) {
        if (!base[fullPath]) return base;
        const copy = { ...base };
        delete copy[fullPath];
        return copy;
      }
      if (base[fullPath]) return base;
      return { ...base, [fullPath]: true };
    });
  };

  const clearAllSelected = () => setSelectedMap({});

  const selectAll = useCallback(() => {
    setSelectedMap(() => {
      const next = {};
      filteredGalleryItems.forEach((i) => {
        if (i?.fullPath) next[i.fullPath] = true;
      });
      return next;
    });
  }, [filteredGalleryItems]);

  const applyCreditBulk = async (fullPaths, creditText, { onlyBlank = false } = {}) => {
    if (!eventId || galleryUploading) return;
    const trimmed = String(creditText || '').trim();
    const paths = Array.isArray(fullPaths) ? fullPaths.filter(Boolean) : [];
    if (!paths.length) return;

    const finalPaths = onlyBlank
      ? paths.filter((p) => {
          const item = galleryItems.find((i) => i.fullPath === p);
          return !String(item?.credit || '').trim();
        })
      : paths;

    if (!finalPaths.length) return;

    setSaveError('');
    setGalleryUploading(true);
    try {
      const patch = {};
      finalPaths.forEach((p) => {
        patch[p] = trimmed ? trimmed : deleteField();
      });

      await setDoc(
        doc(firestore, 'eventMemoryMeta', eventId),
        { galleryCredits: patch },
        { merge: true }
      );

      setMemoryMeta((prev) => {
        const base = prev || {};
        const gc = { ...(base.galleryCredits || {}) };
        finalPaths.forEach((p) => {
          if (trimmed) gc[p] = trimmed;
          else delete gc[p];
        });
        return { ...base, galleryCredits: gc };
      });

      setGalleryItems((prev) =>
        prev.map((i) => (finalPaths.includes(i.fullPath) ? { ...i, credit: trimmed } : i))
      );
    } catch (err) {
      setSaveError(err.message || 'Could not batch update photo credits');
    } finally {
      setGalleryUploading(false);
    }
  };

  const deleteSelectedBulk = async (fullPaths) => {
    if (!eventId || galleryUploading) return;
    const paths = Array.isArray(fullPaths) ? fullPaths.filter(Boolean) : [];
    if (!paths.length) return;

    const ok = window.confirm(`Delete ${paths.length} photo(s) from the gallery? This cannot be undone.`);
    if (!ok) return;

    setSaveError('');
    setGalleryUploading(true);
    try {
      const succeeded = [];
      for (let i = 0; i < paths.length; i += 1) {
        const p = paths[i];
        try {
          await deleteObject(ref(storage, p));
          succeeded.push(p);
        } catch (err) {
          // Keep going; we'll report after.
          console.warn('Gallery delete failed', p, err);
        }
      }

      if (succeeded.length) {
        const patch = {};
        succeeded.forEach((p) => {
          patch[p] = deleteField();
        });
        await setDoc(
          doc(firestore, 'eventMemoryMeta', eventId),
          { galleryCredits: patch },
          { merge: true }
        );
        setMemoryMeta((prev) => {
          const base = prev || {};
          const gc = { ...(base.galleryCredits || {}) };
          succeeded.forEach((p) => {
            delete gc[p];
          });
          return { ...base, galleryCredits: gc };
        });
        setGalleryItems((prev) => prev.filter((i) => !succeeded.includes(i.fullPath)));
        setSelectedMap((prev) => {
          const base = prev && typeof prev === 'object' ? prev : {};
          const copy = { ...base };
          succeeded.forEach((p) => {
            delete copy[p];
          });
          return copy;
        });
      }

      if (succeeded.length !== paths.length) {
        setSaveError(
          `Deleted ${succeeded.length}/${paths.length} photo(s). Some deletes failed (check console).`
        );
      }
    } catch (err) {
      setSaveError(err.message || 'Batch delete failed');
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleRemove = async (fullPath) => {
    setSaveError('');
    try {
      await deleteObject(ref(storage, fullPath));
      await setDoc(
        doc(firestore, 'eventMemoryMeta', eventId),
        { galleryCredits: { [fullPath]: deleteField() } },
        { merge: true }
      );
      setMemoryMeta((prev) => {
        const base = prev || {};
        const gc = { ...(base.galleryCredits || {}) };
        delete gc[fullPath];
        return { ...base, galleryCredits: gc };
      });
      const next = galleryItems.filter((i) => i.fullPath !== fullPath);
      setGalleryItems(next);
      setSelectedForPath(fullPath, false);
    } catch (err) {
      setSaveError(err.message || 'Delete failed');
    }
  };

  const toggleFeatured = async (fullPath) => {
    if (!eventId || !fullPath) return;
    if (featuredBusy) return;
    setFeaturedBusy(true);
    setSaveError('');
    try {
      const prev = Array.isArray(memoryMeta?.featuredGalleryPhotos) ? memoryMeta.featuredGalleryPhotos : [];
      const next = prev.includes(fullPath) ? prev.filter((p) => p !== fullPath) : [...prev, fullPath];
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { featuredGalleryPhotos: next }, { merge: true });
      setMemoryMeta((m) => ({ ...(m || {}), featuredGalleryPhotos: next }));
    } catch (e) {
      setSaveError(e.message || 'Failed to save featured photos');
    } finally {
      setFeaturedBusy(false);
    }
  };

  return (
    <div className="memory-book-tab-content">
      <h3 className="memory-book-section-title">Photo gallery</h3>
      <p className="memory-book-tab-desc">
        Upload photos for the main gallery on the public page. Drag files or a folder here, use the buttons below, or choose a folder to upload every image inside it.
      </p>
      <div
        className={`memory-book-gallery-dropzone${dragOver ? ' memory-book-gallery-dropzone--active' : ''}${galleryUploading ? ' memory-book-gallery-dropzone--disabled' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <p className="memory-book-gallery-dropzone-hint">
          {galleryUploading ? 'Uploading…' : 'Drop images or folders here'}
        </p>
        <div className="memory-book-gallery-upload-actions">
          <label className="memory-book-upload-label memory-book-upload-label--multi">
            <input type="file" accept="image/*" multiple onChange={handleFileInputChange} disabled={galleryUploading} />
            Select photos
          </label>
          <label className="memory-book-upload-label memory-book-upload-label--multi memory-book-upload-label--folder">
            <input
              ref={folderInputRef}
              type="file"
              multiple
              onChange={handleFileInputChange}
              disabled={galleryUploading}
            />
            Upload folder
          </label>
          <label className="memory-book-gallery-upload-credit-toggle">
            <input
              type="checkbox"
              checked={!!applyBulkCreditOnUpload}
              onChange={(e) => setApplyBulkCreditOnUpload(e.target.checked)}
              disabled={galleryUploading}
            />
            Set credit on upload
          </label>
          {missingThumbCount > 0 ? (
            <button
              type="button"
              className="memory-book-upload-label memory-book-upload-label--multi memory-book-upload-label--thumbs"
              onClick={startThumbBackfill}
              disabled={galleryUploading || thumbBackfillBusy}
              title="Generate thumbnails for existing photos (faster previews)"
            >
              {thumbBackfillBusy ? 'Generating thumbs…' : `Backfill thumbs (${missingThumbCount})`}
            </button>
          ) : (
            <button
              type="button"
              className="memory-book-upload-label memory-book-upload-label--multi memory-book-upload-label--thumbs"
              disabled
              title="All photos already have thumbnails"
            >
              Thumbs up to date
            </button>
          )}
          {thumbBackfillBusy ? (
            <button
              type="button"
              className="memory-book-upload-label memory-book-upload-label--multi memory-book-upload-label--thumbs memory-book-upload-label--thumbs-cancel"
              onClick={cancelThumbBackfill}
              title="Stop after current photo finishes"
            >
              Cancel
            </button>
          ) : null}
        </div>
      </div>
      {thumbBackfillBusy ? (
        <p className="memory-book-tab-desc" style={{ marginTop: '0.55rem' }}>
          Generating thumbnails… {thumbBackfillProgress.done}/{thumbBackfillProgress.total}
        </p>
      ) : null}
      <div className="memory-book-gallery-bulkbar" aria-label="Batch photo credit">
        <label className="memory-book-gallery-bulkbar-label">
          <span className="memory-book-gallery-bulkbar-label-text">Batch photo credit</span>
          <input
            type="text"
            value={bulkCredit}
            onChange={(e) => setBulkCredit(e.target.value)}
            placeholder="e.g. Photo: Jane Doe"
            className="memory-book-gallery-bulkbar-input"
            disabled={galleryUploading}
          />
        </label>
        <div className="memory-book-gallery-bulkbar-actions">
          <button
            type="button"
            className="memory-book-gallery-bulkbar-btn memory-book-gallery-bulkbar-btn--ghost"
            onClick={selectAll}
            disabled={
              galleryUploading
              || filteredGalleryItems.length === 0
              || filteredGalleryItems.every((i) => selectedMap?.[i.fullPath])
            }
            title="Select every photo currently shown (respects Featured and Photo credit filters)"
          >
            Select all shown
          </button>
          <button
            type="button"
            className="memory-book-gallery-bulkbar-btn"
            onClick={() => applyCreditBulk(selectedFullPaths, bulkCredit)}
            disabled={galleryUploading || selectedFullPaths.length === 0}
            title={selectedFullPaths.length ? `Apply to ${selectedFullPaths.length} selected photo(s)` : 'Select photos below to enable'}
          >
            Apply to selected ({selectedFullPaths.length})
          </button>
          <button
            type="button"
            className="memory-book-gallery-bulkbar-btn memory-book-gallery-bulkbar-btn--danger"
            onClick={() => deleteSelectedBulk(selectedFullPaths)}
            disabled={galleryUploading || selectedFullPaths.length === 0}
            title={selectedFullPaths.length ? `Delete ${selectedFullPaths.length} selected photo(s)` : 'Select photos below to enable'}
          >
            Delete selected
          </button>
          <button
            type="button"
            className="memory-book-gallery-bulkbar-btn"
            onClick={() => applyCreditBulk(galleryItems.map((i) => i.fullPath), bulkCredit)}
            disabled={galleryUploading || galleryItems.length === 0}
          >
            Apply to all ({galleryItems.length})
          </button>
          <button
            type="button"
            className="memory-book-gallery-bulkbar-btn"
            onClick={() => applyCreditBulk(galleryItems.map((i) => i.fullPath), bulkCredit, { onlyBlank: true })}
            disabled={galleryUploading || galleryItems.length === 0}
            title="Only photos with blank credit will be updated"
          >
            Apply to blanks
          </button>
          <button
            type="button"
            className="memory-book-gallery-bulkbar-btn memory-book-gallery-bulkbar-btn--ghost"
            onClick={clearAllSelected}
            disabled={galleryUploading || selectedFullPaths.length === 0}
          >
            Select none
          </button>
        </div>
      </div>
      <div className="memory-book-gallery-filterbar" aria-label="Gallery filter">
        <button
          type="button"
          className={`memory-book-gallery-bulkbar-btn memory-book-gallery-bulkbar-btn--ghost${galleryFilter === 'all' ? ' memory-book-gallery-bulkbar-btn--active' : ''}`}
          onClick={() => setGalleryFilter('all')}
        >
          All photos ({galleryItems.length})
        </button>
        <button
          type="button"
          className={`memory-book-gallery-bulkbar-btn memory-book-gallery-bulkbar-btn--ghost${galleryFilter === 'featured' ? ' memory-book-gallery-bulkbar-btn--active' : ''}`}
          onClick={() => setGalleryFilter('featured')}
        >
          Featured ({galleryItems.filter((i) => featuredSet.has(i.fullPath)).length})
        </button>
        {galleryItems.length > 0 ? (
          <label className="memory-book-gallery-filterbar-credit">
            <span className="memory-book-gallery-filterbar-credit-label">Photo credit</span>
            <select
              className="memory-book-select memory-book-gallery-filterbar-credit-select"
              value={galleryCreditFilter}
              onChange={(e) => setGalleryCreditFilter(e.target.value)}
              aria-label="Filter gallery by photo credit"
            >
              <option value="">All photographers</option>
              {galleryItems.some((g) => !(g.credit || '').trim()) ? (
                <option value={GALLERY_CREDIT_FILTER_NONE}>No credit listed</option>
              ) : null}
              {uniqueGalleryCredits.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
      <div className="memory-book-gallery-admin-grid">
        {filteredGalleryItems.map((item) => (
          <div key={item.fullPath} className="memory-book-gallery-admin-item">
            <label className="memory-book-gallery-select">
              <input
                type="checkbox"
                checked={!!selectedMap?.[item.fullPath]}
                onChange={(e) => setSelectedForPath(item.fullPath, e.target.checked)}
                disabled={galleryUploading}
              />
              Select
            </label>
            <img
              src={item.thumbUrl || item.url}
              alt=""
              style={{ objectPosition: galleryPhotoObjectPosition(item.fullPath, memoryMeta) }}
              loading="lazy"
              decoding="async"
            />
            <div className="memory-book-gallery-admin-actions">
              <button
                type="button"
                className={`memory-book-icon-btn ${featuredSet.has(item.fullPath) ? 'memory-book-icon-btn--on' : ''}`}
                disabled={galleryUploading || featuredBusy}
                onClick={() => toggleFeatured(item.fullPath)}
                aria-label={featuredSet.has(item.fullPath) ? 'Unfeature photo' : 'Feature photo'}
                title={featuredSet.has(item.fullPath) ? 'Featured (click to remove)' : 'Mark as featured'}
              >
                <StarIcon filled={featuredSet.has(item.fullPath)} />
              </button>
              {typeof onOpenFocalPointEditor === 'function' && (
                <button
                  type="button"
                  className="memory-book-icon-btn"
                  disabled={galleryUploading}
                  onClick={() => onOpenFocalPointEditor({ kind: 'gallery', fullPath: item.fullPath })}
                  aria-label="Set focal point"
                  title="Set focal point"
                >
                  <FocusIcon />
                </button>
              )}
              <button
                type="button"
                className="memory-book-icon-btn memory-book-icon-btn--danger"
                disabled={galleryUploading}
                onClick={() => handleRemove(item.fullPath)}
                aria-label="Remove photo"
                title="Remove"
              >
                <TrashIcon />
              </button>
            </div>
            <label className="memory-book-gallery-credit-label">
              <span className="memory-book-gallery-credit-label-text">Photo credit</span>
              <input
                type="text"
                className="memory-book-gallery-credit-input"
                placeholder="e.g. Photo: Jane Doe"
                value={item.credit || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setGalleryItems((prev) =>
                    prev.map((i) => (i.fullPath === item.fullPath ? { ...i, credit: v } : i))
                  );
                }}
                onBlur={(e) => persistCredit(item.fullPath, e.target.value)}
                disabled={galleryUploading}
              />
            </label>
          </div>
        ))}
      </div>
      {galleryItems.length === 0 && !galleryUploading && (
        <p className="memory-book-tab-empty">No photos yet. Add images using the drop zone or buttons above.</p>
      )}
      {galleryItems.length > 0 && filteredGalleryItems.length === 0 && !galleryUploading && (
        <p className="memory-book-tab-empty">
          {galleryFilter === 'featured' && !galleryCreditFilter
            ? 'No featured photos yet. Click the star icon on any photo to feature it.'
            : 'No photos match the current filters.'}
        </p>
      )}
    </div>
  );
}

function TestimonialsTab({
  eventId,
  flyers = [],
  memoryMeta,
  setMemoryMeta,
  testimonialForm,
  setTestimonialForm,
  editingTestimonialIndex,
  setEditingTestimonialIndex,
  saving,
  setSaving,
  setSaveError,
}) {
  const testimonials = Array.isArray(memoryMeta?.testimonials) ? memoryMeta.testimonials : [];

  const firstNameByEmail = useMemo(() => buildFirstNameByEmailMap(flyers), [flyers]);
  const resolveDisplayNameForEmail = useCallback(
    (email) => testimonialDisplayNameFromRosterOrEmail(firstNameByEmail, email),
    [firstNameByEmail]
  );

  const [bulkPaste, setBulkPaste] = useState('');
  const [bulkQuoteMode, setBulkQuoteMode] = useState('describe');
  const [bulkDefaultRole, setBulkDefaultRole] = useState('');
  const [bulkPreviewRows, setBulkPreviewRows] = useState([]);
  const [bulkParseError, setBulkParseError] = useState('');

  useEffect(() => {
    setBulkPreviewRows((prev) =>
      prev.map((r) => ({
        ...r,
        quote: buildSurveyTestimonialQuote(r.favorite, r.why, r.describe, bulkQuoteMode),
      }))
    );
  }, [bulkQuoteMode]);

  const handleBulkParse = () => {
    setBulkParseError('');
    const matrix = parseTabDelimitedRows(bulkPaste);
    if (!matrix.length) {
      setBulkParseError('No rows found. Paste tab-separated export (from Sheets/Excel).');
      setBulkPreviewRows([]);
      return;
    }
    let start = 0;
    if (matrix[0] && String(matrix[0][0] || '').toLowerCase().includes('email')) {
      start = 1;
    }
    const out = [];
    for (let i = start; i < matrix.length; i += 1) {
      const cells = matrix[i];
      const parsed = parseTestimonialSurveyRow(cells);
      if (!parsed || !parsed.email.includes('@')) continue;
      const { email, favorite, why, describe } = parsed;
      const quote = buildSurveyTestimonialQuote(favorite, why, describe, bulkQuoteMode);
      if (!quote.trim()) continue;
      out.push({
        id: `bulk-${i}-${email}`,
        email,
        favorite,
        why,
        describe,
        name: resolveDisplayNameForEmail(email),
        quote,
      });
    }
    if (!out.length) {
      setBulkParseError(
        'No valid rows. Use tab-separated export from Sheets/Excel, or paste lines starting with an email; if tabs were lost, the whole line after the email is used as the quote when "Describe" is selected.'
      );
      setBulkPreviewRows([]);
      return;
    }
    setBulkPreviewRows(out);
  };

  const handleBulkFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setBulkPaste(String(reader.result || ''));
      setBulkParseError('');
      e.target.value = '';
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleBulkAppend = async () => {
    if (!eventId) {
      setSaveError('Choose an event in the dropdown above before appending testimonials.');
      return;
    }
    const entries = bulkPreviewRows
      .filter((r) => r.quote.trim())
      .map((r) => ({
        quote: r.quote.trim(),
        name: (r.name || '').trim() || resolveDisplayNameForEmail(r.email),
        role: (bulkDefaultRole || '').trim(),
      }));
    if (!entries.length) return;
    setSaving(true);
    setSaveError('');
    try {
      const next = [...testimonials, ...entries];
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { testimonials: next }, { merge: true });
      setMemoryMeta((prev) => ({ ...prev, testimonials: next }));
      setBulkPreviewRows([]);
      setBulkPaste('');
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkRematchRosterNames = () => {
    setBulkPreviewRows((rows) =>
      rows.map((r) => ({ ...r, name: resolveDisplayNameForEmail(r.email) }))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!eventId) return;
    setSaving(true);
    setSaveError('');
    let next = [...testimonials];
    const entry = { quote: (testimonialForm.quote || '').trim(), name: (testimonialForm.name || '').trim(), role: (testimonialForm.role || '').trim() };
    if (!entry.quote) { setSaving(false); return; }
    try {
      if (editingTestimonialIndex !== null) {
        next[editingTestimonialIndex] = entry;
        setEditingTestimonialIndex(null);
      } else {
        next.push(entry);
      }
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { testimonials: next }, { merge: true });
      setMemoryMeta((prev) => ({ ...prev, testimonials: next }));
      setTestimonialForm({ quote: '', name: '', role: '' });
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    const next = testimonials.filter((_, i) => i !== index);
    setSaveError('');
    try {
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { testimonials: next }, { merge: true });
      setMemoryMeta((prev) => ({ ...prev, testimonials: next }));
      if (editingTestimonialIndex === index) setEditingTestimonialIndex(null);
      else if (editingTestimonialIndex !== null && editingTestimonialIndex > index) setEditingTestimonialIndex(editingTestimonialIndex - 1);
    } catch (err) {
      setSaveError(err.message || 'Delete failed');
    }
  };

  const handleDeleteAll = async () => {
    if (!eventId) return;
    if (!testimonials.length) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(`Delete all ${testimonials.length} testimonials? This cannot be undone.`);
    if (!ok) return;
    setSaving(true);
    setSaveError('');
    try {
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { testimonials: [] }, { merge: true });
      setMemoryMeta((prev) => ({ ...prev, testimonials: [] }));
      setEditingTestimonialIndex(null);
      setTestimonialForm({ quote: '', name: '', role: '' });
    } catch (err) {
      setSaveError(err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="memory-book-tab-content">
      <h3 className="memory-book-section-title">Testimonials</h3>
      <p className="memory-book-tab-desc">Add quotes from participants for the memory book.</p>

      <div className="memory-book-bulk-import">
        <h4 className="memory-book-subsection-title">Bulk import from survey export</h4>
        <p className="memory-book-tab-desc memory-book-tab-desc--tight">
          Paste a tab-separated sheet with columns: Email, favorite part, why attend, describe Fresh Meet (export from Google Sheets / Excel for real tabs).
          If you paste from chat and tabs turn into spaces, we still detect each line by email and use everything after it as one quote when using &quot;Describe&quot; (split the columns in the preview if needed).
          First names come from this event&apos;s flyer list when the email matches a mentor or freshling; otherwise we guess from the address.
          Changing &quot;Quote text&quot; rebuilds quotes from the parsed columns.
        </p>
        <label className="memory-book-upload-label memory-book-upload-label--multi">
          <input type="file" accept=".txt,.tsv,.csv,text/plain,text/tab-separated-values" onChange={handleBulkFile} />
          Load .tsv / .txt file
        </label>
        <label className="memory-book-bulk-paste-label">
          Paste export
          <textarea
            className="memory-book-bulk-paste"
            value={bulkPaste}
            onChange={(e) => { setBulkPaste(e.target.value); setBulkParseError(''); }}
            rows={6}
            placeholder="Email Address	Column 2	Column 3	Column 4"
          />
        </label>
        <div className="memory-book-bulk-toolbar">
          <label className="memory-book-bulk-select-label">
            Quote text
            <select value={bulkQuoteMode} onChange={(e) => setBulkQuoteMode(e.target.value)}>
              <option value="describe">How would you describe Fresh Meet? (recommended)</option>
              <option value="favorite">What was your favorite part?</option>
              <option value="why">Why should next year attend?</option>
              <option value="all">All three (labeled blocks)</option>
            </select>
          </label>
          <label className="memory-book-bulk-select-label">
            Default role (optional)
            <input
              type="text"
              value={bulkDefaultRole}
              onChange={(e) => setBulkDefaultRole(e.target.value)}
              placeholder="e.g. Freshling"
            />
          </label>
          <button type="button" className="memory-book-bulk-parse-btn" onClick={handleBulkParse}>
            Parse preview
          </button>
          <button
            type="button"
            className="memory-book-bulk-parse-btn"
            onClick={handleBulkRematchRosterNames}
            disabled={bulkPreviewRows.length === 0}
            title="Overwrite preview first names using flyer roster emails for this event"
          >
            Match names to roster
          </button>
          <button
            type="button"
            className="memory-book-bulk-append-btn"
            onClick={handleBulkAppend}
            disabled={saving || bulkPreviewRows.length === 0}
          >
            Append {bulkPreviewRows.length ? `${bulkPreviewRows.length} ` : ''}to testimonials
          </button>
        </div>
        {bulkParseError ? <p className="memory-book-bulk-error">{bulkParseError}</p> : null}
        {bulkPreviewRows.length > 0 && (
          <div className="memory-book-bulk-preview-wrap">
            <p className="memory-book-tab-desc memory-book-tab-desc--tight">Edit first name or quote, then append.</p>
            <div className="memory-book-bulk-preview-list">
              {bulkPreviewRows.map((r) => (
                <div key={r.id} className="memory-book-bulk-preview-card">
                  <div className="memory-book-bulk-preview-email" title={r.email}>{r.email}</div>
                  <label>
                    First name
                    <input
                      type="text"
                      value={r.name}
                      onChange={(e) => {
                        const v = e.target.value;
                        setBulkPreviewRows((rows) => rows.map((x) => (x.id === r.id ? { ...x, name: v } : x)));
                      }}
                    />
                  </label>
                  <label>
                    Quote
                    <textarea
                      rows={3}
                      value={r.quote}
                      onChange={(e) => {
                        const v = e.target.value;
                        setBulkPreviewRows((rows) => rows.map((x) => (x.id === r.id ? { ...x, quote: v } : x)));
                      }}
                    />
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="memory-book-form">
        <label>
          Quote
          <textarea value={testimonialForm.quote} onChange={(e) => setTestimonialForm((f) => ({ ...f, quote: e.target.value }))} rows={3} />
        </label>
        <label>
          Name
          <input type="text" value={testimonialForm.name} onChange={(e) => setTestimonialForm((f) => ({ ...f, name: e.target.value }))} />
        </label>
        <label>
          Role
          <input type="text" value={testimonialForm.role} onChange={(e) => setTestimonialForm((f) => ({ ...f, role: e.target.value }))} placeholder="e.g. Freshling, Mentor" />
        </label>
        <button type="submit" disabled={saving}>{editingTestimonialIndex !== null ? 'Save changes' : 'Add testimonial'}</button>
        <button type="button" disabled={saving || testimonials.length === 0} onClick={handleDeleteAll}>
          Delete all
        </button>
      </form>
      <div className="memory-book-list">
        {testimonials.map((t, i) => (
          <div key={i} className="memory-book-card memory-book-quote">
            <p className="memory-book-quote-text">"{t.quote || t.text}"</p>
            <p className="memory-book-quote-byline">— {t.name || 'Anonymous'}{t.role ? `, ${t.role}` : ''}</p>
            <button type="button" className="memory-book-edit-btn" onClick={() => { setTestimonialForm({ quote: t.quote || t.text || '', name: t.name || '', role: t.role || '' }); setEditingTestimonialIndex(i); }}>Edit</button>
            <button type="button" className="memory-book-remove-btn" onClick={() => handleDelete(i)}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function IntroHeroTab({
  eventId,
  memoryMeta,
  setMemoryMeta,
  introForm,
  setIntroForm,
  computedNumbers,
  saving,
  setSaving,
  setSaveError,
}) {
  useEffect(() => {
    if (!memoryMeta || !eventId) return;
    setIntroForm({
      intro: memoryMeta.intro || '',
      heroImageUrl: memoryMeta.heroImageUrl || '',
      highlights: Array.isArray(memoryMeta.highlights) ? memoryMeta.highlights.map((h) => (typeof h === 'string' ? h : (h && h.text) || '')) : [],
      statsOverrideForm: memoryBookStatsOverridesMetaToForm(memoryMeta.statsOverrides),
    });
  }, [eventId, memoryMeta, setIntroForm]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!eventId) return;
    setSaving(true);
    setSaveError('');
    try {
      const statsOverrides = memoryBookStatsOverridesFormToFirestore(introForm.statsOverrideForm);
      const payload = {
        intro: (introForm.intro || '').trim(),
        heroImageUrl: (introForm.heroImageUrl || '').trim() || null,
        highlights: (introForm.highlights || []).filter((h) => String(h).trim()).map((h) => String(h).trim()),
        statsOverrides,
      };
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), payload, { merge: true });
      setMemoryMeta((prev) => ({ ...prev, ...payload }));
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !eventId) return;
    setSaveError('');
    try {
      const transcode = imageNeedsJpegTranscodeForWebImg(file);
      let ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/gi, '') || 'jpg';
      let uploadBody = file;
      if (transcode) {
        uploadBody = await imageFileToJpegBlob(file, {
          maxDim: MEMORY_BOOK_TRANSCODE_MAX_DIM,
          quality: 0.9,
        });
        ext = 'jpg';
      }
      const storageRef = ref(storage, `${MEMORY_BOOK_STORAGE_PREFIX}/${eventId}/hero.${ext}`);
      await uploadBytes(
        storageRef,
        uploadBody,
        transcode || ext === 'jpg' ? { contentType: 'image/jpeg' } : undefined
      );
      const url = await getDownloadURL(storageRef);
      setIntroForm((f) => ({ ...f, heroImageUrl: url }));
    } catch (err) {
      setSaveError(err.message || 'Upload failed');
    }
    e.target.value = '';
  };

  return (
    <div className="memory-book-tab-content">
      <h3 className="memory-book-section-title">Intro & hero</h3>
      <p className="memory-book-tab-desc">
        Intro text (shown under the header band) and a cover image. The public page uses a <strong>short full-width band</strong> with the title overlaid on the image; the image is cropped lightly from the <strong>upper-middle</strong> of the frame to favor faces in typical group shots. The header also rotates through podium team photos when those photos exist—use a <strong>wide</strong> photo for least cropping.
      </p>
      <form onSubmit={handleSave} className="memory-book-form">
        <label>
          Intro text
          <textarea value={introForm.intro} onChange={(e) => setIntroForm((f) => ({ ...f, intro: e.target.value }))} rows={5} placeholder="e.g. Relive 2026 — and get ready for 2027." />
        </label>
        <label>
          Hero image URL
          <input type="url" value={introForm.heroImageUrl} onChange={(e) => setIntroForm((f) => ({ ...f, heroImageUrl: e.target.value }))} placeholder="Or upload below" />
        </label>
        <label className="memory-book-upload-label">
          <input type="file" accept="image/*" onChange={handleHeroFile} />
          Upload hero image
        </label>
        <label>
          Highlights (one per line)
          {introForm.highlights.map((h, i) => (
            <input key={i} type="text" value={h} onChange={(e) => setIntroForm((f) => ({ ...f, highlights: f.highlights.map((v, j) => (j === i ? e.target.value : v)) }))} placeholder="Highlight" />
          ))}
          <button type="button" onClick={() => setIntroForm((f) => ({ ...f, highlights: [...(f.highlights || []), ''] }))}>Add highlight</button>
        </label>

        <fieldset className="memory-book-stats-overrides">
          <legend>Stats overrides (optional)</legend>
          <p className="memory-book-tab-desc memory-book-stats-overrides-desc">
            Correct the headline number, hero carousel slides, “By the numbers” teaser, and expanded stats if the computed values are wrong. Leave fields blank to use the automatic counts.
          </p>
          <div className="memory-book-stats-override-block">
            <h4 className="memory-book-stats-override-heading">Headline (“By the numbers” hero)</h4>
            <p className="memory-book-stats-overrides-hint">
              Computed:{' '}
              <strong>
                {computedNumbers?.hero?.value ?? '—'} — {computedNumbers?.hero?.label || 'Freshlings'}
              </strong>
            </p>
            <div className="memory-book-stats-override-row">
              <label>
                Override value
                <input
                  type="text"
                  inputMode="numeric"
                  value={introForm.statsOverrideForm?.hero?.value ?? ''}
                  onChange={(e) =>
                    setIntroForm((f) => ({
                      ...f,
                      statsOverrideForm: {
                        ...f.statsOverrideForm,
                        hero: { ...(f.statsOverrideForm?.hero || {}), value: e.target.value },
                      },
                    }))
                  }
                  placeholder="e.g. 52"
                />
              </label>
              <label>
                Override label
                <input
                  type="text"
                  value={introForm.statsOverrideForm?.hero?.label ?? ''}
                  onChange={(e) =>
                    setIntroForm((f) => ({
                      ...f,
                      statsOverrideForm: {
                        ...f.statsOverrideForm,
                        hero: { ...(f.statsOverrideForm?.hero || {}), label: e.target.value },
                      },
                    }))
                  }
                  placeholder="e.g. Freshlings"
                />
              </label>
            </div>
          </div>

          <div className="memory-book-stats-override-block">
            <h4 className="memory-book-stats-override-heading">Carousel & teaser slides</h4>
            <div className="memory-book-stats-override-table-wrap">
              <table className="memory-book-stats-override-table">
                <thead>
                  <tr>
                    <th>Stat</th>
                    <th>Computed</th>
                    <th>Value</th>
                    <th>Label</th>
                    <th>Text (ages only)</th>
                  </tr>
                </thead>
                <tbody>
                  {MEMORY_BOOK_TEASER_SLIDE_ORDER.map((id) => {
                    const slide = (computedNumbers?.teaserSlides || []).find((s) => s.id === id);
                    const computedDisplay =
                      slide?.kind === 'text'
                        ? slide?.display ?? '—'
                        : slide != null
                          ? String(slide.value)
                          : '—';
                    const row = introForm.statsOverrideForm?.slides?.[id] || { value: '', label: '', display: '' };
                    return (
                      <tr key={id}>
                        <td>{MEMORY_BOOK_TEASER_LABELS[id] || id}</td>
                        <td className="memory-book-stats-override-computed">{computedDisplay}</td>
                        <td>
                          {id === 'ages' ? (
                            <span className="memory-book-stats-override-na">—</span>
                          ) : (
                            <input
                              type="text"
                              inputMode="numeric"
                              className="memory-book-stats-override-input"
                              value={row.value}
                              onChange={(e) =>
                                setIntroForm((f) => ({
                                  ...f,
                                  statsOverrideForm: {
                                    ...f.statsOverrideForm,
                                    slides: {
                                      ...f.statsOverrideForm.slides,
                                      [id]: { ...f.statsOverrideForm.slides[id], value: e.target.value },
                                    },
                                  },
                                }))
                              }
                              placeholder="—"
                            />
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            className="memory-book-stats-override-input"
                            value={row.label}
                            onChange={(e) =>
                              setIntroForm((f) => ({
                                ...f,
                                statsOverrideForm: {
                                  ...f.statsOverrideForm,
                                  slides: {
                                    ...f.statsOverrideForm.slides,
                                    [id]: { ...f.statsOverrideForm.slides[id], label: e.target.value },
                                  },
                                },
                              }))
                            }
                            placeholder="—"
                          />
                        </td>
                        <td>
                          {id === 'ages' ? (
                            <input
                              type="text"
                              className="memory-book-stats-override-input"
                              value={row.display}
                              onChange={(e) =>
                                setIntroForm((f) => ({
                                  ...f,
                                  statsOverrideForm: {
                                    ...f.statsOverrideForm,
                                    slides: {
                                      ...f.statsOverrideForm.slides,
                                      [id]: { ...f.statsOverrideForm.slides[id], display: e.target.value },
                                    },
                                  },
                                }))
                              }
                              placeholder="e.g. 21–34 yrs"
                            />
                          ) : (
                            <span className="memory-book-stats-override-na">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="memory-book-stats-override-block">
            <h4 className="memory-book-stats-override-heading">Jumps &amp; training (expanded stats)</h4>
            <div className="memory-book-stats-override-row memory-book-stats-override-row--activities">
              {[
                { key: 'highAltitude', label: 'High altitude jumps', computed: computedNumbers?.activities?.highAltitude },
                { key: 'jet', label: 'Jet jumps', computed: computedNumbers?.activities?.jet },
                { key: 'canopy', label: 'Canopy courses', computed: computedNumbers?.activities?.canopy },
                { key: 'water', label: 'Water training', computed: computedNumbers?.activities?.water },
              ].map(({ key, label, computed }) => (
                <label key={key}>
                  {label}
                  <span className="memory-book-stats-overrides-hint">Computed: {computed ?? 0}</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={introForm.statsOverrideForm?.activities?.[key] ?? ''}
                    onChange={(e) =>
                      setIntroForm((f) => ({
                        ...f,
                        statsOverrideForm: {
                          ...f.statsOverrideForm,
                          activities: { ...f.statsOverrideForm.activities, [key]: e.target.value },
                        },
                      }))
                    }
                    placeholder="Override"
                  />
                </label>
              ))}
            </div>
          </div>
        </fieldset>

        <button type="submit" disabled={saving}>Save</button>
      </form>
    </div>
  );
}

function QuiplashTab({ eventId, teams, memoryMeta, setMemoryMeta, quiplashForm, setQuiplashForm, editingQuiplashIndex, setEditingQuiplashIndex, saving, setSaving, setSaveError }) {
  const quiplashQuotes = Array.isArray(memoryMeta?.quiplashQuotes) ? memoryMeta.quiplashQuotes : [];
  const teamsByNumber = useMemo(
    () => [...(teams || [])].sort((a, b) => (Number(a.teamNumber) || 0) - (Number(b.teamNumber) || 0)),
    [teams]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!eventId) return;
    setSaving(true);
    setSaveError('');
    const tnRaw = (quiplashForm.teamNumber ?? '').toString().trim();
    const teamNumberParsed = tnRaw === '' ? null : Number(tnRaw);
    const prev = editingQuiplashIndex !== null ? quiplashQuotes[editingQuiplashIndex] : null;
    const entry = {
      prompt: (quiplashForm.prompt || '').trim(),
      winningQuote: (quiplashForm.winningQuote || '').trim(),
    };
    if (teamNumberParsed != null && Number.isFinite(teamNumberParsed)) {
      entry.teamNumber = teamNumberParsed;
    } else if (prev && (prev.author || '').toString().trim()) {
      entry.author = (prev.author || '').toString().trim();
    }
    if (!entry.winningQuote) { setSaving(false); return; }
    let next = [...quiplashQuotes];
    try {
      if (editingQuiplashIndex !== null) {
        next[editingQuiplashIndex] = entry;
        setEditingQuiplashIndex(null);
      } else {
        next.push(entry);
      }
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { quiplashQuotes: next }, { merge: true });
      setMemoryMeta((prev) => ({ ...prev, quiplashQuotes: next }));
      setQuiplashForm({ prompt: '', winningQuote: '', teamNumber: '' });
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    const next = quiplashQuotes.filter((_, i) => i !== index);
    try {
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), { quiplashQuotes: next }, { merge: true });
      setMemoryMeta((prev) => ({ ...prev, quiplashQuotes: next }));
      if (editingQuiplashIndex === index) setEditingQuiplashIndex(null);
      else if (editingQuiplashIndex !== null && editingQuiplashIndex > index) setEditingQuiplashIndex(editingQuiplashIndex - 1);
    } catch (err) {
      setSaveError(err.message || 'Delete failed');
    }
  };

  return (
    <div className="memory-book-tab-content">
      <h3 className="memory-book-section-title">Quiplash winning quotes</h3>
      <p className="memory-book-tab-desc">
        Add winning quotes from Quiplash (or similar). Pick the winning team by number; the public page shows that team’s name.
      </p>
      <form onSubmit={handleSubmit} className="memory-book-form">
        <label>
          Prompt
          <input type="text" value={quiplashForm.prompt} onChange={(e) => setQuiplashForm((f) => ({ ...f, prompt: e.target.value }))} placeholder="e.g. Something you'd say to a time traveler" />
        </label>
        <label>
          Winning quote
          <input type="text" value={quiplashForm.winningQuote} onChange={(e) => setQuiplashForm((f) => ({ ...f, winningQuote: e.target.value }))} />
        </label>
        <label>
          Team #
          <select
            value={quiplashForm.teamNumber === '' ? '' : String(quiplashForm.teamNumber)}
            onChange={(e) => setQuiplashForm((f) => ({ ...f, teamNumber: e.target.value }))}
          >
            <option value="">—</option>
            {teamsByNumber.map((t) => {
              const teamPickLabel = getTeamLabelCore(t);
              const pickNumDisp = getTeamNumberDisplay(t);
              const pickSynthetic = pickNumDisp !== '?' ? `Team ${pickNumDisp}` : '';
              const pickSuffix =
                teamPickLabel && teamPickLabel !== pickSynthetic ? ` — ${teamPickLabel}` : '';
              return (
                <option key={t.id} value={String(t.teamNumber ?? '')}>
                  #{t.teamNumber}
                  {pickSuffix}
                </option>
              );
            })}
          </select>
        </label>
        <button type="submit" disabled={saving}>{editingQuiplashIndex !== null ? 'Save changes' : 'Add quote'}</button>
      </form>
      <div className="memory-book-list">
        {quiplashQuotes.map((q, i) => {
          const teamLine = getQuiplashTeamDisplayName(q, teams || []);
          return (
            <div key={i} className="memory-book-card memory-book-quote">
              {q.prompt && <div className="memory-book-quiplash-prompt">{q.prompt}</div>}
              <p className="memory-book-quote-text">"{q.winningQuote || q.quote}"</p>
              {teamLine && <p className="memory-book-quote-byline">— {teamLine}</p>}
              <button
                type="button"
                className="memory-book-edit-btn"
                onClick={() => {
                  const tn = q.teamNumber;
                  setQuiplashForm({
                    prompt: q.prompt || '',
                    winningQuote: q.winningQuote || q.quote || '',
                    teamNumber: tn != null && tn !== '' ? String(tn) : '',
                  });
                  setEditingQuiplashIndex(i);
                }}
              >
                Edit
              </button>
              <button type="button" className="memory-book-remove-btn" onClick={() => handleDelete(i)}>Delete</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VideosTab({ eventId, memoryMeta, setMemoryMeta, saving, setSaving, setSaveError }) {
  const [form, setForm] = useState({
    beginnerMeet: '',
    intermediateMeet: '',
    loadOrganizer: '',
    canopyControlCourse: '',
  });

  useEffect(() => {
    const videos = memoryMeta?.videos && typeof memoryMeta.videos === 'object' ? memoryMeta.videos : {};
    setForm({
      beginnerMeet: (videos.beginnerMeet || '').toString(),
      intermediateMeet: (videos.intermediateMeet || '').toString(),
      loadOrganizer: (videos.loadOrganizer || '').toString(),
      canopyControlCourse: Array.isArray(videos.canopyControlCourse) ? videos.canopyControlCourse.join('\n') : '',
    });
  }, [memoryMeta?.videos]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!eventId) return;
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        videos: {
          beginnerMeet: normalizeVideoUrl(form.beginnerMeet),
          intermediateMeet: normalizeVideoUrl(form.intermediateMeet),
          loadOrganizer: normalizeVideoUrl(form.loadOrganizer),
          canopyControlCourse: parseVideoLines(form.canopyControlCourse),
        },
      };
      await setDoc(doc(firestore, 'eventMemoryMeta', eventId), payload, { merge: true });
      setMemoryMeta((prev) => ({ ...prev, ...payload }));
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="memory-book-tab-content">
      <h3 className="memory-book-section-title">Videos</h3>
      <p className="memory-book-tab-desc">
        Add links for the videos page. Use one link per field, and one link per line for canopy control course videos.
      </p>
      <form onSubmit={handleSubmit} className="memory-book-form">
        <label>
          Beginner meet videos link
          <input
            type="url"
            value={form.beginnerMeet}
            onChange={(e) => setForm((prev) => ({ ...prev, beginnerMeet: e.target.value }))}
            placeholder="https://..."
          />
        </label>
        <label>
          Intermediate meet videos link
          <input
            type="url"
            value={form.intermediateMeet}
            onChange={(e) => setForm((prev) => ({ ...prev, intermediateMeet: e.target.value }))}
            placeholder="https://..."
          />
        </label>
        <label>
          Load organizer videos link
          <input
            type="url"
            value={form.loadOrganizer}
            onChange={(e) => setForm((prev) => ({ ...prev, loadOrganizer: e.target.value }))}
            placeholder="https://..."
          />
        </label>
        <label>
          Canopy control course videos (one URL per line)
          <textarea
            rows={5}
            value={form.canopyControlCourse}
            onChange={(e) => setForm((prev) => ({ ...prev, canopyControlCourse: e.target.value }))}
            placeholder={'https://...\nhttps://...'}
          />
        </label>
        <button type="submit" disabled={saving}>Save videos</button>
      </form>
    </div>
  );
}

function NextYearInterestTab() {
  const [form, setForm] = useState({ headline: '', datesText: '', locationText: '', noteText: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setStatus('');
      try {
        const snap = await getDoc(doc(firestore, '_config', 'nextYearInterestConfig'));
        const data = snap.exists() ? snap.data() : {};
        if (cancelled) return;
        setForm({
          headline: (data.headline || '').toString(),
          datesText: (data.datesText || '').toString(),
          locationText: (data.locationText || '').toString(),
          noteText: (data.noteText || '').toString(),
        });
      } catch (e) {
        if (!cancelled) setStatus(e?.message || 'Failed to load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus('');
    try {
      await setDoc(
        doc(firestore, '_config', 'nextYearInterestConfig'),
        {
          headline: form.headline || '',
          datesText: form.datesText || '',
          locationText: form.locationText || '',
          noteText: form.noteText || '',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setStatus('Saved.');
      window.setTimeout(() => setStatus(''), 2000);
    } catch (err) {
      setStatus(err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="memory-book-tab-content">
      <h3 className="memory-book-section-title">Next year interest list</h3>
      <p className="memory-book-tab-desc">
        Controls the public “Join next year interest list” modal. Supports the <code>{'{year}'}</code> placeholder.
      </p>
      <form className="memory-book-form" onSubmit={onSubmit}>
        <label>
          Headline (optional)
          <input
            type="text"
            value={form.headline}
            onChange={(e) => setForm((p) => ({ ...p, headline: e.target.value }))}
            placeholder="Ex: Fresh Meet {year}"
            disabled={loading}
          />
        </label>
        <label>
          Dates (optional)
          <input
            type="text"
            value={form.datesText}
            onChange={(e) => setForm((p) => ({ ...p, datesText: e.target.value }))}
            placeholder="Ex: April 10–13, {year}"
            disabled={loading}
          />
        </label>
        <label>
          Location (optional)
          <input
            type="text"
            value={form.locationText}
            onChange={(e) => setForm((p) => ({ ...p, locationText: e.target.value }))}
            placeholder="Ex: Skydive Perris, CA"
            disabled={loading}
          />
        </label>
        <label>
          Note (optional)
          <textarea
            value={form.noteText}
            onChange={(e) => setForm((p) => ({ ...p, noteText: e.target.value }))}
            placeholder="Ex: Dates may shift slightly; you’ll be emailed when registration opens."
            disabled={loading}
          />
        </label>
        <button type="submit" disabled={loading || saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {status && <span className="memory-book-tab-desc" style={{ marginLeft: '0.5rem' }}>{status}</span>}
      </form>
    </div>
  );
}

export default EventMemoryBook;
